import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useDestinations, findTripForDest } from '../hooks/useDestinations';
import { TutoLienChoix } from '../components/TutoInscription';
import { formatDateInput, validateFrenchBirthDate } from '../utils/dateFormat';
import { computePricing } from '../utils/pricing';
import { detectTypoWarnings } from '../utils/typoGuard';

const API = process.env.REACT_APP_BACKEND_URL;
import { useFormTextes } from '../hooks/useFormTextes';

// Iter 263 (Kamel) — Mini-formulaire liste d'attente PUBLIQUE (date complète)
const WaitlistMini = ({ dest, prefill = {}, onDone }) => {
  const [f, setF] = useState({
    nom: prefill.nom || '', prenom: prefill.prenom || '',
    email: prefill.email || '', telephone: prefill.telephone || '',
    nbPersonnes: prefill.nbPersonnes || 1,
  });
  const [state, setState] = useState('idle');
  // Iter 754 — semaine fusionnée (car en option) : la clé d'attente est la semaine seule, sauf pour un siège de car précis (« Car aller » / « Car retour »)
  const trKey = dest.transport_option && !/^Car (aller|retour)$/.test(dest.transport || '') ? '' : dest.transport;
  const voyageKey = trKey ? `${dest.group}|${dest.label}|${trKey}` : `${dest.group}|${dest.label}`;
  const submit = async () => {
    if (!f.nom.trim() || !f.prenom.trim()) { setState('err'); return; }
    if (!f.email.trim() && !f.telephone.trim()) { setState('err'); return; }
    setState('sending');
    try {
      const r = await fetch(`${API}/api/waitlist`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          voyage_key: voyageKey,
          destination: `${dest.group} — ${dest.label}${trKey ? ` (${trKey})` : ''}`,
          nom: f.nom.toUpperCase(), prenom: f.prenom,
          nbPersonnes: parseInt(f.nbPersonnes) || 1,
          telephone: f.telephone, email: f.email,
          note: 'Formulaire public — date complète', source: 'formulaire',
        }),
      });
      const j = await r.json().catch(() => ({}));
      setState(r.ok ? (j.duplicate ? 'dup' : 'ok') : 'err');
    } catch { setState('err'); }
  };
  if (state === 'dup') return (
    <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4 text-center" data-testid="waitlist-mini-duplicate">
      <p className="text-green-700 font-bold text-sm">✅ Vous êtes déjà en liste d'attente !</p>
      <p className="text-xs text-gray-600 mt-1">Votre demande pour <strong>{dest.group} — {dest.label}</strong> est bien enregistrée — votre position d'origine est conservée, inutile de vous réinscrire.</p>
    </div>
  );
  if (state === 'ok') return (
    <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4 text-center" data-testid="waitlist-mini-success">
      <p className="text-green-700 font-bold text-sm">✅ Demande enregistrée !</p>
      <p className="text-xs text-gray-600 mt-1">Nous vous recontacterons dès qu'une place se libère pour <strong>{dest.group} — {dest.label}</strong>.</p>
    </div>
  );
  return (
    <div className="bg-[#1E3A5F] rounded-xl p-4 space-y-2 text-white" data-testid="waitlist-mini-form">
      <p className="text-sm font-bold">✋ Liste d'attente — {dest.group} · {dest.label}{dest.transport ? ` · ${dest.transport}` : ''}</p>
      <p className="text-[11px] text-white/75">Laissez vos coordonnées : nous vous prévenons (email + notification) dès qu'une place se libère, dans l'ordre des demandes.</p>
      <div className="grid grid-cols-2 gap-2">
        <input value={f.nom} onChange={e => setF({ ...f, nom: e.target.value.toUpperCase() })} placeholder="NOM *" required
          className="px-2 py-2 rounded text-xs text-gray-800 bg-white" data-testid="waitlist-mini-nom" />
        <input value={f.prenom} onChange={e => setF({ ...f, prenom: e.target.value })} placeholder="Prénom *" required
          className="px-2 py-2 rounded text-xs text-gray-800 bg-white" data-testid="waitlist-mini-prenom" />
        <input type="email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} placeholder="Email"
          className="px-2 py-2 rounded text-xs text-gray-800 bg-white" data-testid="waitlist-mini-email" />
        <input type="tel" value={f.telephone} onChange={e => setF({ ...f, telephone: e.target.value })} placeholder="Téléphone"
          className="px-2 py-2 rounded text-xs text-gray-800 bg-white" data-testid="waitlist-mini-tel" />
      </div>
      <div className="flex items-center gap-2">
        <label className="text-[11px]">Nombre de personnes :</label>
        <input type="number" min="1" max="10" value={f.nbPersonnes} onChange={e => setF({ ...f, nbPersonnes: e.target.value })}
          className="w-16 px-2 py-1.5 rounded text-xs text-gray-800 bg-white" data-testid="waitlist-mini-nb" />
      </div>
      {state === 'err' && <p className="text-red-300 text-[11px]" data-testid="waitlist-mini-error">Renseignez nom, prénom et au moins un email ou un téléphone, puis réessayez.</p>}
      <div className="flex gap-2">
        <button type="button" onClick={submit} disabled={state === 'sending'}
          className="flex-1 bg-white text-[#1E3A5F] px-3 py-2 rounded-lg text-xs font-bold hover:bg-gray-100 disabled:opacity-50 transition"
          data-testid="waitlist-mini-submit">
          {state === 'sending' ? 'Envoi…' : "M'inscrire en liste d'attente"}
        </button>
        {onDone && <button type="button" onClick={onDone} className="px-3 py-2 text-xs text-white/70 hover:text-white" data-testid="waitlist-mini-close">Fermer</button>}
      </div>
    </div>
  );
};

const RegistrationForm = () => {
  const navigate = useNavigate();
  // Iter 103a : destinations chargées depuis MongoDB (collection form_destinations)
  // au lieu d'un fichier statique. Source de vérité unique.
  const {
    destinations: DESTINATIONS,
    destTripMap: DEST_TRIP_MAP,
    loaded: destinationsLoaded,
    reload: reloadDestinations,
    isDestinationVisible,
    getMaxOccupantsChambre,
    getChambreIndividuelleInfo,
  } = useDestinations();
  const [submitted, setSubmitted] = useState(false);
  const [espaceDirect, setEspaceDirect] = useState(null);
  const [redirectSeconds, setRedirectSeconds] = useState(30);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState('');  // Iter 887 — 'semaine_ajout' : ajout de semaine → organisateurs
  // Iter 263 — liste d'attente publique : date complète choisie via bouton / offre après refus 409
  const [waitlistDest, setWaitlistDest] = useState(null);
  const [waitlistOffer, setWaitlistOffer] = useState(null);
  const [waitlistLeg, setWaitlistLeg] = useState(null);  // Iter 750 — liste d'attente pour UN trajet complet (aller ou retour), la semaine restant réservable
  const [completIds, setCompletIds] = useState(new Set());
  const [dispoMap, setDispoMap] = useState({});
  const [excursionsProg, setExcursionsProg] = useState(null);
  // Iter 262 — statut du chargement des disponibilités : tant que ce n'est pas 'ok',
  // on bloque le choix de la date (bug Imane 28/07 : fetch silencieusement échoué → rien de grisé).
  const [availStatus, setAvailStatus] = useState('loading'); // 'loading' | 'ok' | 'error'
  const [availReload, setAvailReload] = useState(0);
  const [spotsMap, setSpotsMap] = useState({});
  // Iter 199 — Lien d'invitation privé (?invitation=TOKEN) : débloque un voyage
  // affiché COMPLET pour la personne qui possède le lien (valable 24h).
  const [invitation, setInvitation] = useState(null);
  const [availableAvoirs, setAvailableAvoirs] = useState([]);
  const [soloHelpOpen, setSoloHelpOpen] = useState(false); // legacy, retiré
  const [soloHelpShown, setSoloHelpShown] = useState(false); // legacy, retiré
  const [validationModal, setValidationModal] = useState({ open: false, issues: [] });
  // Iter 862 (Kamel) — après une tentative de validation, les champs / cases manquants sont encadrés en rouge
  // jusqu'à ce qu'ils soient remplis ; le message d'erreur est amené à l'écran au lieu de remonter en haut.
  const [validationTentee, setValidationTentee] = useState(false);
  const champsManquantsRef = useRef([]);
  const [chambreIndOverrides, setChambreIndOverrides] = useState({}); // { trip_id: {disabled, montant} }
  // Iter 767 (Kamel — smartphone) : formulaire en 4 écrans sur téléphone (voyage → voyageurs → options → validation).
  // Sur ordinateur (≥ 640 px) tout reste affiché d'un bloc comme avant.
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches);
  const [etape, setEtape] = useState(1);
  const [etapeIssues, setEtapeIssues] = useState([]);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const h = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener('change', h) : mq.addListener(h);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', h) : mq.removeListener(h); };
  }, []);
  const stepperOn = isMobile;
  const ETAPES = [[1, 'Voyage'], [2, 'Voyageurs'], [3, 'Options'], [4, 'Validation']];
  const st = (n) => (stepperOn && etape !== n ? ' hidden' : '');
  const etapeDeAnchor = (it) => {
    const a = it.anchor || '';
    if (it.id === 'tiers' || a.startsWith('autorisation-parentale')) return 4;
    if (a === 'section-admin' || a === 'section-depart') return 1;
    if (/^(voyageur-|section-voyageurs|section-lloret-chambres|lloret-compagnon|lloret-chambre|chambre-|warning-solo|input-telephone|section-adresse)/.test(a)) return 2;
    if (/^(section-adhesion|section-assurance|section-calcul|section-placement)/.test(a)) return 3;
    return 4;
  };
  // Iter 896 (Kamel) — copier-coller et saisie semi-automatique AUTORISÉS sur la confirmation
  // (trop d'abandons) ; seule l'identité email/téléphone avec les coordonnées reste vérifiée.
  // Iter 900 — case « J'ai vérifié mon e-mail et mon téléphone » obligatoire, remise à zéro si l'un des deux change.
  const [coordsVerifiees, setCoordsVerifiees] = useState(false);
  const coordsVerifieesPour = useRef('');
  // Iter 89 : aperçu PDF avant envoi
  const [previewPdfUrl, setPreviewPdfUrl] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  // Sélecteur de destination cascade (iter 69) : étape 1 = groupe, étape 2 = période (Lloret), étape 3 = date précise
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState(''); // Lloret uniquement: 'mai' | 'ete'
  // Iter 87 : indicateur de progression supprimé (surchargeait le formulaire).
  // const [progressExpanded, setProgressExpanded] = useState(true);

  // Iter 266 — Compteur de visites par source réseau (1 fois par session)
  useEffect(() => {
    try {
      if (sessionStorage.getItem('visit_tracked')) return;
      const params = new URLSearchParams(window.location.search);
      let source = (params.get('src') || params.get('utm_source') || '').toLowerCase();
      if (!source) {
        const ref = (document.referrer || '').toLowerCase();
        if (ref.includes('tiktok')) source = 'tiktok';
        else if (ref.includes('instagram')) source = 'instagram';
        else if (ref.includes('facebook') || ref.includes('fb.')) source = 'facebook';
        else if (ref.includes('snapchat')) source = 'snapchat';
        else if (ref.includes('whatsapp') || ref.includes('wa.me')) source = 'whatsapp';
        else if (ref.includes('google')) source = 'google';
        else source = 'direct';
      }
      sessionStorage.setItem('visit_tracked', '1');
      fetch(`${API}/api/track-visit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source, path: '/inscription' }),
      }).catch(() => { });
    } catch { /* tracking best-effort */ }
  }, []);

  // Charger la disponibilité des voyages depuis la BDD
  useEffect(() => {
    // Iter 772 (Kamel — prod « impossible de vérifier les places ») : chaque appel est tolérant ; on n'affiche l'erreur
    // que si LES DEUX sources (fiches voyage + compteur de places) ont échoué. Le compteur /disponibilites fait foi.
    let okTrips = true, okDispo = true;
    Promise.all([
      fetch(`${API}/api/trips`).then(r => (r.ok ? r.json() : Promise.reject(new Error(r.status)))).catch(() => { okTrips = false; return []; }),
      fetch(`${API}/api/disponibilites`).then(r => (r.ok ? r.json() : Promise.reject(new Error(r.status)))).catch(() => { okDispo = false; return {}; }),
    ])
      .then(([trips, dispo]) => {
        if (!okTrips && !okDispo) throw new Error('aucune source');
        if (!Array.isArray(trips)) trips = [];
        const full = new Set();
        const spots = {};
        Object.entries(DEST_TRIP_MAP).forEach(([destId, tm]) => {
          const { dateLabel } = tm;
          const match = findTripForDest(trips, tm, destId);
          if (!match) return;
          // Toggle admin "voyage complet" (iter 73) — prioritaire sur available_spots
          if (match.sold_out) {
            spots[destId] = 0;
            full.add(destId);
            return;
          }
          // Pour Lloret Été: vérifier les places par semaine
          if (match.weekly_availability && destId.startsWith('lloret-8j-')) {
            const weekKey = destId.replace('lloret-8j-', '');
            const weekSpots = match.weekly_availability[weekKey];
            if (weekSpots !== undefined && weekSpots !== -1) {
              spots[destId] = weekSpots;
              if (weekSpots <= 0) full.add(destId);
            } else if (weekSpots === -1) {
              spots[destId] = -1;
            }
          } else if (match.weekly_availability && destId.startsWith('lloret-4j-')) {
            const weekKey = destId.replace('lloret-4j-', '');
            const weekSpots = match.weekly_availability[weekKey];
            if (weekSpots !== undefined && weekSpots !== -1) {
              spots[destId] = weekSpots;
              if (weekSpots <= 0) full.add(destId);
            } else if (weekSpots === -1) {
              spots[destId] = -1;
            }
          } else if (match.date_options && dateLabel) {
            const dateOpt = match.date_options.find(d => d.label?.toLowerCase().includes(dateLabel));
            if (dateOpt) {
              if (dateOpt.spots !== undefined) spots[destId] = dateOpt.spots;
              if (dateOpt.status === 'liste_attente') { spots[destId] = 0; full.add(destId); }
              else if (dateOpt.status !== 'disponible') full.add(destId);
            }
          } else if (match.available_spots !== undefined) {
            if (match.available_spots !== -1) {
              spots[destId] = match.available_spots;
              if (match.available_spots <= 0) full.add(destId);
            } else {
              spots[destId] = -1;
            }
          }
        });
        // Iter 329 (Kamel) — COMPTEUR AUTOMATIQUE : capacité (admin, une seule fois)
        // − inscriptions actives = places restantes, calculé en direct par le backend.
        // Prioritaire sur les réglages manuels ci-dessus pour les voyages gérés.
        Object.entries(dispo || {}).forEach(([destId, d]) => {
          // Iter 688 — cartes Aller / Retour : la semaine reste réservable tant qu'UN trajet a des places
          const unTrajetLibre = d.trajets && (d.trajets.aller.restantes !== 0 || d.trajets.retour.restantes !== 0);
          const sansLibre = d.fusion && (!d.sans_transport || d.sans_transport.restantes !== 0);  // Iter 691 — 3e compteur « sans transport » (-1 = illimité)
          if (d.statut === 'complet' && !unTrajetLibre && !sansLibre) { full.add(destId); spots[destId] = 0; }
          else { full.delete(destId); spots[destId] = d.fusion ? d.restantes : d.trajets ? Math.max(d.trajets.aller.restantes, d.trajets.retour.restantes) : d.restantes; }
        });
        if (invitation?.valid && invitation.dest_id) {
          full.delete(invitation.dest_id);
          spots[invitation.dest_id] = -1;
        }
        setDispoMap(dispo || {});
        setCompletIds(full);
        setSpotsMap(spots);
        // Construire la map des overrides (config tarifaire + chambre ind) par destId (iter 73)
        const overrides = {};
        Object.entries(DEST_TRIP_MAP).forEach(([destId, tm]) => {
          const match = findTripForDest(trips, tm, destId);
          if (!match) return;
          overrides[destId] = {
            disabled: !!match.chambre_ind_disabled,
            montant: match.chambre_ind_montant,
            chambre_ind_weekly: match.chambre_ind_weekly || {},
            reduction_3_11: match.reduction_3_11 ?? null,
            reduction_bebe: match.reduction_bebe ?? null,
            triple_disponible: match.triple_disponible ?? null,
            quadruple_disponible: match.quadruple_disponible ?? null,
            sold_out: !!match.sold_out,
          };
        });
        setChambreIndOverrides(overrides);
        setAvailStatus('ok');
      })
      .catch(() => setAvailStatus('error'));
  }, [DEST_TRIP_MAP, invitation, availReload]);

  // Iter 199 — Vérifie le lien d'invitation présent dans l'URL au chargement
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('invitation');
    if (!token) return;
    fetch(`${API}/api/invitations/${token}`)
      .then(r => r.json())
      .then(setInvitation)
      .catch(() => {});
  }, []);

  // Iter 492 (Kamel) — ?destination=<id> (bouton « Je m'inscris à ce voyage » de l'assistant IA) : pré-sélection du voyage
  const destParamAppliedRef = React.useRef(false);
  useEffect(() => {
    if (destParamAppliedRef.current || !DESTINATIONS.length) return;
    const wanted = new URLSearchParams(window.location.search).get('destination');
    if (!wanted) return;
    const dest = DESTINATIONS.find(d => d.id === wanted);
    destParamAppliedRef.current = true;
    if (!dest) return;
    setSelectedGroup(dest.group);
    changeDestination(dest.id);
    setTimeout(() => document.querySelector('[data-testid="destination-select"], #destination, [name="destination"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 400);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DESTINATIONS]);

  // Iter 731 (Kamel) — ?devis=1 : pré-remplissage depuis le simulateur (/simulateur-espagne) : séjour, voyageurs (dates de naissance), chambres, trajets, départ
  const devisAppliedRef = React.useRef(false);
  const [devisPrefill, setDevisPrefill] = useState(null);
  useEffect(() => {
    if (devisAppliedRef.current || !DESTINATIONS.length) return;
    if (new URLSearchParams(window.location.search).get('devis') !== '1') return;
    let d = null;
    try { d = JSON.parse(sessionStorage.getItem('raav_devis_prefill') || 'null'); } catch { d = null; }
    devisAppliedRef.current = true;
    if (!d) return;
    sessionStorage.removeItem('raav_devis_prefill');
    const dest = DESTINATIONS.find(x => x.id === d.destination);
    if (!dest) { setDevisPrefill({ ...d, indisponible: true }); return; }
    setSelectedGroup(dest.group);
    changeDestination(dest.id);
    setForm(p => ({
      ...p,
      voyageurs: (d.voyageurs || []).map(v => ({ nom: '', prenom: '', dateNaissance: v.dateNaissance || '', civilite: '', telephone: '', forfait: '' })),
      chambres: (d.chambres || []).map(c => ({ occupants: c.occupants || [], compagnons: [], autreOccupants: '', typeChambre: c.typeChambre || '', partageSolo: false, vuePiscine: !!c.vuePiscine })),
      busTrajets: d.busTrajets || p.busTrajets,
      nbSemaines: Math.max(1, Math.min(3, parseInt(d.semaines, 10) || 1)),
      deuxSemaines: 'non',  // Iter 876 — « 2 semaines » = nbSemaines (séjour multi-semaines), jamais déduit du trajet
      lieuDepart: d.lieuDepart || '',
      aireAutoroute: d.aireAutoroute || '',
      aireAutorouteCode: d.aireAutorouteCode || '',
      assurance: d.assurance === 'oui' ? 'oui' : p.assurance,
      montantAssurance: d.assurance === 'oui' ? String(dest.assurance || 0) : p.montantAssurance,
      demandesSpeciales: d.demandesSpeciales || '',
    }));
    setDevisPrefill(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DESTINATIONS]);

  // Iter 199 — Auto-sélection du voyage invité (une seule fois, dès que le catalogue est chargé)
  const invitationAppliedRef = React.useRef(false);
  useEffect(() => {
    if (invitationAppliedRef.current || !invitation?.valid) return;
    const dest = DESTINATIONS.find(d => d.id === invitation.dest_id);
    if (!dest) return;
    invitationAppliedRef.current = true;
    setSelectedGroup(dest.group);
    changeDestination(dest.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitation, DESTINATIONS]);

  // Iter 361 (Kamel) — Mode EXPRESS : masque les blocs pédagogiques pour les habitués
  // Iter 576 (Kamel) — « Inscription express » SUPPRIMÉE : un seul parcours, tout le monde lit les mêmes explications (déjà épurées).
  const expressMode = false;
  try { localStorage.removeItem('raav_express'); } catch { /* ignore */ }
  // Iter 362 (Kamel) — Pré-remplissage habitués via email connu
  const [prefill, setPrefillData] = useState(null);
  const [prefillDone, setPrefillDone] = useState(false);
  const checkPrefill = async (email) => {
    const e = (email || '').trim();
    if (prefillDone || !e || !e.includes('@') || form.rue.trim()) return;
    try {
      const r = await window.fetch(`${API}/api/prefill-adherent?email=${encodeURIComponent(e)}`);
      const d = await r.json();
      if (d.found) setPrefillData(d);
    } catch { /* silencieux */ }
  };
  const appliquerPrefill = () => {
    if (!prefill) return;
    setForm(prev => ({
      ...prev,
      rue: prev.rue.trim() ? prev.rue : (prefill.rue || ''),
      codePostal: prev.codePostal.trim() ? prev.codePostal : (prefill.codePostal || ''),
      ville: prev.ville.trim() ? prev.ville : (prefill.ville || ''),
      telephone: prev.telephone.trim() ? prev.telephone : (prefill.telephone || ''),
      voyageurs: (prev.voyageurs.length === 1 && !prev.voyageurs[0].nom.trim() && (prefill.voyageurs || []).length > 0)
        ? prefill.voyageurs.map(v => ({ nom: v.nom || '', prenom: v.prenom || '', dateNaissance: v.dateNaissance || '', telephone: v.telephone || '', forfait: '' }))
        : prev.voyageurs,
    }));
    setPrefillData(null);
    setPrefillDone(true);
  };

  const T = useFormTextes();
  const [form, setForm] = useState({
    telephone: '',
    canauxContact: ['whatsapp', 'email'],
    destination: '',
    lieuDepart: '',
    aireAutoroute: '',
    aireAutorouteCode: '',
    aireSens: '',
    deuxSemaines: 'non',
    nbSemaines: 1,
    busTrajets: 'aller_retour',
    excursionsPre: [],
    voyageurs: [
      { nom: '', prenom: '', dateNaissance: '', telephone: '', forfait: '' },
    ],
    rue: '',
    codePostal: '',
    ville: '',
    email: '',
    adhesion: '',
    montantAdhesion: '',
    assurance: '',
    montantAssurance: '',
    paiement: '',
    montantCalcule: '',
    modeReglement: '',
    modesReglement: [], // Array : permet la multi-sélection (2026-02)
    avoirMontant: '',
    avoirVoyageOrigine: '',
    avoirReglementIdSource: '',
    avoirRegIdSource: '',
    rempliPar: 'moi',
    rempliParTiersNom: '',
    contactPrenom: '', contactNom: '', contactSource: '', contactVoyageurIndex: null, telAttestation: false, confirmeTrajetUnique: false,  // Iter 866/867/869/877 — personne contact pour ce formulaire (à qui appartiennent téléphone + e-mail) + attestation « c'est mon numéro »
    rempliParTiersPrenom: '',
    rempliParTiersLien: '',
    autorisationParentale: false,
    canalReglement: '',
    formalites: [],
    formalitesStatut: {},
    voyageAvec: '',
    placementAutocarAutre: '',
    demandesSpeciales: '',
    placementAutocarPersonnes: [{ nom: '', prenom: '' }],
    chambres: [{ occupants: [], compagnons: [], autreOccupants: '', typeChambre: '' }],
    certifieAdresse: false,
    accepteConditions: false,
    // Iter 154L — Acceptation obligatoire de la Charte de placement bus
    // (uniquement pour les voyages "Avec transport"). Simple booléen — pas de
    // trace BDD, l'objectif est un formulaire fluide.
    accepteChartePlacementBus: false,
    dateInscription: new Date().toLocaleDateString('fr-FR'),
  });

  const selectedDest = DESTINATIONS.find(d => d.id === form.destination);
  // Iter 744 (Kamel) — plus de choix d'excursions au formulaire : tout se passe dans l'Espace Adhérent, dans un second temps.
  useEffect(() => { setExcursionsProg(null); }, [form.destination]);
  // Iter 691 (Kamel) — Espagne 2027 FUSIONNÉE : une semaine = une seule ligne ; le car est une OPTION (comme l'assurance) :
  // 🏨 sans transport / 🛫 aller / 🛬 retour, prix par trajet (60 € été, 50 € mai) ajouté au tarif hôtel ; 3 compteurs (sans / aller / retour).
  const isFusion = !!(selectedDest && typeof selectedDest.transport_option === 'object' && selectedDest.transport_option);
  const transportActif = isFusion && !!selectedDest.transport_option.actif;
  const isEspagne = selectedDest?.espagne;
  // Iter 870 — autoroutes réellement empruntées pour ce voyage (même règle que le serveur) : aire possible pour TOUT voyage en car
  const [autoroutesOk, setAutoroutesOk] = useState(['A47', 'A46']);
  const [autoroutesLib, setAutoroutesLib] = useState({});
  const [autoroutesInfo, setAutoroutesInfo] = useState({});
  const [aireAutre, setAireAutre] = useState(false);
  useEffect(() => {
    if (!form.destination) return;
    let actif = true;
    axios.get(`${API}/api/autoroutes/${encodeURIComponent(form.destination)}`, { params: { label: `${selectedDest?.group || ''} ${selectedDest?.label || ''}` } })
      .then(r => { if (actif) { setAutoroutesOk(r.data.autoroutes.map(a => a.code)); setAutoroutesLib(Object.fromEntries(r.data.autoroutes.map(a => [a.code, a.label]))); setAutoroutesInfo(Object.fromEntries(r.data.autoroutes.map(a => [a.code, a]))); } })
      .catch(() => {});
    return () => { actif = false; };
  }, [form.destination, selectedDest?.label]);
  // Iter 931 — aires déjà validées par l'association pour l'autoroute choisie (sens aller) ; « autre aire » = saisie libre à confirmer
  const airesProposees = (autoroutesInfo[form.aireAutorouteCode]?.aires || []).filter(a => a.sens !== 'retour');
  const aireDansListe = airesProposees.some(a => a.nom === form.aireAutoroute);
  const choisirAire = (v) => {
    if (v === '__autre__') { setAireAutre(true); setForm(f => ({ ...f, aireAutoroute: '', aireSens: '' })); return; }
    setAireAutre(false);
    setForm(f => ({ ...f, aireAutoroute: v, aireSens: v ? (autoroutesInfo[f.aireAutorouteCode]?.sensAller || '') : '' }));
  };
  const isAvion = selectedDest?.avion;
  // Iter 688 (Kamel) — Espagne été avec car : le car se vend PAR TRAJET (aller 60 € / retour 60 €, tarif unique quel que soit l'âge).
  // Un trajet complet est grisé ; 2 semaines consécutives = Aller sur la 1re, Retour sur la 2e.
  const isLloretEteCar = !isFusion && !!(selectedDest?.car && selectedDest?.espagne && /8j|été|ete/i.test(`${selectedDest.id || ''} ${selectedDest.group || ''}`));
  const showTrajets = isLloretEteCar || transportActif;
  const dispoSel = dispoMap[form.destination];
  const trajetsSepares = showTrajets && !!dispoSel?.trajets;
  const parsePrix = (s) => parseInt(String(s || '').replace(/[^\d]/g, ''), 10) || 0;
  const destSans = isLloretEteCar ? DESTINATIONS.find(d => d.id !== selectedDest.id && !d.car && (
    selectedDest.parent_group ? d.parent_group === selectedDest.parent_group : (d.group === selectedDest.group && d.dateDepart === selectedDest.dateDepart))) : null;
  const supplementCar = destSans ? Math.max(0, parsePrix(selectedDest.price) - parsePrix(destSans.price)) : 0;
  const prixTrajet = transportActif ? (parseInt(selectedDest.transport_option.prix_trajet, 10) || 0) : isLloretEteCar ? Math.round(supplementCar / 2) : 0;
  const trajetsLegs = (isFusion && !transportActif) ? [] : form.busTrajets === 'aller_seul' ? ['aller'] : form.busTrajets === 'retour_seul' ? ['retour'] : form.busTrajets === 'aucun' ? [] : ['aller', 'retour'];
  const remiseTrajet = isLloretEteCar ? (2 - trajetsLegs.length) * prixTrajet : 0;
  // Iter 748 (Kamel) — séjour de 1, 2 ou 3 semaines consécutives en une seule inscription : l'aller se fait sur la 1re semaine,
  // le retour sur la dernière, rien entre les deux. Une fiche par semaine est créée côté serveur.
  const nbSemaines = Math.max(1, Math.min(3, parseInt(form.nbSemaines, 10) || 1));
  const semainesSuite = (() => {
    if (!isFusion || !selectedDest?.dateFin) return [];
    const out = []; let cur = selectedDest;
    for (let k = 0; k < 2; k++) {
      const next = DESTINATIONS.find(d => d.group === cur.group && d.dateDepart && d.dateDepart === cur.dateFin && !d.is_hidden_public);
      if (!next) break;
      const sp = spotsMap[next.id];
      if (sp !== undefined && sp !== -1 && sp <= 0) break;
      out.push(next); cur = next;
    }
    return out;
  })();
  const nbSemainesEff = Math.min(nbSemaines, 1 + semainesSuite.length);
  // Iter 777 (Kamel) — « retour » coché : seules les combinaisons dont la DERNIÈRE semaine a un car retour sont proposées
  const semaineRetourOk = (d) => !!(d?.transport_option && typeof d.transport_option === 'object' && d.transport_option.actif) && (dispoMap[d.id]?.trajets?.retour?.restantes ?? -1) !== 0;
  const retourCoche = form.busTrajets === 'aller_retour' || form.busTrajets === 'retour_seul';
  const nbSemainesOk = (n) => n === 1 || !retourCoche || semaineRetourOk(semainesSuite[n - 2]);
  useEffect(() => {
    if (nbSemaines > 1 && !nbSemainesOk(nbSemaines)) {
      const best = [3, 2, 1].find(n => n <= 1 + semainesSuite.length && nbSemainesOk(n)) || 1;
      set('nbSemaines', best);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.busTrajets, form.destination, nbSemaines, semainesSuite.length]);
  // Iter 775 (Kamel) — séjour ISOLÉ (ex. mai : 6→9 et 15→18, rien ne se suit) : pas de logique « aller sur la 1re / retour sur la 2e »
  const sejourIsole = !!(selectedDest && !DESTINATIONS.some(d => d.id !== selectedDest.id && d.group === selectedDest.group && !d.is_hidden_public &&
    ((d.dateDepart && d.dateDepart === selectedDest.dateFin) || (d.dateFin && d.dateFin === selectedDest.dateDepart))));
  const legsSemaine1 = nbSemainesEff > 1 ? trajetsLegs.filter(l => l === 'aller') : trajetsLegs;
  const supplementTrajets = isFusion ? legsSemaine1.length * prixTrajet : 0;
  const busTrajetsSemaine1 = nbSemainesEff > 1 ? (legsSemaine1.length ? 'aller_seul' : 'aucun') : form.busTrajets;
  const legRest = (leg) => dispoSel?.trajets?.[leg]?.restantes;
  const legPlein = (leg) => trajetsSepares && legRest(leg) !== -1 && (legRest(leg) ?? 1) <= 0;
  // Iter 877 (Kamel — DALILA : retour oublié, handicap numérique) : 1 seule semaine + un seul trajet coché alors que l'autre car a de la place
  const trajetManquant = trajetsLegs.length === 1 ? (trajetsLegs[0] === 'aller' ? 'retour' : 'aller') : null;
  const trajetUniqueOubli = !!(isFusion && transportActif && !sejourIsole && nbSemainesEff === 1 && trajetManquant && !legPlein(trajetManquant));
  const sansRest = dispoSel?.sans_transport?.restantes;
  const sansPlein = isFusion && sansRest !== undefined && sansRest !== -1 && sansRest <= 0;
  // Iter 752 (Kamel) — l'hôtel compte des CHAMBRES : la fiche doit tenir dans les chambres restantes de chaque semaine demandée
  const hotelRestCh = (() => {
    if (!isFusion || !selectedDest) return null;
    const rests = [selectedDest, ...semainesSuite.slice(0, Math.max(0, nbSemainesEff - 1))].map(d => dispoMap[d.id]?.hotel?.restantes).filter(v => v !== undefined && v !== null);
    return rests.length ? Math.min(...rests) : null;
  })();
  const nbChambresDemandees = (form.chambres || []).length || 1;
  const chambresManquent = hotelRestCh !== null && nbChambresDemandees > hotelRestCh;
  // Le lieu de départ n'a de sens qu'avec un trajet en car
  const showBusDepart = isFusion ? trajetsLegs.length > 0 : selectedDest?.car;
  const setTrajets = (legs) => {
    const val = legs.length === 2 ? 'aller_retour' : legs.length === 0 ? 'aucun' : legs[0] === 'aller' ? 'aller_seul' : 'retour_seul';
    setForm(p => ({ ...p, busTrajets: val, deuxSemaines: 'non', confirmeTrajetUnique: false, ...(val === 'aucun' ? { lieuDepart: '', aireAutoroute: '', accepteChartePlacementBus: false } : {}) }));  // Iter 876/877
    setError('');
  };
  useEffect(() => {
    // trajet complet → décoché automatiquement (1 semaine = les deux trajets par défaut) ; séjour isolé : un car complet = plus de car du tout
    if (!trajetsSepares) return;
    const legs = trajetsLegs.filter(l => !legPlein(l));
    if (sejourIsole && legs.length !== trajetsLegs.length) { if (trajetsLegs.length) setTrajets([]); return; }
    if (sejourIsole && legs.length === 1) { setTrajets([]); return; }
    if (legs.length !== trajetsLegs.length) setTrajets(legs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.destination, trajetsSepares, dispoSel?.trajets?.aller?.restantes, dispoSel?.trajets?.retour?.restantes]);
  // Iter 154L — Détection "voyage AVEC transport en autocar" (par destination ID
  // suffixé `-avec` ou par champ transport='Avec transport') → impose
  // l'acceptation de la Charte de placement bus.
  const isAvecTransportBus = (
    selectedDest &&
    (
      (isFusion && trajetsLegs.length > 0) ||
      (!isFusion && (String(selectedDest.transport || '').toLowerCase().includes('avec') ||
      String(selectedDest.id || '').endsWith('-avec')))
    ) &&
    !isAvion
  );
  // Voyage avec nuitée = 2 jours ou plus (pas Turin journée, pas Grau-du-Roi journée)
  // Voyage « journée » (sans nuitée) = pas de chambres / adhésion & assurance journée.
  // Mécanisé : catégorie 'journee' OU départ = retour (les anciens ids restent en secours).
  const hasNuitee = selectedDest && !(
    ['turin-23mai', 'turin-20juin', 'grau-du-roi'].includes(selectedDest.id) ||
    selectedDest.category === 'journee' ||
    (selectedDest.dateDepart && selectedDest.dateFin && selectedDest.dateDepart === selectedDest.dateFin)
  );
  // Année du voyage (depuis dateDepart au format ISO). Par défaut 2026 si rien.
  const voyageYear = (() => {
    const d = selectedDest?.dateDepart || '';
    const m = d.match(/^(\d{4})-/);
    return m ? parseInt(m[1], 10) : 2026;
  })();
  // Iter 763 (Kamel) — adhésion de l'ANNÉE DU VOYAGE : si cet email a déjà cotisé sur un voyage de la même année, on pré-coche « Déjà inscrit »
  const [adhesionDeja, setAdhesionDeja] = useState(null);
  useEffect(() => {
    const e = (form.email || '').trim();
    if (!hasNuitee || !voyageYear || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { setAdhesionDeja(null); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/api/adhesion-statut`, { params: { email: e, annee: voyageYear } }).then(r => {
        setAdhesionDeja(r.data?.deja ? r.data : null);
        if (r.data?.deja && form.adhesion !== 'deja-inscrit') { set('adhesion', 'deja-inscrit'); set('montantAdhesion', '0'); set('adhesionVoyagePrecedent', r.data.voyage || ''); }
      }).catch(() => setAdhesionDeja(null));
    }, 600);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.email, voyageYear, hasNuitee]);

  // Iter 733 (Kamel) — vue piscine garantie : supplément PAR CHAMBRE pour tout le séjour (40 € jusqu'à 4 nuits, 80 € la semaine), Lloret uniquement
  const nuitsSejour = (() => { const a = selectedDest?.dateDepart, b = selectedDest?.dateFin; if (!a || !b) return 0; return Math.round((new Date(b) - new Date(a)) / 86400000); })();
  const piscineMontantChambre = nuitsSejour > 0 ? (nuitsSejour <= 4 ? 40 : 80) : 0;
  const piscineDispo = !!(selectedDest && /lloret/i.test(`${selectedDest.id || ''} ${selectedDest.group || ''}`) && piscineMontantChambre > 0);
  const supplementPiscine = piscineDispo ? (form.chambres || []).filter(c => c.vuePiscine && (c.occupants || []).length > 0).length * piscineMontantChambre : 0;

  const set = (key, val) => { setForm(p => ({ ...p, [key]: val })); setError(''); };
  // Iter 900 — toute modification de l'e-mail ou du téléphone invalide la case « J'ai vérifié »
  useEffect(() => {
    const cle = `${(form.email || '').trim().toLowerCase()}|${(form.telephone || '').replace(/\D/g, '')}`;
    if (coordsVerifiees && coordsVerifieesPour.current && coordsVerifieesPour.current !== cle) setCoordsVerifiees(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.email, form.telephone]);

  // Iter 867 — la personne contact choisie dans la liste suit toute correction du nom / prénom de l'inscrit (même orthographe garantie)
  useEffect(() => {
    if (form.contactSource !== 'voyageur' || form.contactVoyageurIndex == null) return;
    const v = (form.voyageurs || [])[form.contactVoyageurIndex];
    const prenom = (v?.prenom || '').trim(), nom = (v?.nom || '').trim().toUpperCase();
    if (prenom !== (form.contactPrenom || '') || nom !== (form.contactNom || '')) setForm(p => ({ ...p, contactPrenom: prenom, contactNom: nom }));
  }, [form.voyageurs, form.contactSource, form.contactVoyageurIndex, form.contactPrenom, form.contactNom]);
  // Iter 769 (Kamel — smartphone) : BROUILLON gardé sur l'appareil (localStorage) pour reprendre plus tard sans tout ressaisir.
  const BROUILLON_KEY = 'raav_inscription_brouillon';
  // Iter 825 (Kamel) — ?separe=1 : formulaire ouvert pour un AUTRE foyer (même voyage) depuis un formulaire en cours →
  // aucun brouillon local ni serveur (ne doit jamais écraser / mélanger le brouillon du 1er foyer)
  const [formulaireSepare] = useState(() => new URLSearchParams(window.location.search).get('separe') === '1');
  const [brouillon, setBrouillon] = useState(() => {
    try {
      if (new URLSearchParams(window.location.search).toString()) return null; // lien devis / invitation / destination : pas de reprise
      const b = JSON.parse(localStorage.getItem(BROUILLON_KEY) || 'null');
      return b && b.savedAt && Date.now() - b.savedAt < 7 * 86400000 && b.form ? b : null;
    } catch { return null; }
  });
  const [brouillonRepris, setBrouillonRepris] = useState(false);
  const formRempli = !!(form.destination || form.telephone || form.email || (form.voyageurs || []).some(v => v.nom || v.prenom));
  useEffect(() => {
    if (submitted || brouillon || formulaireSepare) return; // la bannière « reprendre ? » est affichée : on n'écrase pas le brouillon
    if (!formRempli) return;
    const t = setTimeout(() => {
      try { localStorage.setItem(BROUILLON_KEY, JSON.stringify({ form, selectedGroup, selectedPeriod, etape, savedAt: Date.now() })); } catch { /* quota */ }
    }, 800);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, selectedGroup, selectedPeriod, etape, submitted, brouillon, formRempli]);
  useEffect(() => { if (submitted && !formulaireSepare) { try { localStorage.removeItem(BROUILLON_KEY); } catch { /* noop */ } } }, [submitted, formulaireSepare]);
  const reprendreBrouillon = () => {
    const b = brouillon;
    setForm(p => ({ ...p, ...b.form, dateInscription: new Date().toLocaleDateString('fr-FR') }));
    if (b.selectedGroup) setSelectedGroup(b.selectedGroup);
    if (b.selectedPeriod) setSelectedPeriod(b.selectedPeriod);
    if (b.etape && stepperOn) setEtape(Math.min(4, Math.max(1, b.etape)));
    setBrouillon(null); setBrouillonRepris(true);
  };
  const jeterBrouillon = () => { try { localStorage.removeItem(BROUILLON_KEY); } catch { /* noop */ } setBrouillon(null); };
  // Iter 770 — copie du brouillon côté serveur dès qu'un e-mail valide est saisi → e-mail « reprendre mon inscription » si abandon (> 2 h)
  const brouillonTokenRef = React.useRef(null);
  useEffect(() => {
    if (submitted || brouillon || !formRempli || formulaireSepare) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email || '')) return;
    const t = setTimeout(async () => {
      try {
        const r = await axios.post(`${API}/api/brouillon`, { token: brouillonTokenRef.current, email: form.email, form, selectedGroup, selectedPeriod, etape });
        brouillonTokenRef.current = r.data.token;
        localStorage.setItem(`${BROUILLON_KEY}_token`, r.data.token);
      } catch { /* silencieux */ }
    }, 3000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, selectedGroup, selectedPeriod, etape, submitted, brouillon, formRempli]);
  useEffect(() => {
    if (formulaireSepare) return; // ni reprise ni jeton du 1er foyer
    try { brouillonTokenRef.current = localStorage.getItem(`${BROUILLON_KEY}_token`) || null; } catch { /* noop */ }
    const tok = new URLSearchParams(window.location.search).get('brouillon');
    if (!tok) return;
    brouillonTokenRef.current = tok;
    axios.get(`${API}/api/brouillon/${encodeURIComponent(tok)}`).then(r => setBrouillon(r.data)).catch(() => setError("Ce lien de reprise n'est plus valable (7 jours) ou l'inscription a déjà été envoyée."));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!submitted || formulaireSepare) return;
    const tok = brouillonTokenRef.current;
    try { localStorage.removeItem(`${BROUILLON_KEY}_token`); } catch { /* noop */ }
    if (tok) axios.post(`${API}/api/brouillon/${encodeURIComponent(tok)}/termine`).catch(() => {});
  }, [submitted]);

  // Iter 464 — 1 numéro = 1 foyer : vérification en direct (non nominative) du téléphone
  // Iter 869 (Kamel) — 1 numéro = 1 PERSONNE CONTACT : conflit nominatif + case « j'atteste que c'est mon numéro »
  const [telCheck, setTelCheck] = useState({ status: null, message: '', autreNom: '' });
  const v0Nom = form.voyageurs?.[0]?.nom || '', v0Prenom = form.voyageurs?.[0]?.prenom || '', v0Ddn = form.voyageurs?.[0]?.dateNaissance || '';
  const ctcPrenom = form.contactPrenom || '', ctcNom = form.contactNom || '';
  useEffect(() => {
    const digits = (form.telephone || '').replace(/\D/g, '');
    const avecContact = ctcPrenom.trim() && ctcNom.trim();
    if (digits.length < 9 || (!avecContact && (!v0Nom.trim() || !v0Prenom.trim()))) { setTelCheck({ status: null, message: '', autreNom: '' }); return; }
    const t = setTimeout(() => {
      fetch(`${API}/api/registrations/verifier-telephone`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ telephone: form.telephone, nom: v0Nom, prenom: v0Prenom, dateNaissance: v0Ddn, contactPrenom: ctcPrenom, contactNom: ctcNom }),
      }).then(r => r.json()).then(j => setTelCheck({ status: j.ok ? 'ok' : (j.conflit_contact ? 'conflit' : 'ko'), message: j.message || '', autreNom: j.autre_nom || '' }))
        .catch(() => setTelCheck({ status: null, message: '', autreNom: '' }));
    }, 600);
    return () => clearTimeout(t);
  }, [form.telephone, v0Nom, v0Prenom, v0Ddn, ctcPrenom, ctcNom]);

  // Reset COMPLET du formulaire quand la destination change — toutes les infos
  // spécifiques au voyage précédent (chambres, voyageurs, PI, forfaits, etc.) sont effacées.
  //
  // INVARIANT (iter 87) : pour tout voyage avec nuitée, `chambres[0].occupants` DOIT
  // contenir [0] dès le `changeDestination`. C'est ce qui permet à
  // `detectChambresIndividuelles` de reconnaître la chambre comme « solo » et
  // d'auto-appliquer le supplément « Chambre individuelle ». Tout reset/refonte
  // future de `chambres` doit préserver ce contrat sous peine de réintroduire
  // le bug récurrent (~6 fois historiquement).
  const changeDestination = (newDestId) => {
    setError('');
    // Iter 87 : pour les voyages avec nuitée, on rattache automatiquement le 1er voyageur
    // à la 1ʳᵉ chambre dès le choix de la destination. Cela garantit que le supplément
    // « chambre individuelle » est immédiatement détecté et appliqué (fix bug récurrent
    // sur Lloret 4j / 8j / NY / Thaïlande / Istanbul).
    const newDest = DESTINATIONS.find(d => d.id === newDestId);
    // Iter 326 — même logique que hasNuitee (bug Turin 17 octobre : la liste
    // codée en dur ne suffisait plus, on détecte via category/dates identiques)
    const newHasNuitee = newDest && !(
      ['turin-23mai', 'turin-20juin', 'grau-du-roi'].includes(newDest.id) ||
      newDest.category === 'journee' ||
      (newDest.dateDepart && newDest.dateFin && newDest.dateDepart === newDest.dateFin)
    );
    const initialOccupants = newHasNuitee ? [0] : [];
    // Iter 99 — Voyage à la journée : 100% à l'inscription, pas d'adhésion, pas d'assurance.
    // On pré-sélectionne automatiquement les valeurs 'journee' pour adhésion et assurance
    // afin que la validation passe même si les blocs correspondants sont masqués.
    const autoAdhesion = newHasNuitee ? '' : 'journee';
    const autoAssurance = newHasNuitee ? '' : 'journee';
    const autoPaiement = newHasNuitee ? '' : '100';
    // Iter 744 (Kamel) — changer de semaine / de séjour (nuitée → nuitée) ne doit PAS vider ce que la famille a déjà saisi :
    // voyageurs, composition des chambres, coordonnées, lieu de départ sont conservés ; les montants (forfaits, type de
    // chambre, assurance, adhésion) sont recalculés / rechoisis pour le nouveau séjour.
    const prevDest = DESTINATIONS.find(d => d.id === form.destination);
    const prevHasNuitee = prevDest && !(prevDest.category === 'journee' || (prevDest.dateDepart && prevDest.dateFin && prevDest.dateDepart === prevDest.dateFin));
    // (changement de zone = changeDestination('') puis choix d'une semaine : on conserve aussi dans ces deux étapes)
    const aDesDonnees = (form.voyageurs || []).some(v => ((v.nom || '') + (v.prenom || '') + (v.dateNaissance || '')).trim());
    const garder = aDesDonnees && (!newDestId || !!newHasNuitee) && (!prevDest || !!prevHasNuitee);
    const busDefaut = newDest?.transport_option ? (newDest.transport_option.actif ? 'aller_retour' : 'aucun') : 'aller_retour';
    setForm({
      destination: newDestId,
      lieuDepart: garder ? form.lieuDepart : '',
      aireAutoroute: garder ? form.aireAutoroute : '',
      aireAutorouteCode: garder ? form.aireAutorouteCode : '',
      deuxSemaines: 'non',
      nbSemaines: 1,
      // Iter 691 — semaine fusionnée : car en option (aller + retour par défaut si un car existe, sinon sans transport)
      busTrajets: garder && newDest?.transport_option?.actif && form.busTrajets ? form.busTrajets : busDefaut,
      excursionsPre: [],
      voyageurs: garder
        ? form.voyageurs.map(v => ({ ...v, forfait: '' }))
        : [{ nom: '', prenom: '', dateNaissance: '', telephone: '', forfait: '' }],
      rue: garder ? form.rue : '',
      codePostal: garder ? form.codePostal : '',
      ville: garder ? form.ville : '',
      email: garder ? form.email : '',
      adhesion: autoAdhesion,
      montantAdhesion: newHasNuitee ? '' : '0',
      assurance: autoAssurance,
      montantAssurance: newHasNuitee ? '' : '0',
      paiement: autoPaiement,
      montantCalcule: '',
      modeReglement: '',
      modesReglement: [],
    avoirMontant: '',
    avoirVoyageOrigine: '',
    avoirReglementIdSource: '',
    avoirRegIdSource: '',
    rempliPar: garder ? form.rempliPar : 'moi',
    rempliParTiersNom: garder ? form.rempliParTiersNom : '',
    contactPrenom: garder ? form.contactPrenom : '', contactNom: garder ? form.contactNom : '', contactSource: garder ? form.contactSource : '', contactVoyageurIndex: garder ? form.contactVoyageurIndex : null,
    rempliParTiersPrenom: garder ? form.rempliParTiersPrenom : '',
    rempliParTiersLien: garder ? form.rempliParTiersLien : '',
    autorisationParentale: false,
      canalReglement: '',
      formalites: [],
      formalitesStatut: {},
      voyageAvec: '',
      placementAutocarChoix: '',
      placementAutocarAutre: '',
    demandesSpeciales: '',
      placementAutocarPersonnes: [{ nom: '', prenom: '' }],
      chambres: garder
        ? form.chambres.map(c => ({ ...c, typeChambre: '', partageSolo: false }))
        : [{ occupants: initialOccupants, compagnons: [], autreOccupants: '', typeChambre: '' }],
      certifieAdresse: false,
      accepteConditions: false,
      supplementChambre: false,
      montantSupplementChambre: '',
      dateInscription: new Date().toLocaleDateString('fr-FR'),
    });
  };

  const setVoyageur = (idx, key, val) => {
    setForm(p => {
      const v = [...p.voyageurs];
      v[idx] = { ...v[idx], [key]: val };
      return { ...p, voyageurs: v };
    });
  };

  // Sync automatique du cascade selector (iter 69) quand la destination est définie
  // via URL (lien direct /inscription?d=...) ou setForm externe. Doit être APRÈS
  // la déclaration de `form` pour éviter "Cannot access 'form' before initialization".
  useEffect(() => {
    if (!form.destination) return;
    const d = DESTINATIONS.find(x => x.id === form.destination);
    if (!d) return;
    if (!selectedGroup) setSelectedGroup(d.group);
    if (d.group.toLowerCase().includes('espagne') && !selectedPeriod) {
      const isMai = /mai\s+2026/i.test(d.label || '');
      setSelectedPeriod(isMai ? 'mai' : 'ete');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.destination]);

  // Auto-format date input as DD/MM/YYYY
  const handleDateInput = (idx, val) => {
    const clean = val.replace(/[^\d]/g, '');
    let formatted = '';
    if (clean.length <= 2) formatted = clean;
    else if (clean.length <= 4) formatted = clean.slice(0, 2) + '/' + clean.slice(2);
    else formatted = clean.slice(0, 2) + '/' + clean.slice(2, 4) + '/' + clean.slice(4, 8);
    setVoyageur(idx, 'dateNaissance', formatted);
  };

  // Calcule l'âge "X ans Y mois" d'un voyageur à la date de départ du voyage sélectionné.
  // Retourne '' si la date n'est pas valide ou si aucune destination n'est sélectionnée.
  const ageLisible = (ddn) => {
    if (!ddn || !/^\d{2}\/\d{2}\/\d{4}$/.test(ddn)) return '';
    const [dd, mm, yyyy] = ddn.split('/').map(Number);
    const born = new Date(yyyy, mm - 1, dd);
    if (isNaN(born.getTime())) return '';
    const ref = selectedDest?.dateDepart ? new Date(selectedDest.dateDepart) : new Date();
    if (isNaN(ref.getTime())) return '';
    let years = ref.getFullYear() - born.getFullYear();
    let months = ref.getMonth() - born.getMonth();
    if (ref.getDate() < born.getDate()) months -= 1;
    if (months < 0) { years -= 1; months += 12; }
    if (years < 0) return '';
    if (years === 0) return `${months} mois`;
    return `${years} an${years > 1 ? 's' : ''}${months > 0 ? ` ${months} mois` : ''}`;
  };

  // Helpers Espagne (Lloret) — règle "2 pleins tarifs par chambre"
  // Les occupants réels d'une chambre = voyageurs du formulaire attribués + compagnons d'un autre formulaire (avec DDN si renseignée) + partageSolo (voyageur solo futur, considéré adulte) + autreOccupants (texte libre, considéré adulte).
  // On trie par âge décroissant, les 2 plus âgés non-bébés paient plein tarif, les suivants (et les bébés) bénéficient de leur réduction.
  // Détection flux "inscription par chambre" : s'applique à tous les voyages avec nuitée (iter 72).
  // Voyages journée (grau-du-roi, turin journée) → ancien flux Voyageurs simple conservé.
  const isLloretTrip = !!(selectedDest?.id && hasNuitee);
  // Alias pour clarté : isLloretTrip est historiquement le nom du flux, étendu désormais à tout voyage nuitée.
  const isLloretOnly = !!(selectedDest?.id && selectedDest.id.startsWith('lloret-'));
  const isNewYork = selectedDest?.id === 'new-york';
  const ageAuDepartNum = (ddn) => {
    if (!ddn || !/^\d{2}\/\d{2}\/\d{4}$/.test(ddn)) return null;
    const [dd, mm, yyyy] = ddn.split('/').map(Number);
    const born = new Date(yyyy, mm - 1, dd);
    const ref = selectedDest?.dateDepart ? new Date(selectedDest.dateDepart) : null;
    if (!ref || isNaN(born.getTime()) || isNaN(ref.getTime())) return null;
    let age = ref.getFullYear() - born.getFullYear();
    const m = ref.getMonth() - born.getMonth();
    if (m < 0 || (m === 0 && ref.getDate() < born.getDate())) age--;
    return age;
  };
  // Catégorie d'âge adaptée au type de voyage (iter 73) :
  // - Voyages en avion : bébé = < 2 ans (règle compagnie aérienne)
  // - Voyages en car / nuitée : bébé = < 3 ans
  const catFromAge = (age) => {
    if (age === null || age === undefined) return 'adult';
    const babyThreshold = isAvion ? 2 : 3;
    if (age < babyThreshold) return 'baby';
    if (age < 12) return 'child';
    return 'adult';
  };

  // Calcule le forfait attendu pour chaque voyageur du formulaire courant (map vIdx → { forfait, detail, category }).
  // Retourne un objet vide si la destination n'est pas Lloret.
  //
  // Règle "optimisation 2 personnes" (iter 70) : reprise du simulateur Espagne.
  // Si chambre = 2 personnes dont au moins 1 non-adulte (enfant 3-11 ou bébé <3),
  // on COMPARE deux scénarios et on prend le moins cher :
  //   A) 2 pleins tarifs = 2 × basePrice
  //   B) 1 plein tarif + supplément chambre individuelle + 1 tarif réduit
  //      (baby ou child discount) = basePrice + singleSupp + (basePrice - disc)
  // Si A <= B : les 2 personnes paient plein tarif (pédagogie : l'enfant 3-11 occupe
  //   un des 2 slots "plein tarif par chambre" requis par l'hôtel).
  // Si B < A : l'adulte paie plein tarif + supplément chambre ind., l'enfant/bébé paie réduit.
  //   Cas typique : adulte + bébé (la réduction bébé étant forte, option B est plus avantageuse).
  // Iter 108 — UNIFIÉ avec utils/pricing.js (source unique de vérité admin↔public).
  // Avant : 146 lignes de code dupliqué entre ce fichier et utils/pricing.js.
  // Maintenant : un simple adaptateur qui appelle computePricing puis enrichit le
  // résultat avec ageText (utilisé pour l'affichage de l'âge sous la DDN).
  // Cf. ARCHITECTURE_MIROIR.md pour la procédure à suivre lors d'un changement métier.
  const computeLloretForfaits = () => {
    if (!isLloretTrip || !selectedDest) return {};
    const r = computePricing({
      destination: selectedDest,
      voyageurs: form.voyageurs,
      chambres: form.chambres,
      chambreIndOverrides,
    });
    const result = { ...r.forfaits };
    // Iter 688 — car vendu par trajet : chaque trajet non pris = −prixTrajet (tarif unique, quel que soit l'âge)
    if (remiseTrajet > 0) {
      const lib = trajetsLegs.length === 0 ? 'sans car' : trajetsLegs[0] === 'aller' ? 'car aller seulement' : 'car retour seulement';
      Object.keys(result).forEach((key) => {
        const f = result[key];
        if (!f || typeof f.forfait !== 'number') return;
        result[key] = { ...f, forfait: Math.max(0, f.forfait - remiseTrajet), detail: `${f.detail} − ${remiseTrajet} € (${lib})` };
      });
    }
    // Iter 691 — fusion : le tarif catalogue = l'hôtel ; l'option car s'ajoute (prixTrajet × trajets, tarif unique quel que soit l'âge)
    if (supplementTrajets > 0) {
      const lib = trajetsLegs.length === 2 ? `car aller + retour ${prixTrajet} € × 2` : `car ${trajetsLegs[0] === 'aller' ? 'aller' : 'retour'} ${prixTrajet} €`;
      Object.keys(result).forEach((key) => {
        const f = result[key];
        if (!f || typeof f.forfait !== 'number') return;
        result[key] = { ...f, forfait: f.forfait + supplementTrajets, detail: `${f.detail} + ${supplementTrajets} € (${lib})` };
      });
    }
    // Exposer les chambres optimisées (= toutes celles où le supp ind s'applique :
    // chambres-1-occupant + optimisations 2pers adulte+bébé) pour le useEffect
    // detectChambresIndividuelles → marquage UI cohérent.
    result._optimizedRooms = r.optimizedRoomIndices || [];
    // Ajouter ageText à chaque entrée (affichage inline dans chaque voyageur)
    Object.keys(result).forEach((key) => {
      if (key === '_optimizedRooms') return;
      const vIdx = parseInt(key, 10);
      if (Number.isNaN(vIdx)) return;
      const v = form.voyageurs[vIdx];
      if (!v) return;
      const age = ageAuDepartNum(v.dateNaissance);
      if (age === null) {
        result[vIdx].ageText = '';
      } else if (age === 0) {
        result[vIdx].ageText = '< 1 an';
      } else {
        result[vIdx].ageText = `${age} an${age > 1 ? 's' : ''}`;
      }
    });
    return result;
  };

  // Iter 748 — forfaits des semaines suivantes (hôtel de la semaine + retour en car sur la dernière uniquement)
  const semainesSupp = (() => {
    if (!isLloretTrip || nbSemainesEff < 2) return [];
    return semainesSuite.slice(0, nbSemainesEff - 1).map((d, i) => {
      const r = computePricing({ destination: d, voyageurs: form.voyageurs, chambres: form.chambres, chambreIndOverrides });
      const derniere = i === nbSemainesEff - 2;
      // Iter 774 (Kamel) — la dernière semaine peut être SANS car (ex. 28 août → 4 sept.) : pas de retour en car possible
      const sansCar = !(d.transport_option && typeof d.transport_option === 'object' && d.transport_option.actif) || !dispoMap[d.id]?.trajets;
      const retourPlein = dispoMap[d.id]?.trajets?.retour?.restantes === 0;  // car retour complet sur la dernière semaine
      const legs = derniere && !retourPlein && !sansCar ? trajetsLegs.filter(l => l === 'retour') : [];
      const retourIndispo = derniere && (retourPlein || sansCar) && trajetsLegs.includes('retour');
      const retourMotif = sansCar ? 'pas de car organisé cette semaine' : 'car retour COMPLET cette semaine';
      const pt = d.transport_option?.actif ? (parseInt(d.transport_option.prix_trajet, 10) || 0) : 0;
      const forfaits = {};
      Object.keys(r.forfaits || {}).forEach(k => {
        const f = r.forfaits[k]; if (!f || typeof f.forfait !== 'number') return;
        forfaits[k] = f.forfait + legs.length * pt;
      });
      const nuits = d.dateDepart && d.dateFin ? Math.round((new Date(d.dateFin) - new Date(d.dateDepart)) / 86400000) : 7;
      const piscine = (form.chambres || []).filter(c => c.vuePiscine && (c.occupants || []).length > 0).length * (nuits <= 3 ? 40 : 80);
      // Iter 777 — le supplément chambre individuelle est dû CHAQUE semaine (le serveur le recopie sur chaque fiche)
      const suppCh = form.supplementChambre ? (parseFloat(form.montantSupplementChambre) || 0) : 0;
      const nbVoy = Object.keys(forfaits).filter(k => form.voyageurs[k]?.nom?.trim()).length;
      // Iter 778 (Kamel) — l'assurance est PAR PERSONNE ET PAR SEMAINE (tarif de la semaine, sinon celui de la 1re)
      const assUnit = form.assurance === 'oui' ? (parseFloat(d.assurance) || parseFloat(form.montantAssurance) || 0) : 0;
      const assurance = assUnit * nbVoy;
      const horsAss = Object.keys(forfaits).filter(k => form.voyageurs[k]?.nom?.trim()).reduce((a, k) => a + forfaits[k], 0) + piscine + suppCh;
      const total = horsAss + assurance;
      return { dest: d, forfaits, legs, prixTrajet: pt, piscine, suppCh, assUnit, assurance, horsAss, total, busTrajets: legs.length ? 'retour_seul' : 'aucun', retourIndispo, retourMotif, sansCar };
    });
  })();
  const totalSemainesSupp = semainesSupp.reduce((a, w) => a + w.total, 0);
  // Iter 778 — acompte par semaine (chaque fiche hebdo est indépendante en gestion) : % sur forfaits + suppléments, assurance due à 100 %
  const acompteSemaine = (w, pct) => Math.ceil(w.horsAss * pct) + w.assurance;

  // Détection DDN manquante sur les compagnons : obsolète (iter 73) — la DDN a été retirée pour les personnes d'un autre formulaire.
  const lloretCompagnonsMissingDDN = () => [];

  // Blocage solo — RETIRÉ sur demande utilisateur (2026-02) : cas rare, géré manuellement par l'association.
  const soloBlockerActive = false;

  // Auto-applique les forfaits Lloret calculés sur les voyageurs (uniquement si la valeur change)
  useEffect(() => {
    if (!isLloretTrip) return;
    const computed = computeLloretForfaits();
    let changed = false;
    const nextVoyageurs = form.voyageurs.map((v, idx) => {
      if (!v.nom?.trim()) return v;
      const target = computed[idx];
      if (!target) return v;
      if (String(v.forfait || '') !== String(target.forfait)) {
        changed = true;
        return { ...v, forfait: String(target.forfait) };
      }
      return v;
    });
    if (changed) setForm((p) => ({ ...p, voyageurs: nextVoyageurs }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLloretTrip,
    selectedDest?.id,
    remiseTrajet,
    supplementTrajets,
    // recalc si DDN du voyageur change
    JSON.stringify(form.voyageurs.map((v) => ({ n: v.nom, d: v.dateNaissance }))),
    // recalc si composition des chambres change
    JSON.stringify((form.chambres || []).map((c) => ({
      o: c.occupants || [],
      c: (c.compagnons || []).map((cc) => ({ n: cc.nom, p: cc.prenom })),
      a: c.autreOccupants || '',
      s: !!c.partageSolo,
    }))),
    // recalc si les overrides admin (tarifs) changent
    JSON.stringify(chambreIndOverrides[selectedDest?.id || ''] || {}),
  ]);

  // Auto-calcul tarif pour les voyages à la journée (Turin, Grau-du-Roi) — iter 73
  // Admin config = source unique : reduction_3_11 renseignée → appliquée ; vide → aucune réduction.
  useEffect(() => {
    if (!selectedDest || hasNuitee) return;
    const baseGlobal = parseInt((selectedDest.price || '').replace(/[^\d]/g, ''), 10) || 0;
    if (baseGlobal <= 0) return;
    const adminCfg = chambreIndOverrides[selectedDest.id] || {};
    const childDisc = (adminCfg.reduction_3_11 !== null && adminCfg.reduction_3_11 !== undefined)
      ? parseFloat(adminCfg.reduction_3_11) : 0;
    let changed = false;
    const nextVoyageurs = form.voyageurs.map((v) => {
      if (!v.nom?.trim()) return v;
      const age = ageAuDepartNum(v.dateNaissance);
      const expected = age !== null && age < 12 ? Math.max(0, baseGlobal - childDisc) : baseGlobal;
      if (String(v.forfait || '') !== String(expected)) {
        changed = true;
        return { ...v, forfait: String(expected) };
      }
      return v;
    });
    if (changed) setForm((p) => ({ ...p, voyageurs: nextVoyageurs }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasNuitee,
    selectedDest?.id,
    JSON.stringify(form.voyageurs.map((v) => ({ n: v.nom, d: v.dateNaissance }))),
    JSON.stringify(chambreIndOverrides[selectedDest?.id || ''] || {}),
  ]);

  // Nombre max de voyageurs autorisé = min(6, places restantes) pour la destination sélectionnée
  const maxVoyageurs = (() => {
    if (!form.destination) return 6;
    let spots = spotsMap[form.destination];
    if (trajetsSepares && trajetsLegs.length) {  // Iter 688 / 691 : compteur de chaque trajet choisi (-1 = car pas encore saisi = pas de limite)
      const r = trajetsLegs.map(l => dispoSel.trajets[l].restantes).filter(x => x !== -1 && x !== undefined);
      spots = r.length ? Math.min(...r) : -1;
    } else if (isFusion && trajetsLegs.length === 0) {
      spots = sansRest === undefined ? -1 : sansRest;  // compteur « sans transport »
    }
    if (spots === undefined || spots === -1) return 6; // places non renseignées => pas de limite
    return Math.min(6, Math.max(1, spots));
  })();

  // Si le nombre de voyageurs dépasse la limite (ex: changement de destination),
  // on tronque automatiquement le tableau à la limite max.
  useEffect(() => {
    if (form.voyageurs.length > maxVoyageurs) {
      setForm(p => ({ ...p, voyageurs: p.voyageurs.slice(0, maxVoyageurs) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxVoyageurs]);


  // Popup "Je voyage seul(e)" — RETIRÉ sur demande utilisateur (2026-02).
  // L'association gère le cas solo manuellement lors des échanges préalables.

  // ============================================================
  // DÉTECTION "INSCRIPTION MINEUR(S) SEUL(S)" (Iter 125)
  // Si toutes les DDN sont remplies et qu'aucun voyageur n'est >= 18 ans
  // à la date de départ → bascule automatique en mode représentant légal :
  //  - rempliPar = 'tiers'
  //  - lien restreint à parent / tuteur-legal
  //  - case "autorisationParentale" obligatoire
  // ============================================================
  const isMinorOnly = (() => {
    const voys = (form.voyageurs || []).filter(v => v.nom?.trim());
    if (voys.length === 0) return false;
    if (!voys.every(v => v.dateNaissance?.trim())) return false; // tant que toutes les DDN ne sont pas saisies, on n'active pas le mode
    return !voys.some(v => {
      const age = ageAuDepartNum(v.dateNaissance);
      return age !== null && age >= 18;
    });
  })();

  // Auto-force rempliPar='tiers' quand on est en mode mineurs seuls
  useEffect(() => {
    if (isMinorOnly && form.rempliPar !== 'tiers') {
      setForm(p => ({ ...p, rempliPar: 'tiers' }));
    }
    // Si on était en mode mineur et qu'une DDN d'adulte est ajoutée, on réinitialise l'autorisation
    if (!isMinorOnly && form.autorisationParentale) {
      setForm(p => ({ ...p, autorisationParentale: false }));
    }
    // Si lien actuel n'est pas autorisé en mode mineurs seuls → reset
    if (isMinorOnly && form.rempliParTiersLien && !['parent', 'tuteur-legal'].includes(form.rempliParTiersLien)) {
      setForm(p => ({ ...p, rempliParTiersLien: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMinorOnly]);

  // Auto-assignation : si le voyageur a coché "Supplément chambre individuelle" (option 3)
  // et qu'il voyage seul, on le place automatiquement dans la 1ère chambre (type individuelle)
  // pour éviter qu'il oublie cette étape et que le type tombe à "Non renseigné".
  useEffect(() => {
    if (!form.supplementChambre || !hasNuitee) return;
    const voyageursActifs = (form.voyageurs || []).filter(v => v.nom?.trim());
    if (voyageursActifs.length !== 1) return;
    const chambres = form.chambres || [];
    const ch0 = chambres[0] || {};
    const alreadyAssigned = (ch0.occupants || []).includes(0);
    if (alreadyAssigned && ch0.typeChambre === 'individuelle') return;
    // Composition déjà saisie (2e personne pas encore nommée, devis prérempli, 2e chambre) → on ne touche pas aux chambres
    if ((ch0.occupants || []).length > 1 || chambres.some((c, i) => i > 0 && (c.occupants || []).length)) return;
    setForm(prev => {
      const ch = [...(prev.chambres || [])];
      ch[0] = {
        ...(ch[0] || {}),
        occupants: [0],
        compagnons: ch[0]?.compagnons || [],
        typeChambre: 'individuelle',
        partageSolo: false, // on est en chambre individuelle → pas de recherche de binôme
      };
      return { ...prev, chambres: ch };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.supplementChambre, form.voyageurs?.[0]?.nom, hasNuitee]);


  // ========================================================================
  // DÉTECTION AUTO DES CHAMBRES INDIVIDUELLES (2026-02, iter 67)
  // ========================================================================
  // Toute chambre avec 1 seul voyageur du formulaire et AUCUN compagnon (ni
  // partage solo, ni nom d'un autre formulaire) est considérée comme "chambre
  // individuelle" → le supplément s'applique automatiquement, même si le
  // formulaire contient plusieurs voyageurs répartis sur plusieurs chambres.
  // ========================================================================
  const detectChambresIndividuelles = () => {
    if (!hasNuitee) return [];
    const chambres = form.chambres || [];
    const seules = [];
    // Chambres "optimisées" par la règle 2 pers adulte+bébé (iter 70) — comptent comme chambres individuelles
    let optimizedSet = new Set();
    if (isLloretTrip) {
      const computed = computeLloretForfaits();
      if (Array.isArray(computed._optimizedRooms)) {
        optimizedSet = new Set(computed._optimizedRooms);
      }
    }
    chambres.forEach((ch, ci) => {
      const occ = (ch.occupants || []).length;
      const compagnonsValid = (ch.compagnons || []).filter(c => (c.nom || '').trim() && (c.prenom || '').trim()).length;
      const autresCount = (ch.autreOccupants || '').trim()
        ? (ch.autreOccupants || '').split(',').filter(n => n.trim()).length
        : 0;
      const partageSoloCount = ch.partageSolo ? 1 : 0;
      const totalOcc = occ + compagnonsValid + autresCount + partageSoloCount;
      if (occ === 1 && totalOcc === 1) {
        seules.push({ chIdx: ci, voyageurIdx: ch.occupants[0], reason: 'seul' });
      } else if (optimizedSet.has(ci)) {
        // Chambre 2 pers optimisée (adulte + bébé) → supplément chambre ind. appliqué
        seules.push({ chIdx: ci, voyageurIdx: (ch.occupants || [])[0], reason: 'optimized' });
      }
    });
    return seules;
  };

  const chambreIndInfo = (() => {
    const staticInfo = selectedDest ? getChambreIndividuelleInfo(selectedDest.id, selectedDest.group, DESTINATIONS) : { available: false, amount: 0 };
    const override = chambreIndOverrides[selectedDest?.id] || {};
    // Iter 74cinq : override par semaine (Lloret été/printemps) — si la semaine a sa propre config, elle prime.
    let weeklyOverride = {};
    if (selectedDest && override.chambre_ind_weekly && typeof override.chambre_ind_weekly === 'object') {
      // ITER 91 — fix bug récurrent (~7 fois) :
      // La BDD stocke les semaines avec leurs suffixes complets (`14mai-sans`,
      // `14mai-avec`, `18juil-avec`, etc.) car chaque variante transport est
      // un voyage distinct côté admin. Le frontend cherchait `14mai` court →
      // mismatch → fallback sur le `disabled=true` global hérité de l'ancienne
      // config → supplément désactivé à tort.
      // Désormais : on essaie la clé COMPLÈTE en premier (`14mai-sans`), puis
      // la clé courte (`14mai`) pour la rétrocompatibilité avec les anciennes
      // configurations admin.
      let fullKey = '';
      let shortKey = '';
      if (selectedDest.id.startsWith('lloret-8j-')) {
        fullKey = selectedDest.id.replace('lloret-8j-', '');
        shortKey = fullKey.replace(/-sans$|-avec$/, '');
      } else if (selectedDest.id.startsWith('lloret-4j-')) {
        fullKey = selectedDest.id.replace('lloret-4j-', '');
        shortKey = fullKey.replace(/-sans$|-avec$/, '');
      }
      if (fullKey && override.chambre_ind_weekly[fullKey]) {
        weeklyOverride = override.chambre_ind_weekly[fullKey] || {};
      } else if (shortKey && override.chambre_ind_weekly[shortKey]) {
        weeklyOverride = override.chambre_ind_weekly[shortKey] || {};
      }
    }
    // Priorité : weeklyOverride > override global > staticInfo
    // (iter 77) `disabled` respecte cette priorité : si la semaine a une valeur explicite,
    // elle prime, sinon on regarde le global, sinon false.
    //
    // ITER 92 — fix bug Lloret 8j : si la semaine a un `montant > 0` explicitement
    // configuré côté admin (même sans champ `disabled`), c'est une intention
    // d'activer la chambre individuelle pour cette semaine. On ne doit donc PAS
    // retomber sur le `disabled=true` global hérité d'une ancienne config.
    // Lloret 4j (BDD) : weekly[14mai-sans] = {montant: 100, disabled: False}
    //                   → weeklyOverride.disabled !== undefined → priorité 1 → OK
    // Lloret 8j (BDD) : weekly[18juil-avec] = {montant: 220}
    //                   → weeklyOverride.disabled === undefined → tombait sur global=true ❌
    // Avec ce fix, weeklyMontant > 0 force disabledByAdmin = false (intent admin clair).
    const hasValidMontant = (v) => v !== undefined && v !== null && v !== '' && Number(v) > 0;
    let disabledByAdmin;
    if (weeklyOverride.disabled !== undefined) disabledByAdmin = weeklyOverride.disabled === true;
    else if (hasValidMontant(weeklyOverride.montant)) disabledByAdmin = false;
    else if (override.disabled !== undefined) disabledByAdmin = override.disabled === true;
    else disabledByAdmin = false;
    // Iter 77 — fix chambre ind Espagne : un montant admin à 0 (saisie "0" ou reliquat
    // ancienne migration) ne doit PAS annuler le supplément. Seul un montant > 0 est
    // considéré comme un override admin volontaire. Sinon on tombe sur staticInfo
    // (100 € Lloret 4j, 220 € Lloret 8j, 490 € Thaïlande, 590 € NY, 250 € Istanbul, ...).
    let montant;
    if (hasValidMontant(weeklyOverride.montant)) montant = Number(weeklyOverride.montant);
    else if (hasValidMontant(override.montant)) montant = Number(override.montant);
    else montant = Number(staticInfo.amount ?? 0);
    const adminProvidedMontant = hasValidMontant(weeklyOverride.montant) || hasValidMontant(override.montant);
    // available = vrai si admin a mis un montant > 0 OU si staticInfo.available && pas désactivé
    const available = !disabledByAdmin && (adminProvidedMontant || staticInfo.available);
    // nonDisponibleStatic = vrai seulement si ni staticInfo ni admin ne propose la chambre ind.
    const nonDisponibleStatic = selectedDest && hasNuitee && !available && !disabledByAdmin;
    return { available, montant, disabledByAdmin, nonDisponibleStatic };
  })();

  const chambresIndividuellesDetectees = detectChambresIndividuelles();
  const totalSupplementAuto = chambresIndividuellesDetectees.length * (chambreIndInfo.montant || 0);

  // Synchronisation auto (iter 67 → iter 75bis) : dès qu'une ou plusieurs
  // chambres individuelles sont détectées, on applique/met à jour le supplément.
  // Si plus aucune chambre seule OU admin désactivé → on RETIRE le supplément.
  useEffect(() => {
    if (!hasNuitee) return;
    const n = chambresIndividuellesDetectees.length;
    // Cas : admin a désactivé OU plus aucune chambre seule → retirer le supplément.
    if (chambreIndInfo.disabledByAdmin || n === 0) {
      if (form.supplementChambre || (form.montantSupplementChambre && String(form.montantSupplementChambre) !== '0')) {
        setForm(prev => ({ ...prev, supplementChambre: false, montantSupplementChambre: '' }));
      }
      return;
    }
    if (!chambreIndInfo.available || chambreIndInfo.montant <= 0) return;
    const expectedMontant = totalSupplementAuto;
    if (form.supplementChambre === true && String(form.montantSupplementChambre) === String(expectedMontant)) return;
    setForm(prev => ({
      ...prev,
      supplementChambre: true,
      montantSupplementChambre: String(expectedMontant),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    JSON.stringify(form.chambres),
    chambreIndInfo.available,
    chambreIndInfo.montant,
    chambreIndInfo.disabledByAdmin,
    hasNuitee,
    totalSupplementAuto,
  ]);


  // Détection des avoirs actifs : appelé quand l'utilisateur passe en mode "avoir"
  // et qu'il a saisi un email OU le nom+prénom du 1er voyageur.
  useEffect(() => {
    if (!(form.modesReglement || []).includes('avoir')) {
      setAvailableAvoirs([]);
      return;
    }
    const email = (form.email || '').trim().toLowerCase();
    const nom = (form.voyageurs?.[0]?.nom || '').trim().toUpperCase();
    const prenom = (form.voyageurs?.[0]?.prenom || '').trim().toUpperCase();
    if (!email && !(nom && prenom)) return;
    const params = new URLSearchParams();
    if (email) params.set('email', email);
    if (nom) params.set('nom', nom);
    if (prenom) params.set('prenom', prenom);
    const controller = new AbortController();
    fetch(`${API}/api/avoirs/available?${params.toString()}`, { signal: controller.signal })
      .then(r => r.ok ? r.json() : [])
      .then(data => setAvailableAvoirs(Array.isArray(data) ? data : []))
      .catch(() => {});
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(form.modesReglement), form.email, form.voyageurs?.[0]?.nom, form.voyageurs?.[0]?.prenom]);


  // Iter 825 (Kamel) — journée : question « même adresse ? » avant chaque ajout (jamais préremplie, reposée à chaque clic)
  const [questionAdresse, setQuestionAdresse] = useState(false);
  const [lienSepare, setLienSepare] = useState('');
  const ouvrirFormulaireSepare = () => {
    const url = `/inscription?destination=${encodeURIComponent(form.destination || '')}&separe=1`;
    const w = window.open(url, '_blank'); // (pas de 'noopener' en 3e argument : il force un retour null → faux « bloqué »)
    if (w) { try { w.opener = null; } catch { /* noop */ } }
    setQuestionAdresse(false);
    if (!w) setLienSepare(url); // popup réellement bloquée → lien de secours, le formulaire en cours n'est jamais remplacé
  };
  const addVoyageur = () => {
    if (form.voyageurs.length < maxVoyageurs) {
      setForm(p => ({ ...p, voyageurs: [...p.voyageurs, { nom: '', prenom: '', dateNaissance: '', forfait: '' }] }));
    }
  };

  const removeVoyageur = (idx) => {
    if (form.voyageurs.length > 1 && idx > 0) {
      setForm(p => ({ ...p, voyageurs: p.voyageurs.filter((_, i) => i !== idx) }));
    }
  };

  // Formate automatiquement une saisie JJMMAAAA en JJ/MM/AAAA au fil de la frappe.
  // Supprime tout caractère non-numérique et insère les slashs après 2 et 4 chiffres.
  const formatDateInput = (value) => {
    const digits = (value || '').replace(/\D/g, '').slice(0, 8);
    if (digits.length < 3) return digits;
    if (digits.length < 5) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  };

  // ========================================================================
  // HELPERS LLORET (iter 68) — inscription par chambre avec voyageurs inline
  // ========================================================================
  const MAX_CHAMBRES_LLORET = 3;

  // Ajoute une nouvelle personne dans la chambre `chIdx`. Si le 1er voyageur (index 0)
  // est encore vide ET non rattaché à aucune chambre (cas de l'init du formulaire),
  // on le réutilise plutôt que d'en créer un nouveau (évite la désynchro voyageur[0]).
  const lloretAddPersonToChambre = (chIdx) => {
    setForm(p => {
      const chambres = Array.isArray(p.chambres) ? [...p.chambres] : [];
      const ch = chambres[chIdx] || { occupants: [], compagnons: [], autreOccupants: '', typeChambre: '', partageSolo: false };
      const maxPers = isLloretOnly ? 4 : 3;
      if ((ch.occupants || []).length >= maxPers) return p;
      // Trouver un voyageur vide non-assigné (priorité au 0)
      const assignedSet = new Set();
      chambres.forEach(c => (c.occupants || []).forEach(o => assignedSet.add(o)));
      const emptyIdx = (p.voyageurs || []).findIndex((v, i) =>
        !assignedSet.has(i) &&
        !((v.nom || '').trim() || (v.prenom || '').trim() || (v.dateNaissance || '').trim())
      );
      let newVoyageurs = p.voyageurs || [];
      let targetIdx;
      if (emptyIdx >= 0) {
        targetIdx = emptyIdx;
      } else {
        const newVoy = { nom: '', prenom: '', dateNaissance: '', civilite: '', telephone: '', forfait: '' };
        newVoyageurs = [...newVoyageurs, newVoy];
        targetIdx = newVoyageurs.length - 1;
      }
      // Iter 744 — la chambre n'est plus « individuelle » dès qu'une 2e personne arrive : on efface ce type auto pour
      // que la famille choisisse la literie (sinon message « type individuelle incompatible » incompréhensible)
      const typeChambre = ch.typeChambre === 'individuelle' ? '' : ch.typeChambre;
      chambres[chIdx] = { ...ch, occupants: [...(ch.occupants || []), targetIdx], typeChambre };
      const supplOff = chIdx === 0 && p.supplementChambre ? { supplementChambre: false, montantSupplementChambre: '' } : {};
      return { ...p, voyageurs: newVoyageurs, chambres, ...supplOff };
    });
  };

  // Supprime la personne à l'index `voyIdx` dans form.voyageurs : retire du tableau,
  // retire de toutes les chambres qui la référençaient, puis re-mappe les autres
  // indices > voyIdx en les décrémentant de 1.
  const lloretRemovePerson = (voyIdx) => {
    setForm(p => {
      if ((p.voyageurs || []).length <= 1) return p; // on garde au moins 1 voyageur
      const newVoyageurs = p.voyageurs.filter((_, i) => i !== voyIdx);
      const newChambres = (p.chambres || []).map(ch => ({
        ...ch,
        occupants: (ch.occupants || [])
          .filter(o => o !== voyIdx)
          .map(o => (o > voyIdx ? o - 1 : o)),
      }));
      return { ...p, voyageurs: newVoyageurs, chambres: newChambres };
    });
  };

  // Ajoute une nouvelle chambre vide (limite 3 chambres pour Lloret).
  const lloretAddChambre = () => {
    setForm(p => {
      const chambres = Array.isArray(p.chambres) ? p.chambres : [];
      if (chambres.length >= MAX_CHAMBRES_LLORET) return p;
      return { ...p, chambres: [...chambres, { occupants: [], compagnons: [], autreOccupants: '', typeChambre: '', partageSolo: false }] };
    });
  };

  // Supprime la chambre `chIdx` ET tous les voyageurs qui y étaient attribués (Lloret only).
  const lloretRemoveChambre = (chIdx) => {
    setForm(p => {
      const chambres = Array.isArray(p.chambres) ? p.chambres : [];
      if (chambres.length <= 1) return p; // garder au moins 1 chambre
      const target = chambres[chIdx];
      if (!target) return p;
      const toRemove = new Set(target.occupants || []);
      // Filtrer voyageurs et construire mapping ancien→nouveau
      const mapping = {};
      const newVoyageurs = [];
      p.voyageurs.forEach((v, i) => {
        if (toRemove.has(i)) return;
        mapping[i] = newVoyageurs.length;
        newVoyageurs.push(v);
      });
      // On garde toujours au moins 1 voyageur (s'il n'en reste plus, on ré-insère un vide)
      const finalVoyageurs = newVoyageurs.length > 0 ? newVoyageurs : [{ nom: '', prenom: '', dateNaissance: '', civilite: '', telephone: '', forfait: '' }];
      const newChambres = chambres
        .filter((_, i) => i !== chIdx)
        .map(ch => ({
          ...ch,
          occupants: (ch.occupants || [])
            .filter(o => mapping[o] !== undefined)
            .map(o => mapping[o]),
        }));
      return { ...p, voyageurs: finalVoyageurs, chambres: newChambres };
    });
  };

  const toggleFormalite = (val) => {
    setForm(p => ({
      ...p,
      formalites: p.formalites.includes(val)
        ? p.formalites.filter(f => f !== val)
        : [...p.formalites, val],
    }));
  };

  // ========== BARRE DE PROGRESSION — Liste exhaustive des points bloquants ==========
  // Contrairement à handleSubmit qui arrête au premier problème, cette fonction renvoie
  // TOUS les problèmes en même temps pour les afficher au visiteur en temps réel.
  const computeValidationIssues = () => {
    if (!selectedDest) return []; // Pas de destination choisie = pas encore d'affichage utile
    const issues = [];
    const voyageursActifs = (form.voyageurs || []).filter(v => v.nom?.trim());

    // Lieu de départ autocar
    if (showBusDepart && !form.lieuDepart) {
      issues.push({ id: 'lieu-depart', section: 'Lieu de départ', msg: 'Choisir votre lieu de départ en autocar.', anchor: 'section-depart' });
    }
    if (form.lieuDepart === 'autoroute' && !form.aireAutorouteCode) {
      issues.push({ id: 'aire-autoroute-code', section: 'Lieu de départ', msg: `Choisir l'autoroute de votre aire (${autoroutesOk.join(', ')}) — uniquement sur le trajet du car.`, anchor: 'select-aire-autoroute' });
    }
    if (form.lieuDepart === 'autoroute' && form.aireAutoroute.trim().length < 3) {
      issues.push({ id: 'aire-autoroute', section: 'Lieu de départ', msg: "Préciser le nom de l'aire d'autoroute (ex. Aire de Montélimar).", anchor: 'input-aire-autoroute' });
    }

    // Voyageurs
    const voyageursAnchor = isLloretTrip ? 'section-lloret-chambres' : 'section-voyageurs';
    if (!form.contactSource || !form.contactPrenom?.trim() || !form.contactNom?.trim()) issues.push({ id: 'coord-contact', section: 'Coordonnées', msg: 'PERSONNE CONTACT pour ce formulaire manquante : choisissez-la dans la liste.', anchor: 'select-contact-formulaire' });
    if (!form.telephone?.trim()) issues.push({ id: 'coord-tel', section: 'Coordonnées', msg: 'TÉLÉPHONE de la famille manquant (section Adresse postale).', anchor: 'input-telephone' });
    // Iter 862 (Kamel) — tout ce qui bloquait seulement par message « en bas » passe dans l'écran de contrôle
    if (isLloretEteCar && trajetsLegs.length === 0) issues.push({ id: 'trajets-aucun', section: 'Transport', msg: 'Choisir au moins un trajet en car (Aller et/ou Retour), ou prendre la formule « Sans transport » de cette semaine.', anchor: 'trajets-car-bloc' });
    if (isFusion && sejourIsole && trajetsLegs.length === 1) issues.push({ id: 'trajets-aller-retour', section: 'Transport', msg: 'Pour ce séjour, le car se prend en aller + retour : cocher les deux trajets ou choisir la formule « Sans transport ».', anchor: 'trajets-car-bloc' });
    if (chambresManquent) issues.push({ id: 'hotel-complet', section: 'Chambres', msg: hotelRestCh > 0 ? `L'hôtel n'a plus que ${hotelRestCh} chambre${hotelRestCh > 1 ? 's' : ''} disponible${hotelRestCh > 1 ? 's' : ''} et votre demande en compte ${nbChambresDemandees} : regrouper les voyageurs ou s'inscrire en liste d'attente.` : "L'hôtel est complet sur une des semaines choisies : inscrivez-vous en liste d'attente.", anchor: 'section-lloret-chambres' });
    if (form.supplementChambre && !(parseFloat(form.montantSupplementChambre) > 0)) issues.push({ id: 'supplement-chambre', section: 'Chambres', msg: 'Supplément chambre individuelle : indiquer le montant (visible sur la page du voyage).', anchor: 'input-supplement-chambre' });
    if (voyageursActifs.length === 0) {
      issues.push({ id: 'no-voyageur', section: 'Voyageurs', msg: 'Renseigner au moins un voyageur (nom, prénom, date de naissance, téléphone).', anchor: voyageursAnchor });
    } else {
      const v0 = form.voyageurs[0] || {};
      const v0Started = v0.nom?.trim() || v0.prenom?.trim() || v0.dateNaissance?.trim();
      if (v0Started) {
        if (!v0.nom?.trim()) issues.push({ id: 'voy-0-nom', section: 'Voyageurs', msg: 'NOM du 1ᵉʳ voyageur manquant.', anchor: 'voyageur-0-nom' });
        if (!v0.prenom?.trim()) issues.push({ id: 'voy-0-prenom', section: 'Voyageurs', msg: 'PRÉNOM du 1ᵉʳ voyageur manquant.', anchor: 'voyageur-0-prenom' });
        if (!v0.dateNaissance?.trim()) issues.push({ id: 'voy-0-ddn', section: 'Voyageurs', msg: 'DATE DE NAISSANCE du 1ᵉʳ voyageur manquante.', anchor: 'voyageur-0-ddn' });
        if (isLloretTrip && !v0.civilite) issues.push({ id: 'voy-0-civilite', section: 'Voyageurs', msg: 'SEXE du 1ᵉʳ voyageur manquant.', anchor: 'voyageur-0-civilite' });
      }
      form.voyageurs.forEach((v, i) => {
        if (i === 0) return;
        // Iter 77bis : un voyageur peut être ajouté SANS fields si l'utilisateur a
        // cliqué "+ Ajouter une personne dans cette chambre" puis n'a rien rempli.
        // Dans ce cas il est rattaché à la chambre (`form.chambres[ci].occupants` contient i)
        // mais ses fields sont tous vides → il serait ignoré par voyageursActifs.
        // On exige qu'il soit complètement rempli s'il est ASSIGNÉ à une chambre
        // OU s'il a au moins un champ commencé. Sinon, bloquer l'envoi.
        const isStarted = v.nom?.trim() || v.prenom?.trim() || v.dateNaissance?.trim();
        const isAssignedToChambre = (form.chambres || []).some(ch => (ch.occupants || []).includes(i));
        if (isStarted || isAssignedToChambre) {
          const hintRemove = isAssignedToChambre
            ? ` — ou cliquez "✕ Retirer" pour enlever cette personne de la chambre.`
            : '';
          if (!v.nom?.trim()) issues.push({ id: `voy-${i}-nom`, section: 'Voyageurs', msg: `NOM du voyageur ${i + 1} manquant${hintRemove}`, anchor: `voyageur-${i}-nom` });
          if (!v.prenom?.trim()) issues.push({ id: `voy-${i}-prenom`, section: 'Voyageurs', msg: `PRÉNOM du voyageur ${i + 1} manquant${hintRemove}`, anchor: `voyageur-${i}-prenom` });
          if (!v.dateNaissance?.trim()) issues.push({ id: `voy-${i}-ddn`, section: 'Voyageurs', msg: `DATE DE NAISSANCE du voyageur ${i + 1} manquante${hintRemove}`, anchor: `voyageur-${i}-ddn` });
          else {
            // Iter 103m - Validation stricte du format + valeurs fantaisistes
            const chk = validateFrenchBirthDate(v.dateNaissance, { minYear: 1900 });
            if (!chk.valid) issues.push({ id: `voy-${i}-ddn-format`, section: 'Voyageurs', msg: `DATE DE NAISSANCE du voyageur ${i + 1} : ${chk.reason}. Format attendu : JJ/MM/AAAA (ex: 15/03/1985)${hintRemove}`, anchor: `voyageur-${i}-ddn` });
          }
          if (isLloretTrip && !v.civilite) issues.push({ id: `voy-${i}-civilite`, section: 'Voyageurs', msg: `SEXE du voyageur ${i + 1} manquant${hintRemove}`, anchor: `voyageur-${i}-civilite` });
        }
      });
      const hasMajeur = voyageursActifs.some((v) => {
        const age = ageAuDepartNum(v.dateNaissance);
        return age !== null && age >= 18;
      });
      const toutesDDNRemplies = voyageursActifs.every(v => v.dateNaissance?.trim());
      // Iter 125 : si inscription mineur(s) seul(s), exiger l'autorisation parentale au lieu de bloquer.
      if (toutesDDNRemplies && !hasMajeur) {
        if (!form.autorisationParentale) {
          issues.push({ id: 'autorisation-parentale', section: 'Conditions', msg: "Inscription d'un/des mineur(s) seul(s) : l'autorisation parentale (case à cocher) est obligatoire.", anchor: 'autorisation-parentale-block' });
        }
        if (form.rempliPar !== 'tiers' || !form.rempliParTiersNom?.trim() || !form.rempliParTiersPrenom?.trim() || !['parent', 'tuteur-legal'].includes(form.rempliParTiersLien)) {
          issues.push({ id: 'responsable-legal', section: 'Conditions', msg: "Inscription d'un/des mineur(s) seul(s) : merci de renseigner le nom, prénom et qualité (parent ou tuteur légal) du représentant légal.", anchor: 'autorisation-parentale-block' });
        }
      }
      // Vérification tarif : pour Lloret, le tarif est auto — on ne bloque pas là-dessus
      if (!isLloretTrip) {
        voyageursActifs.forEach((v, i) => {
          const tarif = parseFloat(v.forfait) || 0;
          if (tarif <= 0) {
            issues.push({ id: `voy-${i}-forfait`, section: 'Voyageurs', msg: `Tarif du voyageur ${v.nom} ${v.prenom || ''} manquant ou à 0.`, anchor: `voyageur-${i}-forfait` });
          }
        });
      }
      // Lloret : chaque voyageur DOIT être rattaché à une chambre
      if (isLloretTrip) {
        const assignedSet = new Set();
        (form.chambres || []).forEach(ch => (ch.occupants || []).forEach(o => assignedSet.add(o)));
        voyageursActifs.forEach((v, i) => {
          const vIdx = form.voyageurs.indexOf(v);
          if (vIdx >= 0 && !assignedSet.has(vIdx)) {
            issues.push({ id: `voy-${i}-unassigned`, section: 'Chambres', msg: `${v.prenom || ''} ${v.nom || 'Voyageur'} n'est rattaché(e) à aucune chambre.`, anchor: 'section-lloret-chambres' });
          }
        });
        // Compagnons (personnes d'autres formulaires) : nom + prénom obligatoires, DDN facultative.
        // Sans DDN : tarif auto = plein tarif 12 ans et + (pas de réduction possible).
        (form.chambres || []).forEach((ch, ci) => {
          (ch.compagnons || []).forEach((c, cj) => {
            if ((c.nom || '').trim() || (c.prenom || '').trim()) {
              if (!(c.nom || '').trim()) issues.push({ id: `comp-${ci}-${cj}-nom`, section: 'Chambres', msg: `Chambre ${ci + 1}, personne ${cj + 1} : NOM manquant.`, anchor: `lloret-compagnon-${ci}-${cj}-nom` });
              if (!(c.prenom || '').trim()) issues.push({ id: `comp-${ci}-${cj}-prenom`, section: 'Chambres', msg: `Chambre ${ci + 1}, personne ${cj + 1} : PRÉNOM manquant.`, anchor: `lloret-compagnon-${ci}-${cj}-prenom` });
            } else {
              // Slot compagnon totalement vide → l'utilisateur a cliqué "+ Ajouter une personne d'un autre formulaire"
              // puis n'a rien rempli. On exige le remplissage OU le retrait.
              issues.push({
                id: `comp-${ci}-${cj}-vide`,
                section: 'Chambres',
                msg: `Chambre ${ci + 1} : une « personne d'un autre formulaire » a été ajoutée sans informations. Renseignez son NOM + PRÉNOM ou cliquez sur "✕ Retirer" pour l'enlever.`,
                anchor: `lloret-compagnon-${ci}-${cj}-nom`,
              });
            }
          });
        });
        // Iter 77bis : chambre totalement vide (l'utilisateur a cliqué "+ Ajouter une chambre"
        // puis n'a rien mis dedans) → blocage avec message explicite.
        (form.chambres || []).forEach((ch, ci) => {
          const occs = ch.occupants || [];
          const compsFilled = (ch.compagnons || []).filter(c => (c.nom || '').trim() || (c.prenom || '').trim()).length;
          const autre = (ch.autreOccupants || '').trim();
          const partageSolo = !!ch.partageSolo;
          const totalOcc = occs.length + compsFilled + (autre ? 1 : 0) + (partageSolo ? 1 : 0);
          if (totalOcc === 0 && (form.chambres || []).length > 1) {
            issues.push({
              id: `chambre-${ci}-vide`,
              section: 'Chambres',
              msg: `Chambre ${ci + 1} n'a aucun occupant. Ajoutez une personne ou cliquez "✕ Retirer la chambre".`,
              anchor: `lloret-chambre-${ci}`,
            });
          }
        });
        // Règle "chambre 4 personnes" (iter 71) : dans une chambre 4 personnes, au moins UNE
        // doit être enfant (3-11 ans) ou bébé (< 3 ans). Sinon = 4 pleins tarifs, interdit
        // (tarif familial 3e/4e lit non applicable).
        //   - 2 adultes + 1 ado + 1 enfant (ou bébé) = OK (3 pleins + 1 réduit)
        //   - 3 adultes + 1 enfant/bébé = OK (3 pleins + 1 réduit)
        //   - 4 adultes/ados = REFUSÉ
        (form.chambres || []).forEach((ch, ci) => {
          const occs = ch.occupants || [];
          const comps = (ch.compagnons || []).filter(c => (c.nom || '').trim() && (c.prenom || '').trim());
          const totalInRoom = occs.length + comps.length;
          if (totalInRoom !== 4) return;
          const peopleCats = [];
          occs.forEach(vi => {
            const v = form.voyageurs[vi];
            if (!v || !v.dateNaissance?.trim()) { peopleCats.push('unknown'); return; }
            peopleCats.push(catFromAge(ageAuDepartNum(v.dateNaissance)));
          });
          comps.forEach(c => {
            if (c.tranche === 'moins12') { peopleCats.push('child'); return; }  // Iter 914 — tranche d'âge déclarée
            const age = ageAuDepartNum(c.dateNaissance || '');
            peopleCats.push(age === null ? 'adult' : catFromAge(age));
          });
          const hasChildOrBaby = peopleCats.some(c => c === 'child' || c === 'baby');
          if (!hasChildOrBaby) {
            issues.push({
              id: `chambre-${ci}-4-pleins-tarifs`,
              section: 'Chambres',
              msg: `Chambre ${ci + 1} : 4 personnes de 12 ans ou plus dans une même chambre n'est pas possible (4 pleins tarifs interdits). Pour une chambre de 4 personnes, au moins une doit être un enfant (3-11 ans) ou un bébé (< 3 ans). Merci de répartir en 2 chambres ou de nous contacter.`,
              anchor: `lloret-chambre-${ci}`,
            });
          }
        });
      }
    }

    // Adresse / email / certification
    if (!form.rue?.trim()) issues.push({ id: 'rue', section: 'Adresse', msg: 'Adresse (rue) manquante.', anchor: 'section-adresse' });
    if (!form.codePostal?.trim()) issues.push({ id: 'cp', section: 'Adresse', msg: 'Code postal manquant.', anchor: 'section-adresse' });
    if (!form.ville?.trim()) issues.push({ id: 'ville', section: 'Adresse', msg: 'Ville manquante.', anchor: 'section-adresse' });
    if (!form.email?.trim()) issues.push({ id: 'email', section: 'Adresse', msg: 'Adresse e-mail manquante.', anchor: 'section-adresse' });
    if (!form.certifieAdresse) issues.push({ id: 'certif-adr', section: 'Adresse', msg: "Certifier que tous les voyageurs habitent à la même adresse (ou sont mineurs sous votre responsabilité).", anchor: 'section-adresse' });

    // Iter 154L — Charte de placement bus : obligatoire si voyage Avec transport
    if (isAvecTransportBus && !form.accepteChartePlacementBus) {
      issues.push({ id: 'charte-bus', section: 'Charte autocar', msg: "Lire et accepter la Charte de placement en autocar (case à cocher en bas du formulaire, juste avant le bouton Envoyer).", anchor: 'charte-bus-block' });
    }

    // Adhésion / assurance / paiement — non concernés sur les voyages à la journée (iter 326)
    if (hasNuitee && !form.adhesion) issues.push({ id: 'adhesion', section: 'Adhésion', msg: "Indiquer votre situation d'adhésion.", anchor: 'section-adhesion' });
    if (hasNuitee && !form.assurance) issues.push({ id: 'assurance', section: 'Assurance', msg: "Indiquer votre choix concernant l'assurance multirisque.", anchor: 'section-assurance' });
    if (hasNuitee && !form.paiement) issues.push({ id: 'paiement', section: 'Paiement', msg: 'Choisir une option de paiement (acompte / totalité).', anchor: 'section-calcul' });
    // Iter 103e/g - Validations "avoir" et "canalReglement" SUPPRIMÉES de la check-list (sections retirées
    // du formulaire public). Le voyageur indique son mode/canal de règlement directement sur la page « Régler ».

    // Chambre : ne pas exiger typeChambre si 1 seule option (auto-sélectionnée côté affichage)
    // Détection du nb d'occupants réel (voyageurs attribués + compagnons valides)
    // Cette logique doit matcher celle de l'UI pour éviter les blocages artificiels.

    // Iter 179 — placement autocar : plus de question au formulaire (les voyageurs
    // choisissent eux-mêmes leurs places via le lien « Mon Bus » après inscription).

    // Chambres (uniquement voyages avec nuitée)
    if (hasNuitee && voyageursActifs.length > 0) {
      const chambres = form.chambres || [];
      const totalAssignes = chambres.reduce((s, c) => s + (c.occupants || []).length, 0);
      if (totalAssignes === 0) {
        issues.push({ id: 'chambre-aucune', section: 'Chambres', msg: 'Répartir les voyageurs dans une ou plusieurs chambres.', anchor: 'chambre-0' });
      } else if (totalAssignes < voyageursActifs.length) {
        issues.push({ id: 'chambre-partiel', section: 'Chambres', msg: `Tous les voyageurs doivent être répartis dans une chambre (${totalAssignes}/${voyageursActifs.length} attribué(s)).`, anchor: 'chambre-0' });
      }
      // BLOCAGE ITER 74ter/quater : si l'admin a coché "plus de chambre individuelle"
      // OU si l'hôtel de ce voyage ne propose simplement pas de chambre ind. (Algérie, Marseille)
      // et que l'utilisateur s'est placé seul dans une chambre → interdire l'envoi.
      if ((chambreIndInfo.disabledByAdmin || chambreIndInfo.nonDisponibleStatic) && chambresIndividuellesDetectees.length > 0) {
        const chambresBloquees = chambresIndividuellesDetectees.map(c => c.chIdx + 1).join(', ');
        const raison = chambreIndInfo.nonDisponibleStatic
          ? "l'hôtel partenaire ne propose pas de chambre individuelle pour ce voyage"
          : "plus aucune chambre individuelle n'est disponible pour ce voyage (décision de l'association)";
        issues.push({
          id: 'chambre-ind-bloquee',
          section: 'Chambres',
          msg: `Chambre ${chambresBloquees} : ${raison}. Merci d'ajouter une personne (même chambre ou « personne d'un autre formulaire ») OU de cocher "Partager ma chambre avec un(e) autre voyageur(se) solo".`,
          anchor: `lloret-chambre-${chambresIndividuellesDetectees[0].chIdx}`,
        });
      }
      chambres.forEach((ch, ci) => {
        (ch.compagnons || []).forEach((c) => {
          if ((c.nom?.trim() || c.prenom?.trim()) && (!c.nom?.trim() || !c.prenom?.trim())) {
            issues.push({ id: `compagnon-${ci}`, section: 'Chambres', msg: `Chambre ${ci + 1} : indiquer nom ET prénom de chaque personne d'un autre formulaire ajoutée manuellement.`, anchor: `chambre-${ci}` });
          }
        });
      });
      chambres.forEach((ch, ci) => {
        const occCount = (ch.occupants || []).length + (ch.compagnons || []).filter(c => c.nom?.trim() && c.prenom?.trim()).length;
        if (occCount > 0 && !ch.typeChambre) {
          // Ne pas alerter si typeChambre sera auto-choisi (1 seule option possible) :
          // 1 occ → individuelle ; 3 occ → 3-lits ; 4 occ → 4-lits
          // Seuls 2 occ (grand-lit vs 2-lits) nécessitent un choix explicite.
          const needsExplicitChoice = occCount === 2;
          if (needsExplicitChoice) {
            issues.push({ id: `type-chambre-${ci}`, section: 'Chambres', msg: `Chambre ${ci + 1} : choisir entre "1 grand lit" ou "2 lits simples".`, anchor: `chambre-${ci}` });
          }
        }
      });
      if (soloBlockerActive) {
        issues.push({ id: 'solo-blocker', section: 'Chambres', msg: "Voyageur(se) solo : choisir une des 3 options (chambre individuelle, partage binôme, ou personne d'un autre formulaire).", anchor: 'warning-solo-oubli' });
      }

      // Blocage chambre individuelle indispo (iter 67)
      if (chambresIndividuellesDetectees.length > 0 && chambreIndInfo.disabledByAdmin) {
        issues.push({
          id: 'chambre-ind-indispo',
          section: 'Chambres',
          msg: "Chambre individuelle non disponible pour ce voyage. Merci de nous contacter (WhatsApp) avant de finaliser : soit prévoir une personne avec qui partager la chambre, soit nous interpeller pour une solution.",
          anchor: 'chambre-ind-indispo-banner',
        });
      }
    }

    // Rempli par un tiers — Iter 862 : champs réels (rempliPar / rempliParTiers*) + ancre sur le bloc « Qui remplit ce formulaire »
    if (form.rempliPar === 'tiers') {
      if (!form.rempliParTiersNom?.trim() || !form.rempliParTiersPrenom?.trim() || !form.rempliParTiersLien?.trim()) {
        issues.push({ id: 'tiers', section: 'Responsable du formulaire', msg: "Vous remplissez ce formulaire pour quelqu'un d'autre : renseigner votre nom, votre prénom et votre lien avec l'inscrit(e).", anchor: 'rempli-par-section' });
      }
    }

    // Conditions générales
    if (!form.cond1 || !form.cond2 || !form.cond3 || !form.cond4 || !form.cond5) {
      const cochees = [1, 2, 3, 4, 5].filter(n => form[`cond${n}`]).length;
      issues.push({ id: 'conditions', section: 'Conditions', msg: `Cocher toutes les conditions générales (${cochees}/5 cochées).`, anchor: 'section-declaration' });
    }
    // Iter 100 — Consentement RGPD obligatoire (case dédiée distincte)
    if (!form.condRgpd) {
      issues.push({ id: 'rgpd', section: 'Conditions', msg: 'Cocher le consentement RGPD (traitement de vos données personnelles).', anchor: 'section-declaration' });
    }

    // Iter 78sex — Confirmation email + téléphone juste avant l'envoi
    // Iter 103k — Détection anti-autofill : si le champ contient plus de caractères
    // que de frappes clavier, c'est un paste/autofill → refus bloquant.
    const emailMain = (form.email || '').trim().toLowerCase();
    const emailConf = (form.emailConfirmation || '').trim().toLowerCase();
    if (!emailConf) {
      issues.push({ id: 'email-conf-missing', section: 'Confirmation', msg: "Re-saisir l'email de confirmation pour vérifier qu'il est correct.", anchor: 'email-confirmation-input' });
    } else if (emailMain && emailConf !== emailMain) {
      issues.push({ id: 'email-conf-mismatch', section: 'Confirmation', msg: "L'email de confirmation diffère de l'email saisi en haut du formulaire — corrigez l'un ou l'autre pour que les deux soient identiques.", anchor: 'email-confirmation-input' });
    }
    const telMain = (form.telephone || '').replace(/\D/g, '');
    const telConf = (form.telephoneConfirmation || '').replace(/\D/g, '');
    if (trajetUniqueOubli && !form.confirmeTrajetUnique) issues.push({ id: 'trajet-unique', section: 'Transport', msg: `Vous n'avez pas choisi de ${trajetManquant} en car : ajoutez-le, ou cochez « Je confirme : je fais le ${trajetManquant} par mes propres moyens ».`, anchor: 'alerte-trajet-unique' });
    if (telCheck.status === 'ko' || (telCheck.status === 'conflit' && !form.telAttestation)) {
      issues.push({ id: 'tel-autre-foyer', section: 'Adresse postale', msg: telCheck.status === 'conflit' ? `Ce numéro est déjà au nom de ${telCheck.autreNom}. Cochez « J'atteste que ce numéro est le mien » ou indiquez votre propre numéro.` : telCheck.message, anchor: 'input-telephone' });
    }
    if (!telConf) {
      issues.push({ id: 'tel-conf-missing', section: 'Confirmation', msg: "Re-saisir votre téléphone pour vérifier qu'il est correct.", anchor: 'telephone-confirmation-input' });
    } else if (telMain && telConf !== telMain) {
      issues.push({ id: 'tel-conf-mismatch', section: 'Confirmation', msg: "Le téléphone de confirmation diffère de celui saisi dans vos coordonnées — corrigez l'un ou l'autre pour que les deux soient identiques.", anchor: 'telephone-confirmation-input' });
    }
    if (!coordsVerifiees) {
      issues.push({ id: 'coords-non-verifiees', section: 'Confirmation', msg: "Cochez « J'ai vérifié mon adresse e-mail et mon numéro de téléphone » après avoir relu vos coordonnées affichées.", anchor: 'coords-verifiees-checkbox' });
    }

    // Places dispo
    const spotsStr = (selectedDest.available_spots ?? -1);
    const spots = parseInt(spotsStr, 10);
    if (!isNaN(spots) && spots >= 0 && voyageursActifs.length > spots) {
      issues.push({ id: 'no-spots', section: 'Destination', msg: 'Il ne reste pas assez de places sur ce voyage pour tous les voyageurs de ce formulaire — réduire le nombre de voyageurs.', anchor: 'section-admin' });
    }

    return issues;
  };

  // Iter 864 — compteur en direct (affiché sous le bouton Envoyer)
  const nbPointsManquants = selectedDest ? computeValidationIssues().length : 0;

  // Iter 862 — encadrement rouge des champs manquants (après 1ʳᵉ tentative), recalculé à chaque saisie
  useEffect(() => {
    champsManquantsRef.current.forEach(el => { el.classList.remove('champ-manquant'); el.removeAttribute('data-manquant'); });
    champsManquantsRef.current = [];
    if (!validationTentee || !selectedDest) return;
    const vus = new Set();
    computeValidationIssues().forEach(it => {
      if (!it.anchor || vus.has(it.anchor)) return;
      vus.add(it.anchor);
      const el = document.querySelector(`[data-testid="${it.anchor}"]`);
      if (el) { el.classList.add('champ-manquant'); el.setAttribute('data-manquant', '1'); champsManquantsRef.current.push(el); }
    });
  });
  // Iter 862 — le message d'erreur (client ou serveur) vient à l'écran au lieu de laisser l'écran remonter en haut
  useEffect(() => {
    if (!error) return;
    setTimeout(() => document.querySelector('[data-testid="form-error"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 80);
  }, [error]);

  // Iter 767 — navigation entre écrans (téléphone) : on ne passe à l'écran suivant que si l'écran courant est complet.
  const scrollHautFormulaire = () => setTimeout(() => document.querySelector('[data-testid="etapes-indicateur"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  const allerEtape = (n) => { setEtapeIssues([]); setEtape(n); scrollHautFormulaire(); };
  const etapeSuivante = () => {
    const probs = !selectedDest
      ? [{ id: 'dest', msg: 'Choisissez votre voyage et votre date.' }]
      : computeValidationIssues().filter(it => etapeDeAnchor(it) === etape);
    if (probs.length) { setValidationTentee(true); setEtapeIssues(probs); return; }
    allerEtape(Math.min(4, etape + 1));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    // ========== VALIDATION GLOBALE : affichée dans une MODALE au clic ==========
    const blockingIssues = computeValidationIssues();
    setValidationTentee(true);
    if (blockingIssues.length > 0) {
      setValidationModal({ open: true, issues: blockingIssues });
      // Scroll vers le premier point manquant
      setTimeout(() => {
        const first = blockingIssues[0];
        const el = first && first.anchor ? document.querySelector(`[data-testid="${first.anchor}"]`) : null;
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
      return;
    }

    if (!form.destination) return setError('Veuillez choisir une destination.');
    if (showBusDepart && !form.lieuDepart) return setError('Veuillez choisir votre lieu de départ en autocar.');
    if (isLloretEteCar && trajetsLegs.length === 0) return setError('Choisissez au moins un trajet en car (Aller et/ou Retour), ou prenez la formule « Sans transport » de cette semaine.');
    if (isFusion && sejourIsole && trajetsLegs.length === 1) return setError('Pour ce séjour, le car se prend en aller + retour : cochez les deux trajets ou choisissez la formule « Sans transport ».');
    if (isFusion && trajetsLegs.length === 0 && sansPlein) return setError('Les places « sans transport » de cette semaine sont toutes prises : choisissez un trajet en car ou inscrivez-vous en liste d\'attente.');
    if (chambresManquent) return setError(hotelRestCh > 0
      ? `L'hôtel n'a plus que ${hotelRestCh} chambre${hotelRestCh > 1 ? 's' : ''} disponible${hotelRestCh > 1 ? 's' : ''} sur la ou les semaines choisies et votre demande en compte ${nbChambresDemandees} : regroupez les voyageurs dans moins de chambres ou inscrivez-vous en liste d'attente.`
      : 'L\'hôtel est complet sur une des semaines choisies : inscrivez-vous en liste d\'attente ci-dessous.');
    if (trajetsSepares) {
      const nbV = form.voyageurs.filter(v => (v.nom || '').trim()).length;
      const plein = trajetsLegs.find(l => dispoSel.trajets[l].restantes !== -1 && dispoSel.trajets[l].restantes < nbV);
      if (plein) return setError(`Le car ${plein === 'aller' ? 'ALLER' : 'RETOUR'} de cette semaine n'a plus assez de places (${dispoSel.trajets[plein].restantes} restante(s) pour ${nbV} voyageur(s)) : décochez ce trajet ou choisissez la formule sans transport.`);
    }
    if (form.lieuDepart === 'autoroute' && (!form.aireAutoroute.trim() || !form.aireAutorouteCode)) return setError('Veuillez préciser l\'autoroute et le nom de l\'aire d\'autoroute.');
    if (!form.voyageurs[0].nom.trim()) return setError('Le NOM du 1er voyageur est obligatoire.');
    if (!form.voyageurs[0].prenom.trim()) return setError('Le PRÉNOM du 1er voyageur est obligatoire.');
    if (!form.voyageurs[0].dateNaissance.trim()) return setError('La DATE DE NAISSANCE du 1er voyageur est obligatoire.');
    // Iter 103m - Validation stricte du format JJ/MM/AAAA + valeurs fantaisistes
    const ddnCheck = validateFrenchBirthDate(form.voyageurs[0].dateNaissance, { minYear: 1900 });
    if (!ddnCheck.valid) return setError(`DATE DE NAISSANCE du 1ᵉʳ voyageur : ${ddnCheck.reason}. Format attendu : JJ/MM/AAAA (ex: 15/03/1985).`);
    for (let i = 1; i < form.voyageurs.length; i++) {
      const v = form.voyageurs[i];
      if ((v.nom || '').trim() || (v.prenom || '').trim() || (v.dateNaissance || '').trim()) {
        // Voyageur en cours de saisie : valide sa DDN aussi
        const c = validateFrenchBirthDate(v.dateNaissance || '', { minYear: 1900 });
        if (!c.valid) return setError(`DATE DE NAISSANCE du voyageur ${i + 1} (${v.prenom} ${v.nom}) : ${c.reason}. Format attendu : JJ/MM/AAAA.`);
      }
    }
    if (!form.contactSource || !(form.contactPrenom || '').trim() || !(form.contactNom || '').trim()) return setError('Choisissez la PERSONNE CONTACT pour ce formulaire (menu déroulant de la section Adresse postale) : c\'est à elle qu\'appartiennent le téléphone et l\'e-mail.');
    if (trajetUniqueOubli && !form.confirmeTrajetUnique) return setError(`⚠️ Vous n'avez pas choisi de ${trajetManquant.toUpperCase()} en car. Si c'est un oubli, cliquez sur « Ajouter le car ${trajetManquant} » dans la partie Transport ; sinon cochez la case « Je confirme : je fais le ${trajetManquant} par mes propres moyens ».`);
    if (telCheck.status === 'conflit' && !form.telAttestation) return setError(`Ce numéro de téléphone est déjà au nom de ${telCheck.autreNom}. Si c'est bien le vôtre, cochez « J'atteste que ce numéro est le mien » sous le téléphone ; sinon indiquez votre propre numéro.`);
    if (!(form.telephone || '').trim()) return setError('Le TÉLÉPHONE de la famille (section Adresse postale) est obligatoire.');
    // Vérifier tous les voyageurs ajoutés
    for (let i = 1; i < form.voyageurs.length; i++) {
      const v = form.voyageurs[i];
      if (v.nom.trim() || v.prenom.trim()) {
        if (!v.nom.trim()) return setError(`Le NOM du voyageur ${i + 1} est obligatoire.`);
        if (!v.prenom.trim()) return setError(`Le PRÉNOM du voyageur ${i + 1} est obligatoire.`);
        if (!v.dateNaissance.trim()) return setError(`La DATE DE NAISSANCE du voyageur ${i + 1} est obligatoire.`);
      }
    }

    // Iter 156n — Détection des doublons entre voyageurs : NOM + PRÉNOM +
    // DATE DE NAISSANCE doivent être TOUS LES TROIS identiques pour qu'on
    // considère qu'il s'agit du même voyageur saisi 2 fois.
    // Avant (Iter 102c) : règle "≥ 2 infos identiques sur 4" qui bloquait
    // injustement les jumeaux (même nom + même date de naissance, prénoms
    // différents). Cas signalé par Kamel en production.
    const norm = (s) => (s || '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
    const remplis = form.voyageurs
      .map((v, i) => ({ idx: i + 1, nom: norm(v.nom), prenom: norm(v.prenom), ddn: norm(v.dateNaissance) }))
      .filter(v => v.nom || v.prenom || v.ddn);
    for (let i = 0; i < remplis.length; i++) {
      for (let j = i + 1; j < remplis.length; j++) {
        const a = remplis[i], b = remplis[j];
        const sameNom = a.nom && a.nom === b.nom;
        const samePrenom = a.prenom && a.prenom === b.prenom;
        const sameDdn = a.ddn && a.ddn === b.ddn;
        if (sameNom && samePrenom && sameDdn) {
          return setError(
            `Doublon détecté : voyageur ${a.idx} et voyageur ${b.idx} ont le même nom, prénom et date de naissance. Si ce sont deux personnes différentes (homonymes), merci de vérifier les saisies. Sinon, supprimez le voyageur en double.`
          );
        }
      }
    }

    // Iter 152q — Filet anti-typo nom/prénom (cas BENSASSI saisi 2x différemment
    // sur clavier prédictif Android). Non bloquant : juste un confirm pour faire
    // relire l'utilisatrice avant signature.
    const typoWarnings = detectTypoWarnings(form.voyageurs, form.email);
    if (typoWarnings.length > 0) {
      const ok = window.confirm(
        "⚠️ Vérifie l'orthographe des noms/prénoms\n\n" +
        typoWarnings.join("\n\n") +
        "\n\nLe nom est utilisé sur la facture, le PDF organisateur et les listings " +
        "(aéroport, hôtel…). Une faute peut bloquer ton voyage.\n\n" +
        "✅ Clique « OK » si l'orthographe est correcte.\n" +
        "✏️  Clique « Annuler » pour corriger."
      );
      if (!ok) return;
    }

    if (!form.rue.trim()) return setError('L\'adresse (rue) est obligatoire.');
    if (!form.codePostal.trim()) return setError('Le code postal est obligatoire.');
    if (!form.ville.trim()) return setError('La ville est obligatoire.');
    if (!form.email.trim()) return setError('L\'adresse e-mail est obligatoire.');
    if (!form.certifieAdresse) return setError('Vous devez certifier que tous les voyageurs inscrits habitent à la même adresse ou sont mineurs sous votre responsabilité.');
    // Iter 154L — Acceptation obligatoire de la Charte de placement bus
    if (isAvecTransportBus && !form.accepteChartePlacementBus) {
      return setError("Vous devez lire et accepter la Charte de placement en autocar (case à cocher juste avant le bouton Envoyer).");
    }
    // Iter 326 — filet de sécurité journée : les blocs adhésion/assurance sont
    // masqués (non concernés) → on force les valeurs 'journee' au lieu de bloquer.
    if (!hasNuitee) {
      if (form.adhesion !== 'journee' || form.assurance !== 'journee') {
        form.adhesion = 'journee';
        form.montantAdhesion = '0';
        form.assurance = 'journee';
        form.montantAssurance = '0';
        if (!form.paiement) form.paiement = '100';
        setForm(p => ({ ...p, adhesion: 'journee', montantAdhesion: '0', assurance: 'journee', montantAssurance: '0', paiement: p.paiement || '100' }));
      }
    }
    if (!form.adhesion) return setError('Veuillez indiquer votre situation d\'adhésion.');
    if (!form.assurance) return setError('Veuillez indiquer votre choix concernant l\'assurance.');
    if (!form.paiement) return setError('Veuillez choisir une option de paiement.');
    // Iter 103e/g - Validation mode de règlement SUPPRIMÉE (la section a été retirée du formulaire).
    // L'utilisateur choisit son mode de règlement directement sur la page « Régler » (CB, virement,
    // ou rendez-vous pour espèces/chèque/ANCV). Le canalReglement reste optionnel.
    // Iter 179 — validation placement autocar supprimée (choix des places par le voyageur via « Mon Bus »).
    // Validation supplément chambre obligatoire si coché
    if (form.supplementChambre) {
      const m = parseFloat(form.montantSupplementChambre);
      if (!m || m <= 0) return setError('Supplément chambre individuelle : merci d\'indiquer le montant (visible sur la page du voyage ou via WhatsApp).');
    }
    // modeReglement / canalReglement conservés en état par défaut pour le PDF mais plus bloquants à la soumission
    // Vérifier que chaque voyageur a un tarif > 0
    const voyageursActifs = form.voyageurs.filter(v => v.nom.trim());
    // Blocage places disponibles
    const spots = spotsMap[form.destination];
    if (spots !== undefined && spots !== -1 && voyageursActifs.length > spots) {
      return setError('Il ne reste pas assez de places sur ce voyage pour tous les voyageurs de ce formulaire. Merci de réduire le nombre de voyageurs.');
    }
    for (let i = 0; i < voyageursActifs.length; i++) {
      const f = parseFloat(voyageursActifs[i].forfait);
      if (!f || f <= 0) {
        return setError(`Le tarif du voyageur ${voyageursActifs[i].nom} ${voyageursActifs[i].prenom} est obligatoire et doit être supérieur à 0. Consultez la page du voyage pour connaître le prix par personne.`);
      }
    }
    if (!form.cond1 || !form.cond2 || !form.cond3 || !form.cond4 || !form.cond5) return setError('Vous devez cocher toutes les conditions pour valider votre inscription.');
    if (!form.condRgpd) return setError('Vous devez accepter la politique de confidentialité (RGPD) pour valider votre inscription.');

    // Iter 125 — Inscription mineur(s) seul(s) : exiger autorisation parentale + représentant légal renseigné
    const hasMajeur = voyageursActifs.some((v) => {
      const age = ageAuDepartNum(v.dateNaissance);
      return age !== null && age >= 18;
    });
    if (!hasMajeur) {
      if (!form.autorisationParentale) {
        setTimeout(() => {
          const el = document.querySelector('[data-testid="autorisation-parentale-block"]');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
        return setError("Inscription d'un/des mineur(s) seul(s) : vous devez cocher la case d'autorisation parentale et renseigner vos coordonnées en tant que représentant légal.");
      }
      if (form.rempliPar !== 'tiers' || !form.rempliParTiersNom?.trim() || !form.rempliParTiersPrenom?.trim() || !['parent', 'tuteur-legal'].includes(form.rempliParTiersLien)) {
        setTimeout(() => {
          const el = document.querySelector('[data-testid="autorisation-parentale-block"]');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
        return setError("Inscription d'un/des mineur(s) seul(s) : merci de renseigner votre nom, prénom et qualité (parent ou tuteur légal) du représentant légal.");
      }
    }

    // Blocage voyageur solo — retiré (soloBlockerActive est désormais toujours false)

    // Validation "Rempli par un tiers"
    if (form.rempliPar === 'tiers') {
      if (!form.rempliParTiersNom?.trim() || !form.rempliParTiersPrenom?.trim() || !form.rempliParTiersLien) {
        return setError('Vous avez indiqué remplir ce formulaire pour un tiers. Merci de renseigner votre nom, prénom et lien avec l\'inscrit(e).');
      }
    }

    // Validation répartition des chambres — obligatoire si voyage avec nuits
    const isNightTrip = selectedDest?.dateDepart && selectedDest?.dateFin && selectedDest.dateDepart !== selectedDest.dateFin;
    if (isNightTrip) {
      const chambres = form.chambres || [];
      const totalAssignes = chambres.reduce((s, c) => s + (c.occupants || []).length, 0);
      if (totalAssignes === 0) return setError('Veuillez répartir les voyageurs dans une ou plusieurs chambres.');
      if (totalAssignes < voyageursActifs.length) return setError(`Tous les voyageurs doivent être répartis dans une chambre (${totalAssignes}/${voyageursActifs.length} attribué(s)).`);
      // Chaque chambre doit avoir un type choisi
      for (let ci = 0; ci < chambres.length; ci++) {
        const ch = chambres[ci];
        const compagnons = Array.isArray(ch.compagnons) ? ch.compagnons.filter(c => (c.nom || '').trim() || (c.prenom || '').trim()) : [];
        const autresNames = ch.autreOccupants?.trim() ? ch.autreOccupants.split(',').filter(n => n.trim()) : [];
        const autresCount = compagnons.length + autresNames.length;
        const count = (ch.occupants || []).length + autresCount + (ch.partageSolo ? 1 : 0);
        if (count === 0) continue;
        // Validation : nom + prénom OBLIGATOIRES pour chaque compagnon
        for (const comp of compagnons) {
          if (!comp.nom?.trim() || !comp.prenom?.trim()) {
            return setError(`Chambre ${ci + 1} : merci d'indiquer le nom ET le prénom de chaque personne d'un autre formulaire ajoutée manuellement.`);
          }
        }
        // Type de chambre : auto-appliqué quand une seule option est possible (1, 3 ou 4 occupants).
        // Seule la chambre à 2 personnes nécessite un choix explicite (grand lit ou 2 lits).
        if (!ch.typeChambre) {
          let autoType = null;
          if (count === 1) autoType = 'individuelle';
          else if (count === 3) autoType = '3-lits';
          else if (count === 4) autoType = '4-lits';
          if (autoType) {
            const updatedChambres = [...chambres];
            updatedChambres[ci] = { ...ch, typeChambre: autoType };
            setForm(prev => ({ ...prev, chambres: updatedChambres }));
            ch.typeChambre = autoType;
          } else {
            return setError(`Chambre ${ci + 1} : choisissez entre "1 grand lit (couple)" et "2 lits simples".`);
          }
        }
        // Iter 97 - Cohérence typeChambre vs nombre d'occupants. Cas reporté par
        // l'utilisateur : Hassani 2 voyageurs marqué "individuelle". On force
        // l'admin/voyageur à corriger explicitement le type.
        if (count === 2 && ch.typeChambre !== 'grand-lit' && ch.typeChambre !== '2-lits') {
          return setError(`Chambre ${ci + 1} : pour 2 personnes, vous devez choisir explicitement "1 grand lit (couple)" ou "2 lits simples". Le type actuel "${ch.typeChambre}" n'est pas compatible.`);
        }
        // Iter 109h/318 — pour count >= 3, la literie est TOUJOURS auto-corrigée
        // (3-lits / 4-lits), même si un choix "grand-lit"/"2-lits" résiduel est
        // resté après l'ajout d'une 3e personne (voyageur ou autre formulaire).
        if (count >= 3 && ch.typeChambre !== (count === 3 ? '3-lits' : '4-lits')) {
          const autoType = count === 3 ? '3-lits' : '4-lits';
          const updatedChambres = [...chambres];
          updatedChambres[ci] = { ...ch, typeChambre: autoType };
          setForm(prev => ({ ...prev, chambres: updatedChambres }));
          ch.typeChambre = autoType;
        }
        if (count === 1 && ch.typeChambre !== 'individuelle') {
          const updatedChambres = [...chambres];
          updatedChambres[ci] = { ...ch, typeChambre: 'individuelle' };
          setForm(prev => ({ ...prev, chambres: updatedChambres }));
          ch.typeChambre = 'individuelle';
        }
        // Iter 278/318 — capacité max par chambre selon la config admin (triple/quadruple)
        {
          const adminCfgSubmit = chambreIndOverrides[selectedDest?.id] || {};
          const tripleOkSubmit = adminCfgSubmit.triple_disponible === false ? false : true;
          const quadOkSubmit = adminCfgSubmit.quadruple_disponible === true ? true
            : adminCfgSubmit.quadruple_disponible === false ? false : isLloretOnly;
          const maxPersSubmit = quadOkSubmit ? 4 : tripleOkSubmit ? 3 : 2;
          if (count > maxPersSubmit) {
            return setError(`Chambre ${ci + 1} : maximum ${maxPersSubmit} personnes par chambre sur ce voyage (personnes d'un autre formulaire incluses). Répartissez les voyageurs autrement ou contactez l'association au 06 60 41 62 18.`);
          }
        }
        // Contrainte Espagne
        if (selectedDest?.espagne && count === 4) {
          const parseDDN = (ddn) => { if (!ddn) return null; const m = ddn.match(/^(\d{2})\/(\d{2})\/(\d{4})$/); if (!m) return null; return new Date(+m[3], +m[2] - 1, +m[1]); };
          const dateDepart = new Date(selectedDest.dateDepart);
          const ageAuDepart = (ddn) => { const d = parseDDN(ddn); if (!d) return null; let a = dateDepart.getFullYear() - d.getFullYear(); const mo = dateDepart.getMonth() - d.getMonth(); if (mo < 0 || (mo === 0 && dateDepart.getDate() < d.getDate())) a--; return a; };
          const adultesInscrits = (ch.occupants || []).map(vi => voyageursActifs[vi]).filter(Boolean).filter(v => { const age = ageAuDepart(v.dateNaissance); return age === null || age >= 12; }).length;
          const compagnonsMoins12 = compagnons.filter(c => c.tranche === 'moins12').length;  // Iter 914
          const adultesTotal = adultesInscrits + autresCount - compagnonsMoins12 + (ch.partageSolo ? 1 : 0);
          if (adultesTotal >= 4) return setError(`Chambre ${ci + 1} (Espagne) : une chambre quadruple nécessite au moins 1 personne de moins de 12 ans. Merci de modifier la répartition.`);
        }
      }
    }

    setSubmitting(true);
    try {
      // Enrichir chaque voyageur avec son age au depart (lisible)
      const voyageursEnriched = form.voyageurs.map(v => ({
        ...v,
        ageAuDepart: ageLisible(v.dateNaissance) || '',
      }));
      let provenance = null;
      try { provenance = JSON.parse(localStorage.getItem('raav_provenance') || 'null'); } catch { provenance = null; }
      const payload = {
        ...form,
        provenance: { source: provenance?.source || 'direct', campagne: provenance?.campagne || null, lien: provenance?.lien || null, declare: form.connuPar || null, parrain: provenance?.parrain || null },
        voyageurs: voyageursEnriched,
        adresse: `${form.rue}, ${form.codePostal} ${form.ville}`,
        contactPrenom: (form.contactPrenom || '').trim(), contactNom: (form.contactNom || '').trim().toUpperCase(),
        contactNomComplet: `${(form.contactPrenom || '').trim()} ${(form.contactNom || '').trim().toUpperCase()}`.trim(),
        telAttestation: telCheck.status === 'conflit' && !!form.telAttestation,
        destinationLabel: selectedDest ? selectedDest.group : form.destination,
        destinationDate: selectedDest ? selectedDest.label : '',
        destinationDateDepart: selectedDest ? (selectedDest.dateDepart || '') : '',
        destinationPrice: selectedDest ? selectedDest.price : '',
        destinationTransport: isFusion ? (trajetsLegs.length ? 'Avec transport' : 'Sans transport') : (selectedDest ? selectedDest.transport : ''),
        isAvion: selectedDest?.avion || false,
        isCar: isFusion ? trajetsLegs.length > 0 : (selectedDest?.car || false),
        prixTrajet: isFusion ? prixTrajet : undefined,
        busTrajets: isFusion ? busTrajetsSemaine1 : form.busTrajets,
        nbSemaines: nbSemainesEff,
        // Iter 778 — une semaine = une facture : la fiche mère ne porte QUE la semaine 1 ; chaque semaine suivante a son total, son assurance et son acompte
        semainesSuivantes: semainesSupp.map(w => ({ destination: w.dest.id, busTrajets: w.busTrajets, prixTrajet: w.prixTrajet, supplementPiscine: w.piscine,
          montantAssurance: w.assUnit, assuranceTotal: w.assurance, totalVoyage: w.total,
          montantCalcule: acompteSemaine(w, form.paiement === '20' ? 0.2 : form.paiement === '50' ? 0.5 : 1),
          forfaits: Object.fromEntries(Object.keys(w.forfaits).map(k => [k, w.forfaits[k]])) })),
        excursionsPre: form.excursionsPre || [],
        isEspagne: selectedDest?.espagne || false,
        forfaitUnitaire: selectedDest ? parseInt(selectedDest.price.replace(/[^\d]/g, '')) || 0 : 0,
        forfaitTotal: form.voyageurs.filter(v => v.nom.trim()).reduce((s, v) => s + (parseFloat(v.forfait) || 0), 0),
        assuranceTotal: (parseFloat(form.montantAssurance) || 0) * form.voyageurs.filter(v => v.nom.trim()).length,
        montantCalcule: nbSemainesEff > 1 && form._acompteS1 ? form._acompteS1 : form.montantCalcule,
        totalVoyage: nbSemainesEff > 1 && form._totalVoyageS1 ? form._totalVoyageS1 : (form._totalVoyage || form.montantCalcule),
        // Iter 100 — Audit RGPD : horodatage du consentement
        rgpdConsentAt: new Date().toISOString(),
        newsletterConsent: !!form.condNewsletter,
        // Iter 262 — le token d'invitation privée est renvoyé au serveur pour le bypass
        // de la garde anti-sur-réservation (voyage complet).
        invitationToken: new URLSearchParams(window.location.search).get('invitation') || '',
      };
      const res = await fetch(`${API}/api/registrations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        let espaceToken = null;
        try { espaceToken = (await res.json())?.espace_token || null; } catch { espaceToken = null; }
        setSubmitted(true);
        window.scrollTo(0, 0);
        // Iter 550 — nouvel adhérent : l'espace s'ouvre directement (sinon lien par e-mail pour protéger l'historique)
        if (espaceToken) {
          setEspaceDirect(`/espace-adherent/${espaceToken}?payer=1&bienvenue=1`);
          navigate(`/espace-adherent/${espaceToken}?payer=1&bienvenue=1`);  // Iter 551 — ouverture immédiate
          setRedirectSeconds(0);
          return;
        }
        // Pour le mode "Avoir" (seul mode) → pas de redirection vers /payer
        if ((form.modesReglement || []).length === 1 && form.modesReglement[0] === 'avoir') {
          setRedirectSeconds(0);
          return;
        }
        // Iter 551 — adhérent déjà connu : page /moi immédiate, lien envoyé automatiquement (protège son historique)
        navigate(`/moi?payer=1&auto=1&email=${encodeURIComponent(form.email || '')}`);
        setRedirectSeconds(0);
        return;
        // eslint-disable-next-line no-unreachable
        setRedirectSeconds(20);
        const intervalId = setInterval(() => {
          setRedirectSeconds(prev => {
            if (prev <= 1) {
              clearInterval(intervalId);
              navigate(`/moi?payer=1&auto=1&email=${encodeURIComponent(form.email || '')}`);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
      else {
        // Iter 262 — Affiche le message précis du serveur (ex : voyage complet → 409)
        let msg = 'Une erreur est survenue. Veuillez réessayer.';
        let typeErr = '';
        try {
          const j = await res.json();
          if (j?.detail && typeof j.detail === 'object') { msg = j.detail.message || msg; typeErr = j.detail.type || ''; }
          else if (j?.detail) msg = j.detail;
        } catch { /* garde le message générique */ }
        // Iter 263 — voyage complet (409) : proposer la liste d'attente pré-remplie (pas pour un ajout de semaine — Iter 887)
        if (res.status === 409 && selectedDest && typeErr !== 'semaine_ajout') setWaitlistOffer(selectedDest);
        setErrorType(typeErr);
        setError(msg);
      }
    } catch { setError('Erreur de connexion. Veuillez réessayer.'); }
    setSubmitting(false);
  };

  const Radio = ({ name, value, label, current, onClick }) => (
    <label className="flex items-center gap-3 cursor-pointer group py-1.5 px-2 -mx-2 rounded-lg hover:bg-sky-50" onClick={onClick} data-testid={`radio-${name}-${value}`}>
      <div className={`w-6 h-6 rounded-full border-[3px] flex items-center justify-center transition flex-shrink-0 ${current === value ? 'border-[#1E3A5F] bg-[#1E3A5F]' : 'border-gray-500 bg-white hover:border-[#1E3A5F]'}`}>
        {current === value && <div className="w-2.5 h-2.5 rounded-full bg-white" />}
      </div>
      <span className="text-sm text-gray-700">{label}</span>
    </label>
  );

  const Check = ({ checked, onChange, label, testId }) => (
    <label className="flex items-start gap-3 cursor-pointer group py-2 px-2 -mx-2 rounded-lg hover:bg-sky-50" onClick={onChange} data-testid={testId}>
      <div className={`w-6 h-6 rounded border-[3px] flex items-center justify-center transition flex-shrink-0 mt-0.5 ${checked ? 'border-[#1E3A5F] bg-[#1E3A5F]' : 'border-gray-500 hover:border-[#1E3A5F] bg-white'}`}>
        {checked && <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/></svg>}
      </div>
      <span className="text-sm text-gray-700 font-medium leading-snug">{label}</span>
    </label>
  );

  const SectionHeader = ({ title, color }) => (
    <div className={`${color || 'bg-[#79c9ea]'} text-white px-6 py-3`}>
      <h2 className="font-bold text-base sm:text-lg">{title}</h2>
    </div>
  );

  // Generate PDF of the completed form.
  // mode='save' (default) → télécharge directement (workflow post-soumission)
  // mode='preview' → retourne un blob URL pour aperçu modal (iter 89)
  const generateFormPDF = (mode = 'save') => {
    const doc = new jsPDF();
    const m = 20;
    const w = doc.internal.pageSize.getWidth();
    let y = 20;
    const line = (text, size, style, color) => {
      doc.setFontSize(size); doc.setFont('helvetica', style || 'normal'); doc.setTextColor(...(color || [0,0,0]));
      const lines = doc.splitTextToSize(text, w - m * 2);
      if (y + lines.length * size * 0.5 > 275) { doc.addPage(); y = 20; }
      doc.text(lines, m, y);
      y += lines.length * size * 0.45 + 2;
    };
    const row = (l, r) => { doc.setFontSize(10); doc.setFont('helvetica','normal'); doc.setTextColor(0,0,0); doc.text(l, m, y); doc.text(r, w - m, y, {align:'right'}); y += 6; };

    // Bandeau "APERCU" en mode preview (rouge clair, italique)
    if (mode === 'preview') {
      doc.setFillColor(254, 226, 226);
      doc.rect(0, 0, w, 10, 'F');
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(180, 30, 30);
      doc.text('APERCU AVANT VALIDATION - non contractuel - vos donnees ne sont pas encore enregistrees', w / 2, 6.5, { align: 'center' });
      y = 16;
    }

    line('RHONE ALPES AMITIE VOYAGES', 14, 'bold', [30,58,95]);
    line('RECAPITULATIF D\'INSCRIPTION', 12, 'bold', [230,126,34]);
    y += 4;
    line(`Destination : ${selectedDest ? `${selectedDest.group} - ${selectedDest.label}` : form.destination || '(non choisie)'}`, 10, 'bold');
    if (selectedDest?.dateDepart) row('Date de depart :', selectedDest.dateDepart.split('-').reverse().join('/'));
    if (form.lieuDepart && form.lieuDepart !== 'non-concerne') row('Lieu de depart :', form.lieuDepart === 'autoroute' ? `Aire autoroute : ${form.aireAutoroute}` : form.lieuDepart);
    y += 4;

    // VOYAGEURS avec forfaits calcules
    line('VOYAGEURS', 11, 'bold', [30,58,95]);
    const lloretComputed = isLloretTrip ? computeLloretForfaits() : {};
    let totalForfaits = 0;
    form.voyageurs.forEach((v, i) => {
      if (v.nom || v.prenom) {
        const civ = v.civilite === 'M' ? 'M.' : v.civilite === 'Mme' ? 'Mme' : '';
        const ageStr = v.dateNaissance ? ` (${ageLisible(v.dateNaissance) || ''})` : '';
        row(`  ${i+1}. ${civ} ${v.nom} ${v.prenom}${ageStr}`, v.dateNaissance || '');
        if (v.telephone) row('     Tel :', v.telephone);
        const forfait = isLloretTrip ? (parseFloat(lloretComputed[i]?.forfait) || 0) : (parseFloat(v.forfait) || 0);
        if (forfait > 0) {
          row('     Forfait :', `${forfait} EUR`);
          totalForfaits += forfait;
        }
      }
    });
    y += 2;

    // CHAMBRES
    if (hasNuitee && (form.chambres || []).length > 0) {
      line('REPARTITION DES CHAMBRES', 11, 'bold', [30,58,95]);
      (form.chambres || []).forEach((ch, ci) => {
        const occs = (ch.occupants || []).map(idx => {
          const v = form.voyageurs?.[idx];
          return v ? `${v.nom || ''} ${v.prenom || ''}`.trim() : '';
        }).filter(Boolean);
        const compagnons = (ch.compagnons || []).filter(c => (c.nom || '').trim()).map(c => `${c.nom} ${c.prenom} (autre form.)`);
        const autres = (ch.autreOccupants || '').trim() ? [(ch.autreOccupants || '').trim() + ' (autre form.)'] : [];
        const all = [...occs, ...compagnons, ...autres];
        const typeLabels = {'individuelle':'Chambre individuelle','grand-lit':'1 grand lit','2-lits':'2 lits separes','3-lits':'3 lits separes','4-lits':'4 lits separes'};
        const typeStr = typeLabels[ch.typeChambre] || ch.typeChambre || '(type a confirmer)';
        row(`  Chambre ${ci+1} (${typeStr}) :`, '');
        all.forEach(name => row(`     - ${name}`, ''));
        if (ch.partageSolo) row('     - + 1 voyageur(se) solo a binomer', '');
      });
      y += 2;
    }

    if (isLloretEteCar) row('Car :', trajetsLegs.length === 2 ? `Aller + Retour (${prixTrajet} EUR x 2 / pers., inclus)` : trajetsLegs.length === 1 ? `${trajetsLegs[0] === 'aller' ? 'Aller' : 'Retour'} seulement (${prixTrajet} EUR / pers.)` : 'aucun trajet');
    if (isFusion) row('Transport :', trajetsLegs.length === 0 ? 'Sans transport (par mes propres moyens)' : trajetsLegs.length === 2 ? `Car aller + retour (+${prixTrajet} EUR x 2 / pers.)` : `Car ${trajetsLegs[0] === 'aller' ? 'aller' : 'retour'} seulement (+${prixTrajet} EUR / pers.)`);
    if ((form.excursionsPre || []).length) row('Excursions :', `${form.excursionsPre.length} pre-reservee(s), a regler en especes sur place`);
    row('Adresse :', `${form.rue || ''}, ${form.codePostal || ''} ${form.ville || ''}`.trim());
    row('Email :', form.email || '');
    y += 4;

    // ADHESION / ASSURANCE / SUPPLEMENTS / TOTAL
    line('TARIFS ET OPTIONS', 11, 'bold', [30,58,95]);
    const adhMontant = parseFloat(form.montantAdhesion) || 0;
    const adhLabel = form.adhesion === 'deja-inscrit' ? `Deja inscrit (autre voyage ${voyageYear})` : form.adhesion === 'premier-voyage' ? `1er voyage ${voyageYear} - ${adhMontant} EUR` : form.adhesion === 'journee' ? 'Voyage a la journee (sans adhesion)' : '(non choisi)';
    row('Adhesion :', adhLabel);
    const assMontant = parseFloat(form.assuranceTotal || form.montantAssurance) || 0;
    const assLabel = form.assurance === 'oui' ? `Souscrite - ${assMontant} EUR` : form.assurance === 'non' ? 'Refusee' : 'Voyage a la journee (non concernee)';
    row('Assurance multirisque :', assLabel);
    const suppChamb = parseFloat(form.montantSupplementChambre) || 0;
    if (suppChamb > 0) {
      row('Supplement chambre individuelle :', `${suppChamb} EUR`);
      totalForfaits += suppChamb;
    }
    const suppPisc = parseFloat(form.supplementPiscine) || 0;
    if (suppPisc > 0) {
      row('Vue piscine garantie (par chambre) :', `${suppPisc} EUR`);
      totalForfaits += suppPisc;
    }
    const totalGeneral = totalForfaits + adhMontant + assMontant;
    if (totalGeneral > 0) {
      doc.setDrawColor(200, 200, 200);
      doc.line(m, y, w - m, y);
      y += 4;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 58, 95);
      doc.text('TOTAL VOYAGE :', m, y);
      doc.text(`${totalGeneral} EUR`, w - m, y, { align: 'right' });
      y += 8;
    }

    // PAIEMENT
    line('REGLEMENT', 11, 'bold', [30,58,95]);
    row('Paiement choisi :', form.paiement === '20' ? 'Acompte 20% (depart dans plus de 6 mois)' : form.paiement === '50' ? 'Acompte 50% (depart dans moins de 6 mois)' : form.paiement === '100' ? 'Reglement integral 100% (depart dans moins de 45 jours)' : form.paiement === 'journee' ? 'Voyage a la journee - 100%' : '(non choisi)');
    const reglementLabels = {'carte-bancaire':'Carte bancaire','virement':'Virement bancaire','especes':'Especes','cheque':'Cheque','ancv':'Cheques-vacances ANCV','avoir':'Avoir (voyage anterieur)'};
    const modesTxt = (form.modesReglement || []).map(mm => reglementLabels[mm] || mm).join(' + ') || '(non precise)';
    row('Mode(s) de reglement :', modesTxt);
    const canalTxt = form.canalReglement === 'en-ligne' ? 'En ligne'
      : form.canalReglement === 'rendez-vous' ? 'En rendez-vous'
      : form.canalReglement === 'lettre-recommandee' ? 'Lettre recommandee'
      : form.canalReglement === 'autre' ? `Autre : ${form.canalAutreType || ''}${form.canalAutreExplication ? ' - ' + form.canalAutreExplication : ''}`
      : '(non precise)';
    row('Reglement prevu :', canalTxt);
    if (form.montantCalcule) {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(230, 126, 34);
      doc.text('Montant a regler aujourd\'hui :', m, y);
      doc.text(`${form.montantCalcule} EUR`, w - m, y, { align: 'right' });
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      y += 8;
    }

    // PLACEMENT AUTOCAR
    if (showBusDepart && form.placementAutocarChoix) {
      line('PLACEMENT AUTOCAR', 11, 'bold', [30,58,95]);
      if (form.placementAutocarChoix === 'seul') row('  Choix :', 'Personne d\'un autre formulaire a regrouper');
      else if (form.placementAutocarChoix === 'avec') {
        row('  A regrouper avec :', '');
        const personnes = form.placementAutocarPersonnes || [];
        personnes.filter(p => (p.nom || '').trim() || (p.prenom || '').trim()).forEach(p => {
          row(`     - ${(p.nom || '').trim()} ${(p.prenom || '').trim()}`, '');
        });
      }
      y += 2;
    }

    // FORMALITES
    if (form.formalites && form.formalites.length > 0) {
      const formalitesLabels = {'carte-identite': "Carte d'identite", 'carte-sejour': 'Carte de sejour', 'passeport': 'Passeport'};
      const statutLabels = {'valide': 'en cours de validite', 'renouveler': 'a renouveler'};
      const details = form.formalites.map(f => {
        const statut = form.formalitesStatut[f];
        return formalitesLabels[f] + (statut ? ` (${statutLabels[statut]})` : '');
      });
      row('Pieces d\'identite :', details.join(', '));
    }

    y += 6;
    doc.setDrawColor(200,200,200); doc.line(m, y, w-m, y); y += 6;
    line('IMPORTANT : Le paiement en ligne ou la prise de rendez-vous doit etre effectue(e) dans les 24 heures.', 9, 'bold', [200,0,0]);
    line('Sans reglement dans ce delai, l\'inscription sera automatiquement annulee.', 9, 'normal', [200,0,0]);
    y += 4;
    line(`Date d'inscription : ${form.dateInscription}`, 9);
    line('Signature : Validee electroniquement', 9, 'italic', [100,100,100]);
    y += 4;
    line('Rhone Alpes Amitie Voyages - rhoneamitie@gmail.com', 8, 'italic', [150,150,150]);

    if (mode === 'preview') {
      return doc.output('bloburl');
    }
    doc.save(`inscription-${form.voyageurs[0].nom || 'voyage'}-${form.dateInscription.replace(/\//g, '-')}.pdf`);
    return null;
  };

  // Iter 89 : ouvrir un aperçu PDF avant l'envoi du formulaire
  const openPreview = () => {
    try {
      // Révoquer l'ancienne URL si elle existe (libère la mémoire)
      if (previewPdfUrl) {
        try { URL.revokeObjectURL(previewPdfUrl); } catch { /* ignore */ }
      }
      const url = generateFormPDF('preview');
      setPreviewPdfUrl(url);
      setPreviewOpen(true);
    } catch (e) {
      setError('Impossible de générer l\'aperçu. Merci de vérifier les champs déjà remplis et de réessayer.');
    }
  };

  // Iter 595 (mobile) — la bulle assistant remonte pour ne pas cacher le bouton Envoyer collant
  useEffect(() => { document.body.classList.add('form-inscription'); return () => document.body.classList.remove('form-inscription'); }, []);

  if (submitted) {
    const montantRappel = parseFloat(form.montantCalcule) || 0;
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F8F4EF] to-white pt-28 pb-16 flex items-center">
        <div className="max-w-xl mx-auto px-4 w-full space-y-4" data-testid="registration-success">
          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl shadow-xl p-8 text-white text-center">
            <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-9 h-9 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
            </div>
            <h1 className="text-2xl font-bold mb-2">Inscription enregistrée !</h1>
            <p className="text-green-50 text-sm leading-relaxed mb-1">Merci pour votre confiance, nous sommes ravis de vous compter parmi nos voyageurs.</p>
            <p className="text-green-50 text-sm leading-relaxed mt-2">
              Un email vous a été envoyé avec le <strong>récapitulatif complet de votre inscription</strong>.
            </p>
            <div className="mt-3 bg-amber-100 border-2 border-amber-300 rounded-xl px-3 py-2.5 text-amber-900 text-sm leading-relaxed" data-testid="success-spam-notice">
              <p className="font-bold mb-0.5">⚠️ Email introuvable ?</p>
              <p className="text-xs">
                Pensez à vérifier votre <strong>dossier courriers indésirables / spams</strong> — certains fournisseurs (Orange, Yahoo, Hotmail…) y classent parfois nos emails. Marquez-nous comme "non indésirable" pour la prochaine fois.
              </p>
            </div>

            {montantRappel > 0 && !((form.modesReglement || []).length === 1 && form.modesReglement[0] === 'avoir') && (
              <div className="mt-5 bg-white/15 backdrop-blur rounded-xl p-4" data-testid="success-montant-rappel">
                <p className="text-[10px] uppercase tracking-wider opacity-80">Montant à régler</p>
                <p className="text-3xl font-bold mt-1">{montantRappel} €</p>
                <p className="text-[12px] font-semibold mt-2">Vous avez 24 heures pour payer en ligne ou prendre un rendez-vous.</p>
              </div>
            )}

            {(form.modesReglement || []).includes('avoir') && (
              <div className="mt-5 bg-white/15 backdrop-blur rounded-xl p-4" data-testid="success-avoir-info">
                <p className="text-[10px] uppercase tracking-wider opacity-80">Avoir à appliquer</p>
                <p className="text-2xl font-bold mt-1">{form.avoirMontant} € — {form.avoirVoyageOrigine}</p>
                <p className="text-[12px] mt-2">Nous vérifierons votre avoir et vous confirmerons <strong>la déduction de cet avoir</strong>, qui apparaîtra sur votre facture.</p>
              </div>
            )}

            {espaceDirect && (
              <div className="mt-5 bg-white/15 backdrop-blur rounded-xl p-4" data-testid="success-espace-direct">
                <p className="text-sm font-bold">👤 Bienvenue ! Votre Espace Adhérent est prêt.</p>
                <p className="text-[12px] mt-1 opacity-90">Il s'ouvre tout seul dans quelques secondes : vous y verrez votre reste à payer et toutes les façons de régler à distance. Votre lien personnel vous est aussi envoyé par e-mail.</p>
                <button onClick={() => navigate(espaceDirect)} data-testid="goto-espace-direct-btn" className="mt-3 bg-white text-green-700 font-bold px-6 py-3 rounded-xl text-sm hover:bg-green-50 transition">Ouvrir mon espace maintenant →</button>
              </div>
            )}
            {!espaceDirect && !((form.modesReglement || []).length === 1 && form.modesReglement[0] === 'avoir') && (
            <>
              <p className="text-green-100 text-xs mt-4 opacity-90">
                Ouverture automatique de votre <strong>Espace Adhérent</strong> dans <strong>{redirectSeconds} seconde{redirectSeconds > 1 ? 's' : ''}</strong>…
              </p>
              <p className="text-green-100 text-[11px] opacity-80 mt-1">
                Votre lien d'accès personnel est envoyé sur <strong>{form.email}</strong> : un clic ouvre votre espace, puis réglez à distance (virement, carte, chèques-vacances papier) ou prenez rendez-vous à la permanence.
              </p>
              <button onClick={() => navigate(`/moi?payer=1&auto=1&email=${encodeURIComponent(form.email || '')}`)}
                data-testid="goto-payer-btn"
                className="mt-5 bg-white text-green-700 font-bold px-6 py-3 rounded-xl text-sm hover:bg-green-50 transition inline-flex items-center gap-2">
                Ouvrir mon espace pour régler maintenant
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3"/></svg>
              </button>
            </>
            )}
          </div>

          {/* Assistance WhatsApp */}
          <div className="bg-white border-l-4 border-[#25D366] rounded-xl shadow-sm px-5 py-4" data-testid="success-whatsapp-help">
            <p className="text-sm text-[#1E3A5F] font-semibold mb-1">Une difficulté pour régler cette somme ?</p>
            <p className="text-xs text-gray-600 leading-relaxed mb-2">
              Si ce montant pose problème, contactez-nous par WhatsApp : nous étudierons ensemble une solution (étalement, mode de paiement alternatif…).
            </p>
            <a href="https://wa.me/message/XTYPH2HUY3L6L1" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white font-bold px-4 py-2 rounded-lg text-xs transition">
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/></svg>
              Nous écrire sur WhatsApp
            </a>
          </div>

          <p className="text-center text-xs text-gray-400">Si vous avez bien reçu l'email, merci de nous confirmer sa bonne réception (par email ou WhatsApp).</p>
        </div>
      </div>
    );
  }


  // Iter 595 (mobile) — avancement du dossier pour le bandeau collant (champs essentiels)
  const avancement = (() => {
    const v0 = (form.voyageurs || [])[0] || {};
    const champs = [['Voyage', !!form.destination], ['Nom', !!(v0.nom || '').trim()], ['Prénom', !!(v0.prenom || '').trim()], ['Date de naissance', !!(v0.dateNaissance || '').trim()],
      ['Téléphone', !!(form.telephone || '').trim()], ['E-mail', !!(form.email || '').trim()], ['Adresse', !!(form.rue || '').trim()], ['Code postal', !!(form.codePostal || '').trim()], ['Ville', !!(form.ville || '').trim()]];
    const ok = champs.filter(c => c[1]).length;
    return { pct: Math.round(ok / champs.length * 100), manquants: champs.filter(c => !c[1]).map(c => c[0]) };
  })();

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F8F4EF] to-white">
      <section className="bg-gradient-to-r from-[#79c9ea] to-[#5ba8c8] text-white py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 uppercase tracking-wide">S'INSCRIRE À UN VOYAGE</h1>
          <p className="text-base sm:text-lg opacity-90">Association Rhône Alpes Amitié Voyages</p>
          <p className="text-sm opacity-70 mt-2 max-w-md mx-auto italic">
            On part pour voir du pays, mais on revient souvent avec bien plus que des photos.
          </p>
        </div>
      </section>
      <div className="max-w-3xl mx-auto px-4 py-10" data-testid="registration-form-page">

        {/* En ligne Tab */}
            {/* AVERTISSEMENTS IMPORTANTS */}
            <div className={`space-y-3 mb-6${st(1)}`}>
              <div className="flex justify-end" data-testid="express-bar">
                <a href="/comment-sinscrire" target="_blank" rel="noopener noreferrer"
                  className="text-sm font-bold text-[#5BC5D4] underline hover:text-[#1E3A5F]" data-testid="lien-depliant">
                  📖 Tout savoir en détail
                </a>
              </div>

              {expressMode ? (
                <div className="bg-[#1E3A5F] text-white rounded-2xl p-3 shadow-lg text-sm leading-relaxed" data-testid="express-rappel">
                  ⚡ Mode express : les explications sont masquées. Rappel essentiel : <strong>règlement ou rendez-vous dans les 24 h</strong> (depuis votre Espace Adhérent), sinon l'inscription est <strong className="text-red-300">supprimée</strong> · <strong>1 formulaire = 1 adresse</strong>.
                </div>
              ) : (<>
              {/* MODE D'EMPLOI */}
              <div className="bg-[#1E3A5F] text-white rounded-2xl p-5 shadow-lg" data-testid="mode-emploi">
                <h2 className="text-base font-bold mb-3 flex items-center gap-2">
                  <svg className="w-6 h-6 text-[#5BC5D4] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>
                  Comment s'inscrire en 2 étapes
                </h2>
                <div className="space-y-2 text-sm leading-relaxed">
                  <div className="flex items-start gap-3">
                    <span className="bg-[#5BC5D4] text-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                    <span>{T('intro_etape1')}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="bg-[#5BC5D4] text-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                    <span className="whitespace-pre-line">{T('intro_etape2')}</span>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-white/10 text-xs text-white/90 leading-relaxed" data-testid="help-whatsapp">
                  <p>
                    Si vous avez des difficultés pour remplir ce formulaire,{' '}
                    <a href="https://wa.me/message/XTYPH2HUY3L6L1" target="_blank" rel="noopener noreferrer"
                      className="text-[#25D366] font-bold underline hover:text-[#1ebe57] inline-flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/></svg>
                      contactez-nous par WhatsApp
                    </a>
                    , nous vous aiderons à le compléter.
                  </p>
                </div>
              </div>

              {/* Iter 360 (Kamel — allègement) : bloc rouge « warning-24h » supprimé,
                  la règle + conséquence sont désormais dites UNE fois dans le mode d'emploi. */}

              <div className="bg-[#79c9ea] text-white rounded-2xl p-4 shadow-lg" data-testid="warning-adresse">
                <p className="text-sm leading-relaxed whitespace-pre-line">{T('foyer_note')}</p>
              </div>
              </>)}
            </div>

            <form onSubmit={handleSubmit} noValidate className="space-y-5">

              {/* Iter 825 — formulaire séparé (autre foyer, même voyage) : pas de brouillon → avertissement */}
              {formulaireSepare && !submitted && (
                <div className="bg-violet-50 border-2 border-violet-300 rounded-2xl p-3 shadow-sm text-xs text-violet-900" data-testid="separe-banner">
                  <p className="font-bold text-sm">🧾 Nouveau formulaire pour un autre foyer</p>
                  <p className="mt-0.5">Ce formulaire n'a pas de sauvegarde automatique. Terminez votre inscription avant de fermer ou de recharger la page.</p>
                </div>
              )}
              {/* Iter 769 — reprise du brouillon gardé sur cet appareil */}
              {brouillon && (
                <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-4 shadow-md" data-testid="brouillon-banner">
                  <p className="font-bold text-[#1E3A5F] text-sm">💾 Vous aviez commencé une inscription le {new Date(brouillon.savedAt).toLocaleDateString('fr-FR')} à {new Date(brouillon.savedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}{brouillon.form?.voyageurs?.[0]?.prenom ? ` (${brouillon.form.voyageurs[0].prenom}${brouillon.form.voyageurs[0].nom ? ' ' + brouillon.form.voyageurs[0].nom : ''})` : ''}.</p>
                  <p className="text-xs text-gray-600 mt-0.5">Elle est gardée uniquement sur cet appareil, 7 jours. Reprendre là où vous en étiez ?</p>
                  <div className="flex gap-2 mt-3">
                    <button type="button" onClick={reprendreBrouillon} className="flex-1 py-3 min-h-[48px] rounded-xl font-bold text-sm bg-[#1E3A5F] text-white" data-testid="brouillon-reprendre">✅ Reprendre mon inscription</button>
                    <button type="button" onClick={jeterBrouillon} className="px-4 py-3 min-h-[48px] rounded-xl font-bold text-sm border-2 border-gray-300 text-gray-700 bg-white" data-testid="brouillon-recommencer">Recommencer</button>
                  </div>
                </div>
              )}
              {brouillonRepris && <p className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="brouillon-repris">✅ Brouillon repris — vos réponses sont de nouveau là. Tout ce que vous saisissez reste enregistré sur cet appareil jusqu'à l'envoi.</p>}

              {/* Iter 767 — indicateur d'écrans (téléphone uniquement) */}
              {stepperOn && (
                <div className="bg-white rounded-2xl shadow-md p-3 scroll-mt-24" data-testid="etapes-indicateur">
                  <div className="flex items-center justify-between gap-1">
                    {ETAPES.map(([n, lab]) => (
                      <button type="button" key={n} onClick={() => n < etape && allerEtape(n)} disabled={n > etape}
                        className={`flex-1 flex flex-col items-center gap-1 ${n > etape ? 'opacity-40' : ''}`} data-testid={`etape-${n}`}>
                        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${n === etape ? 'bg-[#1E3A5F] text-white' : n < etape ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-600'}`}>{n < etape ? '✓' : n}</span>
                        <span className={`text-[10px] font-semibold ${n === etape ? 'text-[#1E3A5F]' : 'text-gray-500'}`}>{lab}</span>
                      </button>
                    ))}
                  </div>
                  <p className="text-center text-xs text-[#1E3A5F] font-bold mt-2" data-testid="etape-titre">Écran {etape} / 4 — {ETAPES[etape - 1][1]}</p>
                </div>
              )}

              {/* Iter 731 — Bandeau pré-remplissage depuis le simulateur */}
              {devisPrefill && (
                <div className="bg-[#FFF7CC] border-2 border-[#F5C400] rounded-2xl p-4" data-testid="devis-prefill-banner">
                  {devisPrefill.indisponible ? (
                  <p className="text-sm text-[#1E3A5F] leading-relaxed" data-testid="devis-prefill-indisponible">
                    🧮 <strong>Votre devis ({devisPrefill.sejour})</strong> : les inscriptions pour ces dates ne sont pas encore ouvertes en ligne. Gardez votre devis, ou choisissez ci-dessous un voyage déjà ouvert.
                  </p>
                  ) : (
                  <p className="text-sm text-[#1E3A5F] leading-relaxed">
                    🧮 <strong>Pré-rempli depuis votre devis</strong> ({devisPrefill.sejour}) : séjour, dates de naissance, chambres, trajets et point de départ sont reportés — il ne reste que les noms, prénoms et coordonnées à saisir. <strong>Les forfaits et le total s'affichent dès que les noms sont renseignés</strong> (le montant du devis sera retrouvé). Vérifiez chaque étape avant de valider.
                    {devisPrefill.semaines > 1 && <><br /><strong>Séjour de {devisPrefill.semaines} semaines :</strong> le nombre de semaines est préréglé ci-dessous ; une fiche par semaine sera créée automatiquement (car aller sur la 1re semaine, car retour sur la dernière, aucun car entre les deux).</>}
                  </p>
                  )}
                </div>
              )}

              {/* Iter 199 — Bandeau invitation privée */}
              {invitation && invitation.valid && (
                <div className="bg-emerald-50 border-2 border-emerald-400 rounded-2xl p-4" data-testid="invitation-banner">
                  <p className="text-sm text-emerald-900 leading-relaxed">
                    🎟️ <strong>Invitation privée</strong> — Le voyage <strong>{invitation.group}</strong> ({invitation.label}) est débloqué pour vous, même s'il est affiché complet.
                    Ce lien est valable jusqu'au <strong>{new Date(invitation.expires_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</strong>.
                  </p>
                </div>
              )}
              {invitation && invitation.valid === false && (
                <div className="bg-red-50 border-2 border-red-400 rounded-2xl p-4" data-testid="invitation-expired-banner">
                  <p className="text-sm text-red-900 leading-relaxed">
                    ⏰ <strong>Ce lien d'invitation a expiré</strong> (validité 24h). Merci de recontacter l'association pour recevoir un nouveau lien.
                  </p>
                </div>
              )}

              {/* Indicateur de progression supprimé (iter 87) — surchargeait le formulaire,
                  les validations bloquantes restent affichées dans la modale au moment de l'envoi. */}

              {/* DESTINATION — Sélecteur cascade (iter 69) : groupe → date */}
              <section className={`bg-white rounded-2xl shadow-md overflow-hidden${st(1)}`} data-testid="section-admin">
                <SectionHeader title={T('titre_destination')} />
                {/* Iter 231 (Kamel) — Confirmation visuelle du lien d'invitation valide (l'expiré a déjà son bandeau ⏰). */}
                {invitation && invitation.valid && (
                  <div className="mx-5 mt-4 bg-teal-50 border-2 border-teal-200 rounded-xl p-3 text-sm text-teal-800" data-testid="invitation-valide-banner">
                    🔗 <strong>Invitation privée</strong> : le voyage <strong>{invitation.group} ({invitation.label})</strong> a été pré-sélectionné pour vous ci-dessous.
                  </div>
                )}
                {/* Iter 231 (Kamel, bug mobile) — Catalogue non chargé : message + bouton réessayer (avant : menu vide silencieux). */}
                {destinationsLoaded && DESTINATIONS.length === 0 && (
                  <div className="mx-5 mt-4 bg-red-50 border-2 border-red-200 rounded-xl p-3 text-sm text-red-800 text-center" data-testid="destinations-erreur-banner">
                    <p>⚠️ <strong>Impossible de charger la liste des voyages.</strong><br />Vérifiez votre connexion internet puis réessayez.</p>
                    <button type="button" onClick={reloadDestinations} data-testid="destinations-retry-btn"
                      className="mt-2 px-4 py-2 bg-red-600 text-white text-xs font-bold rounded-lg hover:bg-red-700">
                      🔄 Réessayer
                    </button>
                  </div>
                )}
                {!destinationsLoaded && (
                  <p className="mx-5 mt-4 text-sm text-gray-500 animate-pulse" data-testid="destinations-loading">⏳ Chargement des voyages…</p>
                )}
                <div className="p-5 space-y-3" data-testid="select-destination">
                  {(() => {
                    // Construire la liste des groupes visibles
                    const visibleDests = DESTINATIONS.filter(d => isDestinationVisible(d));
                    const groups = [...new Set(visibleDests.map(d => d.group))];
                    // Dests du groupe sélectionné — affichées directement (iter 152ar :
                    // Kamel a explicitement demandé de retirer le sous-menu période
                    // qui n'a plus de sens depuis que Lloret est splitté en 2 groupes
                    // "4 JOURS" (mai) et "ÉTÉ 8 JOURS" (juillet-septembre)).
                    const dGroup = selectedGroup ? visibleDests.filter(d => d.group === selectedGroup) : [];
                    const finalList = dGroup;
                    // Détection du groupe "ÉTÉ 8 JOURS" pour afficher l'info "15 jours = 2 formulaires"
                    const isLloretEte = selectedGroup && (
                      selectedGroup.toLowerCase().includes('été 8 jours') ||
                      selectedGroup.toLowerCase().includes('ete 8 jours') ||
                      selectedGroup.toLowerCase().includes('8 jours')
                    );
                    const handleGroupChange = (g) => {
                      setSelectedGroup(g);
                      setSelectedPeriod('');
                      if (form.destination) changeDestination('');
                    };
                    const selectClass = "w-full border-2 border-gray-400 rounded-xl px-4 py-3 text-sm font-medium text-[#1E3A5F] bg-white focus:border-[#79c9ea] focus:ring-2 focus:ring-[#79c9ea] focus:ring-opacity-30 outline-none appearance-none cursor-pointer";
                    const caretStyle = { backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%231E3A5F' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center' };
                    return (
                      <div className="space-y-3">
                        {/* Étape 1 : groupe / zone */}
                        <div>
                          <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">1. Choisir la zone / destination</label>
                          <select value={selectedGroup} onChange={(e) => handleGroupChange(e.target.value)}
                            className={selectClass} style={caretStyle} data-testid="dropdown-destination-group">
                            <option value="">-- Choisissez une zone --</option>
                            {groups.map(g => (
                              <option key={g} value={g}>{g}</option>
                            ))}
                          </select>
                          {/* Iter 152ar (Kamel) — Info séjours 15 jours Espagne :
                              affichée directement sous le menu groupe quand "ÉTÉ 8 JOURS"
                              est sélectionné, sans menu période intermédiaire. */}
                          {isLloretEte && (
                            <div className="mt-2 p-3 bg-amber-50 border-2 border-amber-300 rounded-lg text-[12px] text-amber-900" data-testid="info-espagne-15-jours">
                              <strong>ℹ️ Vous partez 15 jours (2 semaines consécutives) ?</strong><br/>
                              Choisissez votre <strong>1re semaine</strong> ci-dessous, puis « <strong>2 semaines</strong> » : un seul formulaire suffit, nous enregistrons une inscription par semaine (l'hôtel loue à la semaine).<br/>
                              <span className="font-semibold">🚌 Si vous prenez le car : l'aller est sur votre 1re semaine et le retour sur la dernière — à condition qu'un car circule cette semaine-là.</span>
                            </div>
                          )}
                        </div>

                        {/* Étape finale : date précise */}
                        {(finalList.length > 0 || form.destination) && (
                          <div>
                            <label className="text-[10px] font-bold text-gray-500 uppercase block mb-1">
                              2. Choisir votre date + transport
                            </label>
                            <select value={form.destination} onChange={(e) => changeDestination(e.target.value)}
                              disabled={availStatus !== 'ok'}
                              className={selectClass} style={caretStyle} data-testid="dropdown-destination">
                              <option value="">{availStatus === 'ok' ? '-- Choisissez votre date --' : '-- Vérification des disponibilités… --'}</option>
                              {availStatus === 'ok' && finalList.map(d => {
                                const isComplet = completIds.has(d.id);
                                const spots = spotsMap[d.id];
                                const dispo = dispoMap[d.id];
                                // Iter 329/338 — 3 états publics ; Iter 613 (Kamel) — chiffre exact dès 5 places ou moins (« Plus que 1 place ! »)
                                const restantes = dispo ? dispo.restantes : spots;
                                const peu = restantes !== undefined && restantes !== -1 && restantes <= 10;
                                // Iter 765 (Kamel — smartphone) : libellé COURT dans le menu (date — prix + état),
                                // le détail hôtel / car aller / car retour s'affiche sous le menu une fois la date choisie.
                                const tj = dispo?.trajets;
                                const fus = d.transport_option && typeof d.transport_option === 'object';
                                const legPlein = (l) => l && l.restantes !== -1 && l.restantes <= 0;
                                const legPeu = (l) => l && l.restantes !== -1 && l.restantes > 0 && l.restantes <= 10;
                                const carPlein = tj && legPlein(tj.aller) && legPlein(tj.retour);
                                const carPeu = tj && (legPeu(tj.aller) || legPeu(tj.retour) || legPlein(tj.aller) || legPlein(tj.retour));
                                const hr = dispo?.hotel?.restantes;
                                const spotsText = isComplet ? ' · COMPLET'
                                  : (hr !== undefined && hr !== null && hr > 0 && hr <= 10) ? ` · plus que ${hr} ch.`
                                  : tj ? (carPlein ? ' · car complet' : carPeu ? ' · ⚠ peu de places car' : '')
                                  : peu ? (restantes > 0 ? ` · plus que ${restantes} pl.` : ' · < 10 pl.')
                                  : '';
                                const transportText = fus ? '' : d.transport ? ` · ${d.transport}` : '';
                                const optionText = `${d.label}${transportText} — ${d.price}${spotsText}`;
                                return (
                                  <option key={d.id} value={d.id} disabled={isComplet}>{optionText}</option>
                                );
                              })}
                            </select>
                            {availStatus === 'loading' && (
                              <p className="text-[11px] text-gray-500 italic mt-1" data-testid="avail-loading">⏳ Vérification des places disponibles…</p>
                            )}
                            {availStatus === 'error' && (
                              <p className="text-[11px] text-red-600 font-bold mt-1" data-testid="avail-error">
                                ⚠️ Impossible de vérifier les places disponibles (connexion instable).{' '}
                                <button type="button" onClick={() => { setAvailStatus('loading'); setAvailReload(k => k + 1); }} className="underline" data-testid="avail-retry">Réessayer</button>
                              </p>
                            )}
                            {finalList.length === 0 && !form.destination && (
                              <p className="text-[11px] text-gray-500 italic mt-1">Aucune date disponible pour cette sélection.</p>
                            )}
                            {/* Iter 587 (Kamel) — dès qu'une date est complète, les dates encore ouvertes sont listées
                                clairement sous le menu, cliquables (Turin, Algérie…). */}
                            {availStatus === 'ok' && finalList.some(d => completIds.has(d.id)) && (() => {
                              const ouvertes = finalList.filter(d => !completIds.has(d.id));
                              return ouvertes.length > 0 ? (
                                <div className="mt-2 bg-emerald-50 border-2 border-emerald-300 rounded-xl p-3" data-testid="dates-dispo-block">
                                  <p className="text-xs font-bold text-emerald-900">✅ Dates encore disponibles — cliquez pour choisir :</p>
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    {ouvertes.map(d => (
                                      <button type="button" key={d.id} onClick={() => changeDestination(d.id)}
                                        className={`text-xs px-3 py-2 rounded-lg border-2 font-semibold transition ${form.destination === d.id ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-emerald-400 text-emerald-900 hover:bg-emerald-100'}`}
                                        data-testid={`date-dispo-${d.id}`}>
                                        📅 {d.label}{d.transport ? ` · ${d.transport}` : ''} — {d.price}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <p className="mt-2 text-xs font-bold text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="dates-toutes-completes">
                                  🈵 Toutes les dates sont complètes pour le moment — inscrivez-vous en liste d'attente ci-dessous, nous vous prévenons dès qu'une place se libère.
                                </p>
                              );
                            })()}
                            {/* Iter 156f (Kamel — révoque le bandeau WhatsApp Iter 156d) :
                                pour Lloret été et tout autre voyage complet, la liste
                                d'attente se gère désormais depuis la fiche voyage
                                (tableau de disponibilité par semaine) — onglet admin
                                "Liste d'attente" interne. Plus de bandeau ici. */}
                            {/* Iter 263 (Kamel) — Liste d'attente PUBLIQUE : les dates complètes
                                sont proposées en liste d'attente directement depuis le formulaire. */}
                            {availStatus === 'ok' && finalList.some(d => completIds.has(d.id)) && (
                              <div className="mt-3 bg-amber-50 border-2 border-amber-300 rounded-xl p-3 space-y-2" data-testid="waitlist-public-block">
                                <p className="text-xs font-bold text-amber-900">🈵 Certaines dates sont complètes — inscrivez-vous en liste d'attente, nous vous prévenons dès qu'une place se libère :</p>
                                {finalList.filter(d => completIds.has(d.id)).map(d => (
                                  <div key={d.id}>
                                    {waitlistDest?.id === d.id ? (
                                      <WaitlistMini dest={d} onDone={() => setWaitlistDest(null)} />
                                    ) : (
                                      <button type="button" onClick={() => setWaitlistDest(d)}
                                        className="w-full text-left text-xs px-3 py-2 bg-white border border-amber-300 rounded-lg hover:bg-amber-100 transition font-semibold text-[#1E3A5F]"
                                        data-testid={`waitlist-open-${d.id}`}>
                                        ✋ {d.label}{d.transport ? ` · ${d.transport}` : ''} — <span className="text-red-600 font-bold">COMPLET</span> → rejoindre la liste d'attente
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Récapitulatif de la destination sélectionnée */}
                  {selectedDest && (
                    <div className="bg-sky-50 border-2 border-[#79c9ea] rounded-xl p-4" data-testid="dest-recap">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div>
                          <p className="font-bold text-[#1E3A5F] text-sm">{selectedDest.group}</p>
                          <p className="text-xs text-gray-600 mt-0.5">{selectedDest.detail}</p>
                          <p className="text-sm text-gray-800 mt-1">{selectedDest.label}{selectedDest.transport ? ` — ${selectedDest.transport}` : ''}</p>
                          {selectedDest.note && <p className="text-xs text-orange-600 font-medium mt-0.5">({selectedDest.note})</p>}
                        </div>
                        <div className="sm:text-right leading-tight border-t sm:border-t-0 border-sky-200 pt-2 sm:pt-0">
                          <div className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">{isFusion ? 'Séjour (hôtel, par pers.)' : 'Prix de base'}</div>
                          <span className="text-xl font-bold text-[#1E3A5F]">{selectedDest.price}</span>
                          {isFusion && transportActif && <div className="text-[11px] font-bold text-sky-700" data-testid="dest-recap-option-car">+ car en option : {prixTrajet} € par trajet</div>}
                          <div className="text-[10px] text-gray-400 italic">Réductions/suppléments non calculés ici</div>
                        </div>
                      </div>
                      {(() => {
                        // Iter 765 (Kamel — smartphone) : disponibilité détaillée en puces courtes (hôtel / car aller / car retour)
                        const dispo = dispoMap[selectedDest.id];
                        const tj = dispo?.trajets;
                        const etat = (r, unite = 'pl.') => (r === undefined || r === null || r === -1) ? { t: 'disponible', c: 'bg-emerald-100 text-emerald-800 border-emerald-300' }
                          : r <= 0 ? { t: 'COMPLET', c: 'bg-red-100 text-red-700 border-red-300' }
                          : r <= 10 ? { t: `plus que ${r} ${unite}`, c: 'bg-orange-100 text-orange-800 border-orange-300' }
                          : { t: 'disponible', c: 'bg-emerald-100 text-emerald-800 border-emerald-300' };
                        if (tj || dispo?.hotel) {
                          const puces = [];
                          if (dispo?.hotel) puces.push({ ico: '🏨', lab: 'Hôtel', ...etat(dispo.hotel.restantes, 'ch.') });
                          else if (isFusion) puces.push({ ico: '🏨', lab: 'Hôtel', ...etat(dispo?.sans_transport?.restantes ?? dispo?.restantes) });
                          if (tj && sejourIsole) {
                            const r = [tj.aller?.restantes, tj.retour?.restantes].filter(v => v !== undefined && v !== null && v !== -1);
                            puces.push({ ico: '🚌', lab: 'Car aller-retour', ...etat(r.length ? Math.min(...r) : -1) });
                          } else if (tj) {
                            puces.push({ ico: '🚌', lab: 'Car aller', ...etat(tj.aller?.restantes) });
                            puces.push({ ico: '🚌', lab: 'Car retour', ...etat(tj.retour?.restantes) });
                          }
                          return (
                            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="selected-dest-dispo">
                              {puces.map(p => (
                                <span key={p.lab} className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border ${p.c}`}>{p.ico} {p.lab} : {p.t}</span>
                              ))}
                            </div>
                          );
                        }
                        const spots = spotsMap[selectedDest.id];
                        if (spots !== undefined && spots !== -1) {
                          if (spots <= 0) return <p className="text-xs font-bold mt-2 text-orange-600" data-testid="selected-dest-dispo">Complet — Liste d'attente</p>;
                          if (spots <= 10) return <p className="text-xs font-bold mt-2 text-orange-600" data-testid="selected-dest-dispo">Plus que quelques places !</p>;
                          return <p className="text-xs font-bold mt-2 text-green-600" data-testid="selected-dest-dispo">Places disponibles</p>;
                        }
                        return null;
                      })()}
                      {isFusion && semainesSuite.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-sky-200" data-testid="nb-semaines-bloc">
                          <p className="text-xs font-bold text-[#1E3A5F] mb-1.5">📅 Combien de semaines ? <span className="font-normal text-gray-600">(semaines consécutives, à partir du {selectedDest.label})</span></p>
                          <div className="flex flex-wrap gap-2">
                            {[1, 2, 3].slice(0, 1 + semainesSuite.length).map(n => {
                              const ok = nbSemainesOk(n);
                              return (
                              <button type="button" key={n} onClick={() => ok && set('nbSemaines', n)} disabled={!ok} data-testid={`nb-semaines-${n}`}
                                title={ok ? '' : 'Pas de car retour sur cette dernière semaine — décochez « Car retour » pour choisir cette durée'}
                                className={`px-3 py-1.5 rounded-lg border-2 text-xs font-bold transition ${!ok ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed line-through' : nbSemainesEff === n ? 'bg-[#1E3A5F] text-white border-[#1E3A5F]' : 'bg-white text-[#1E3A5F] border-[#79c9ea] hover:bg-sky-100'}`}>
                                {n} semaine{n > 1 ? 's' : ''}{n > 1 ? ` · jusqu'au ${semainesSuite[n - 2].label.split('→').pop().trim()}` : ''}{!ok ? ' · pas de car retour' : ''}
                              </button>
                              );
                            })}
                          </div>
                          {retourCoche && [2, 3].slice(0, semainesSuite.length).some(n => !nbSemainesOk(n)) && (
                            <p className="text-[11px] text-gray-600 mt-1.5" data-testid="nb-semaines-filtre-info">ℹ️ Les durées barrées se terminent sur une semaine sans car retour : elles redeviennent possibles si vous décochez « Car retour » (retour par vos propres moyens).</p>
                          )}
                          {nbSemainesEff > 1 && (
                            <p className="text-[11px] text-emerald-800 mt-1.5 bg-emerald-50 border border-emerald-200 rounded px-2 py-1" data-testid="nb-semaines-info">
                              ✅ {nbSemainesEff} semaines : {selectedDest.label}{semainesSuite.slice(0, nbSemainesEff - 1).map(d => ` + ${d.label}`).join('')}. <strong>Un seul formulaire</strong> : nous enregistrons nous-mêmes une inscription par semaine (l'hôtel loue à la semaine), avec une seule chambre à préciser ci-dessous. Si vous prenez le car : aller sur la 1re semaine, retour sur la dernière.
                            </p>
                          )}
                          {nbSemainesEff > 1 && (
                            <div className="mt-1.5 space-y-1" data-testid="nb-semaines-dispo">
                              <p className="text-[10px] font-bold text-gray-500 uppercase">Disponibilités en direct, semaine par semaine</p>
                              {[selectedDest, ...semainesSuite.slice(0, nbSemainesEff - 1)].map((d, i) => {
                                const dp = dispoMap[d.id]; const last = i === nbSemainesEff - 1;
                                const carActif = !!(d.transport_option && typeof d.transport_option === 'object' && d.transport_option.actif);
                                const et = (r, u = 'pl.') => (r === undefined || r === null || r === -1) ? ['disponible', 'bg-emerald-100 text-emerald-800 border-emerald-300']
                                  : r <= 0 ? ['COMPLET', 'bg-red-100 text-red-700 border-red-300'] : r <= 10 ? [`plus que ${r} ${u}`, 'bg-orange-100 text-orange-800 border-orange-300'] : ['disponible', 'bg-emerald-100 text-emerald-800 border-emerald-300'];
                                const puces = [['🏨 Hôtel', ...et(dp?.hotel?.restantes ?? dp?.sans_transport?.restantes ?? dp?.restantes, 'ch.')]];
                                if (i === 0 && trajetsLegs.includes('aller')) puces.push(carActif ? ['🚌 Car aller', ...et(dp?.trajets?.aller?.restantes)] : ['🚌 Car aller', 'pas de car cette semaine', 'bg-gray-100 text-gray-500 border-gray-300']);
                                if (last && trajetsLegs.includes('retour')) puces.push(carActif ? ['🚌 Car retour', ...et(dp?.trajets?.retour?.restantes)] : ['🚌 Car retour', 'pas de car cette semaine', 'bg-gray-100 text-gray-500 border-gray-300']);
                                if (i > 0 && !last) puces.push(['🚌 Transport', 'aucun — vous restez sur place', 'bg-gray-100 text-gray-500 border-gray-300']);
                                return (
                                  <div key={d.id} className="flex flex-wrap items-center gap-1.5 text-[11px]" data-testid={`nb-semaines-dispo-${i + 1}`}>
                                    <span className="font-bold text-[#1E3A5F] w-full sm:w-auto">Semaine {i + 1} · {d.label} :</span>
                                    {puces.map(([lab, t, c]) => <span key={lab} className={`inline-flex items-center gap-1 font-semibold px-2 py-0.5 rounded-full border ${c}`}>{lab} : {t}</span>)}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {semainesSupp.some(w => w.retourIndispo) && (
                            <p className="text-[11px] text-red-800 mt-1.5 bg-red-50 border-2 border-red-300 rounded px-2 py-1.5 font-semibold" data-testid="nb-semaines-retour-impossible">
                              ⚠️ Retour en car impossible sur votre dernière semaine ({semainesSupp.find(w => w.retourIndispo).dest.label}) : {semainesSupp.find(w => w.retourIndispo).retourMotif}. Le retour se fera par vos propres moyens et <strong>aucun trajet retour ne vous sera facturé</strong>. Pour un retour en car, choisissez des semaines dont la dernière a un car.
                            </p>
                          )}
                        </div>
                      )}
                      <div className="mt-3 pt-3 border-t border-sky-200">
                        <p className="text-xs text-[#1E3A5F]">
                          <span className="font-bold">{T('avant_poursuivre_titre')}</span> {T('avant_poursuivre_texte')}{' '}
                          <Link to="/voyages" target="_blank" className="text-[#79c9ea] font-bold underline hover:text-[#5ba8c8]">
                            Voir la page du voyage
                          </Link>
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </section>

              {/* BARRE DE PROGRESSION RETIRÉE (2026-02) sur demande utilisateur : trop chargée, démotive.
                  Les points manquants sont désormais affichés dans une MODALE au clic sur "Envoyer", voir <ValidationModal /> plus bas. */}

              {/* Iter 691 (Kamel) — Espagne fusionnée : le TRANSPORT est une option du séjour (comme l'assurance).
                  Tout le monde se retrouve au même hôtel ; le car (aller / retour) s'ajoute au tarif hôtel, par trajet. */}
              {isFusion && (
                <section className={`bg-white rounded-2xl shadow-md overflow-hidden${st(1)}`} data-testid="section-transport-option">
                  <SectionHeader title={T('titre_transport_option')} />
                  <div className="p-5 space-y-3">
                    <p className="text-xs text-gray-700">
                      Le séjour à l'hôtel est le même pour tous : <strong>{selectedDest.price} par personne</strong>. Vous choisissez ensuite comment vous y rendre :
                      {transportActif ? (sejourIsole
                        ? <> par vos propres moyens, ou avec le <strong>car de l'association en option : {prixTrajet * 2} € aller-retour par personne</strong> (tarif unique, quel que soit l'âge). Avec le car, vous choisirez ensuite votre siège dans votre espace adhérent.</>
                        : <> par vos propres moyens, ou avec le <strong>car de l'association à {prixTrajet} € par personne et par trajet</strong> (tarif unique, quel que soit l'âge). Avec le car, vous choisirez ensuite votre siège pour chaque trajet dans votre espace adhérent.</>) : <> par vos propres moyens (pas de car de l'association sur cette semaine).</>}
                    </p>
                    <label className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition ${sansPlein ? 'opacity-50 bg-gray-100 cursor-not-allowed' : trajetsLegs.length === 0 ? 'border-[#1E3A5F] bg-sky-50' : 'border-gray-200 bg-white hover:border-[#79c9ea]'}`} data-testid="transport-sans">
                      <input type="radio" name="transport-option" checked={trajetsLegs.length === 0} disabled={sansPlein} onChange={() => setTrajets([])} className="mt-1 w-5 h-5 accent-[#1E3A5F]" data-testid="transport-sans-radio" />
                      <span className="text-sm">
                        <strong>🏨 Sans transport</strong> — je viens par mes propres moyens <span className="text-xs text-gray-500">(voiture, train, covoiturage…) · aucun supplément</span>
                        {sansPlein ? <span className="block text-xs font-bold text-red-600">COMPLET — plus de place « sans transport » cette semaine</span>
                          : sansRest !== undefined && sansRest !== -1 && sansRest <= 10 ? <span className="block text-xs font-bold text-orange-600">Plus que {sansRest} place{sansRest > 1 ? 's' : ''} sans transport !</span> : null}
                      </span>
                    </label>
                    {transportActif && (
                      <div className={`rounded-xl border-2 p-3 ${trajetsLegs.length ? 'border-[#1E3A5F] bg-sky-50' : 'border-gray-200'}`} data-testid="trajets-car-bloc">
                        <p className="text-sm font-bold text-[#1E3A5F] mb-1">{sejourIsole ? <>🚌 Car en option — {prixTrajet * 2} € aller-retour par personne</> : <>🚌 Avec le car de l'association — {prixTrajet} € par trajet et par personne</>}</p>
                        <p className="text-xs text-gray-600 mb-2" data-testid="trajets-car-texte">
                          {sejourIsole ? (
                            <>Pour ce séjour, le car se prend <strong>en aller + retour</strong> (pas de trajet seul). Si l'un des deux cars est complet, il reste la formule « Sans transport ».</>
                          ) : (
                            <>Cochez le ou les trajets dont vous avez besoin <strong>pour cette semaine</strong>. 1 semaine = Aller + Retour.{' '}
                            <strong>Plusieurs semaines</strong> : choisissez « 2 semaines » plus haut, l'aller est pris sur votre 1re semaine et le retour sur la dernière, nous nous occupons du reste.</>
                          )}
                        </p>
                        {sejourIsole ? (() => {
                          const plein = legPlein('aller') || legPlein('retour');
                          const coche = trajetsLegs.length === 2;
                          const rests = ['aller', 'retour'].map(legRest).filter(r => r !== undefined && r !== -1);
                          const rest = rests.length ? Math.min(...rests) : -1;
                          return (
                            <label className={`flex items-start gap-3 p-2 rounded-lg border mb-1 cursor-pointer ${plein ? 'opacity-50 bg-gray-100 cursor-not-allowed' : coche ? 'bg-white border-[#1E3A5F]' : 'bg-white'}`} data-testid="trajet-aller-retour">
                              <input type="checkbox" checked={coche && !plein} disabled={plein} data-testid="trajet-aller-retour-checkbox"
                                onChange={() => setTrajets(coche ? [] : ['aller', 'retour'])} className="mt-1 w-5 h-5 accent-[#1E3A5F]" />
                              <span className="text-sm">
                                <strong>🚌 CAR ALLER + RETOUR</strong> — +{prixTrajet * 2} €/pers. <span className="text-xs text-gray-500">(région lyonnaise → Lloret au départ, Lloret → région lyonnaise à la fin du séjour)</span>
                                {plein ? <span className="block text-xs font-bold text-red-600">COMPLET — plus de place dans le car (formule « Sans transport » possible)
                                  <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setWaitlistLeg(waitlistLeg ? null : (legPlein('aller') ? 'aller' : 'retour')); }} data-testid="waitlist-leg-aller-retour-btn"
                                    className="ml-2 inline-flex items-center gap-1 bg-amber-500 hover:bg-amber-600 text-white px-2 py-0.5 rounded-full text-[11px] font-bold">⏰ Liste d'attente car</button></span>
                                  : rest !== -1 && rest <= 10 ? <span className="block text-xs font-bold text-orange-600">Plus que {rest} place{rest > 1 ? 's' : ''} !</span> : null}
                              </span>
                            </label>
                          );
                        })() : [['aller', '🛫 CAR ALLER', 'région lyonnaise → Lloret, au départ de cette semaine'], ['retour', '🛬 CAR RETOUR', 'Lloret → région lyonnaise, à la fin de cette semaine']].map(([leg, lib, desc]) => {
                          const plein = legPlein(leg);
                          const coche = trajetsLegs.includes(leg);
                          const rest = legRest(leg);
                          return (
                            <label key={leg} className={`flex items-start gap-3 p-2 rounded-lg border mb-1 cursor-pointer ${plein ? 'opacity-50 bg-gray-100 cursor-not-allowed' : coche ? 'bg-white border-[#1E3A5F]' : 'bg-white'}`} data-testid={`trajet-${leg}`}>
                              <input type="checkbox" checked={coche && !plein} disabled={plein} data-testid={`trajet-${leg}-checkbox`}
                                onChange={() => setTrajets(coche ? trajetsLegs.filter(l => l !== leg) : [...trajetsLegs, leg].sort((a) => (a === 'aller' ? -1 : 1)))}
                                className="mt-1 w-5 h-5 accent-[#1E3A5F]" />
                              <span className="text-sm">
                                <strong>{lib}</strong> — +{prixTrajet} €/pers. <span className="text-xs text-gray-500">({desc})</span>
                                {plein ? <span className="block text-xs font-bold text-red-600">COMPLET — plus de place dans ce car (vous pouvez faire ce trajet par vos propres moyens)
                                  <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setWaitlistLeg(waitlistLeg === leg ? null : leg); }} data-testid={`waitlist-leg-${leg}-btn`}
                                    className="ml-2 inline-flex items-center gap-1 bg-amber-500 hover:bg-amber-600 text-white px-2 py-0.5 rounded-full text-[11px] font-bold">⏰ Liste d'attente {leg === 'aller' ? 'aller' : 'retour'}</button></span>
                                  : rest !== undefined && rest !== -1 && rest <= 10 ? <span className="block text-xs font-bold text-orange-600">Plus que {rest} place{rest > 1 ? 's' : ''} !</span> : null}
                              </span>
                            </label>
                          );
                        })}
                        {waitlistLeg && selectedDest && legPlein(waitlistLeg) && (
                          <div className="mt-2" data-testid={`waitlist-leg-${waitlistLeg}`}>
                            <WaitlistMini dest={{ ...selectedDest, transport: waitlistLeg === 'aller' ? 'Car aller' : 'Car retour' }} onDone={() => setWaitlistLeg(null)} />
                          </div>
                        )}
                        {trajetUniqueOubli && (
                          <div className={`mt-2 border-2 rounded-xl p-3 ${form.confirmeTrajetUnique ? 'bg-green-50 border-green-600' : 'bg-red-50 border-red-500'}`} data-testid="alerte-trajet-unique">
                            <p className="text-sm font-bold text-red-800">⚠️ ATTENTION : vous n'avez pas choisi de {trajetManquant === 'retour' ? 'RETOUR' : 'ALLER'} en car !</p>
                            <p className="text-xs text-gray-800 mt-1">Vous avez coché seulement le car <strong>{trajetsLegs[0] === 'aller' ? 'ALLER' : 'RETOUR'}</strong> pour une seule semaine. Si c'est un oubli, cliquez sur le bouton vert. Sinon, cochez la case pour confirmer.</p>
                            <button type="button" onClick={() => setTrajets(['aller', 'retour'])} className="mt-2 w-full sm:w-auto bg-green-700 hover:bg-green-800 text-white font-bold px-4 py-3 rounded-xl text-sm" data-testid="alerte-trajet-unique-ajouter">
                              ✅ Ajouter le car {trajetManquant === 'retour' ? 'RETOUR' : 'ALLER'} (+{prixTrajet} €/pers.)
                            </button>
                            <label className="flex items-start gap-2 mt-2 cursor-pointer">
                              <input type="checkbox" checked={!!form.confirmeTrajetUnique} onChange={(e) => set('confirmeTrajetUnique', e.target.checked)} className="w-5 h-5 mt-0.5 accent-green-700" data-testid="alerte-trajet-unique-confirme" />
                              <span className="text-xs font-semibold text-gray-900">Je confirme : je fais le {trajetManquant} <strong>par mes propres moyens</strong> (ce n'est pas un oubli).</span>
                            </label>
                          </div>
                        )}
                        <p className="text-xs font-semibold text-[#1E3A5F] mt-1" data-testid="transport-resume">
                          {trajetsLegs.length === 0 ? '→ Formule sans transport sélectionnée (aucun supplément).'
                            : trajetsLegs.length === 2 ? (sejourIsole ? `→ Car aller-retour : +${prixTrajet * 2} € par personne (1 siège à choisir par personne, pour l'aller et le retour).` : `→ Car aller + retour : +${prixTrajet * 2} € par personne (2 sièges à choisir : aller, retour).`)
                            : sejourIsole ? '→ ⚠️ Pour ce séjour, le car se prend en aller + retour : cochez la case ci-dessus ou choisissez « Sans transport ».'
                            : `→ Car ${trajetsLegs[0] === 'aller' ? 'aller' : 'retour'} seulement : +${prixTrajet} € par personne (${sejourIsole ? `le ${trajetsLegs[0] === 'aller' ? 'retour' : 'aller'} se fera par vos propres moyens` : trajetsLegs[0] === 'aller' ? 'cette semaine est votre aller ; le retour se choisit sur votre 2e semaine ou par vos propres moyens' : 'cette semaine est votre retour ; l\'aller a été pris sur votre 1re semaine ou par vos propres moyens'}).`}
                        </p>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {/* CHOISIR VOTRE LIEU DE DÉPART – VOYAGES EN CAR */}
              {showBusDepart && (
                <section className={`bg-white rounded-2xl shadow-md overflow-hidden${st(1)}`} data-testid="section-depart">
                  <SectionHeader title={T('titre_depart')} />
                  <div className="p-5 space-y-2">
                    <Radio name="depart" value="st-etienne" label={T('depart_st_etienne')} current={form.lieuDepart} onClick={() => { set('lieuDepart', 'st-etienne'); set('aireAutoroute', ''); }} />
                    <Radio name="depart" value="st-priest" label={T('depart_st_priest')} current={form.lieuDepart} onClick={() => { set('lieuDepart', 'st-priest'); set('aireAutoroute', ''); }} />
                    <Radio name="depart" value="villeurbanne" label={T('depart_villeurbanne')} current={form.lieuDepart} onClick={() => { set('lieuDepart', 'villeurbanne'); set('aireAutoroute', ''); }} />
                    <>
                        <Radio name="depart" value="autoroute" label={`SUR UNE AIRE D'AUTOROUTE DE NOTRE TRAJET (${autoroutesOk.join(' / ')}) — HORAIRE PRÉCISÉ DANS VOTRE CONVOCATION`} current={form.lieuDepart} onClick={() => set('lieuDepart', 'autoroute')} />
                        {form.lieuDepart === 'autoroute' && (
                          <div className="ml-8 space-y-2">
                            <select value={form.aireAutorouteCode} onChange={(e) => { setAireAutre(false); setForm(f => ({ ...f, aireAutorouteCode: e.target.value, aireAutoroute: '', aireSens: '' })); }} data-testid="select-aire-autoroute"
                              className="w-full border border-gray-400 rounded-lg px-4 py-3 text-sm bg-white focus:border-[#79c9ea] outline-none">
                              <option value="">— Autoroute de l'aire (obligatoire) —</option>
                              {autoroutesOk.map(c => <option key={c} value={c}>{autoroutesLib[c] || c}</option>)}
                            </select>
                            {form.aireAutorouteCode && airesProposees.length > 0 && (
                              <select value={aireAutre ? '__autre__' : (aireDansListe ? form.aireAutoroute : '')} onChange={(e) => choisirAire(e.target.value)} data-testid="select-aire-nom"
                                className="w-full border border-gray-400 rounded-lg px-4 py-3 text-sm bg-white focus:border-[#79c9ea] outline-none">
                                <option value="">— Choisir l'aire ({autoroutesInfo[form.aireAutorouteCode]?.sensAller || 'sens aller'}) —</option>
                                {airesProposees.map(a => <option key={a.nom} value={a.nom}>{a.nom}</option>)}
                                <option value="__autre__">Autre aire (à préciser — sera vérifiée par l'association)</option>
                              </select>
                            )}
                            {(aireAutre || !form.aireAutorouteCode || airesProposees.length === 0) && (
                              <input type="text" value={form.aireAutoroute} onChange={(e) => set('aireAutoroute', e.target.value)} placeholder="Nom de l'aire d'autoroute (ex : Aire de Montélimar)" data-testid="input-aire-autoroute"
                                className="w-full border border-gray-400 rounded-lg px-4 py-3 text-sm focus:border-[#79c9ea] focus:ring-1 focus:ring-[#79c9ea] outline-none" />
                            )}
                            {aireDansListe && !aireAutre && (
                              <div className="text-[11px] text-emerald-700 font-bold space-y-0.5" data-testid="aire-validee-info">
                                <p>✅ Aire déjà validée par l'association — sens {form.aireSens}</p>
                                {airesProposees.find(a => a.nom === form.aireAutoroute)?.rdv
                                  ? <p className="text-gray-800 font-normal" data-testid="aire-rdv-approx">🕒 Rendez-vous approximatif sur l'aire <strong>vers {airesProposees.find(a => a.nom === form.aireAutoroute).rdv.approx}</strong> — soyez sur place à <strong>{airesProposees.find(a => a.nom === form.aireAutoroute).rdv.sur_place}</strong> : c'est vous qui attendez le car, pas l'inverse. L'horaire sera précisé dans votre convocation.</p>
                                  : <p className="text-gray-600 font-normal" data-testid="aire-rdv-inconnu">🕒 L'horaire de rendez-vous sur l'aire vous sera communiqué dans votre convocation (c'est vous qui attendez le car).</p>}
                              </div>
                            )}
                            <div className="text-[11px] text-gray-700 bg-amber-50 border border-amber-200 rounded-lg p-2 space-y-1" data-testid="aire-autoroute-info">
                              <p className="font-bold text-amber-900">Ramassage sur une aire d'autoroute</p>
                              <p>Vous pouvez demander une prise en charge sur une aire d'autoroute en indiquant son nom et l'autoroute concernée. Cette possibilité est limitée aux aires situées <strong>sur le trajet de notre autocar ({autoroutesOk.join(', ')}) et dans le bon sens de circulation</strong>. Aucun détour n'est prévu.</p>
                              <p>Si votre aire n'est pas encore confirmée, l'association vérifiera votre demande. Le lieu exact et l'horaire seront précisés dans votre convocation.</p>
                              <p>Au retour, la dépose peut se faire sur une autre aire, dans le sens du retour. <strong>Ne traversez jamais l'autoroute à pied.</strong></p>
                            </div>
                          </div>
                        )}
                      </>
                    {isLloretEteCar && (
                      <div className="mt-3 border-2 border-[#79c9ea] rounded-lg p-3 bg-sky-50" data-testid="trajets-car-bloc">
                        <p className="text-sm font-bold text-[#1E3A5F] mb-1">🚌 Vos trajets en car — {prixTrajet} € par trajet et par personne (tarif unique, quel que soit l'âge)</p>
                        <p className="text-xs text-gray-600 mb-2" data-testid="trajets-car-texte">{sejourIsole
                          ? <>Trajets pour ce séjour : en général <strong>Aller + Retour</strong>. Un trajet complet est grisé : vous pouvez alors ne prendre que l'autre (le reste par vos propres moyens).</>
                          : <>1 semaine = Aller + Retour. <strong>2 semaines consécutives</strong> (une inscription par semaine) : cochez seulement l'<strong>Aller</strong> sur votre 1re semaine et seulement le <strong>Retour</strong> sur votre 2e. Un trajet complet est grisé : vous pouvez alors ne prendre que l'autre trajet (l'autre par vos propres moyens).</>}</p>
                        {[['aller', '🛫 ALLER', 'départ de la région lyonnaise vers Lloret'], ['retour', '🛬 RETOUR', 'Lloret vers la région lyonnaise']].map(([leg, lib, desc]) => {
                          const plein = legPlein(leg);
                          const coche = trajetsLegs.includes(leg);
                          const rest = dispoSel?.trajets?.[leg]?.restantes;
                          return (
                            <label key={leg} className={`flex items-start gap-3 p-2 rounded-lg border mb-1 cursor-pointer ${plein ? 'opacity-50 bg-gray-100 cursor-not-allowed' : coche ? 'bg-white border-[#1E3A5F]' : 'bg-white'}`} data-testid={`trajet-${leg}`}>
                              <input type="checkbox" checked={coche && !plein} disabled={plein} data-testid={`trajet-${leg}-checkbox`}
                                onChange={() => setTrajets(coche ? trajetsLegs.filter(l => l !== leg) : [...trajetsLegs, leg].sort((a) => (a === 'aller' ? -1 : 1)))}
                                className="mt-1 w-5 h-5 accent-[#1E3A5F]" />
                              <span className="text-sm">
                                <strong>{lib}</strong> — {prixTrajet} €/pers. <span className="text-xs text-gray-500">({desc})</span>
                                {plein ? <span className="block text-xs font-bold text-red-600">COMPLET — plus de place dans ce car
                                  <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setWaitlistLeg(waitlistLeg === leg ? null : leg); }} data-testid={`waitlist-leg2-${leg}-btn`}
                                    className="ml-2 inline-flex items-center gap-1 bg-amber-500 hover:bg-amber-600 text-white px-2 py-0.5 rounded-full text-[11px] font-bold">⏰ Liste d'attente</button></span>
                                  : rest !== undefined && rest <= 10 ? <span className="block text-xs font-bold text-orange-600">Plus que {rest} place{rest > 1 ? 's' : ''} !</span> : null}
                              </span>
                            </label>
                          );
                        })}
                        {trajetsLegs.length === 0 && <p className="text-xs font-bold text-red-600 mt-1" data-testid="trajets-aucun">Choisissez au moins un trajet, ou prenez la formule « Sans transport » de cette semaine dans la liste des dates.</p>}
                        {waitlistLeg && selectedDest && legPlein(waitlistLeg) && (
                          <div className="mt-2" data-testid={`waitlist-leg-${waitlistLeg}`}>
                            <WaitlistMini dest={{ ...selectedDest, transport: waitlistLeg === 'aller' ? 'Car aller' : 'Car retour' }} onDone={() => setWaitlistLeg(null)} />
                          </div>
                        )}
                        {trajetsLegs.length === 1 && <p className="text-xs text-emerald-700 font-semibold mt-1" data-testid="trajets-remise">✅ Un seul trajet : −{prixTrajet} € par personne sur le tarif « avec transport » ({trajetsLegs[0] === 'aller' ? 'cette semaine est votre aller' : 'cette semaine est votre retour'}).</p>}
                      </div>
                    )}
                    <Radio name="depart" value="non-concerne" label="NON CONCERNÉ" current={form.lieuDepart} onClick={() => { set('lieuDepart', 'non-concerne'); set('aireAutoroute', ''); }} />
                  </div>
                </section>
              )}

              {/* Iter 691 (Kamel) — EXCURSIONS pré-réservées à l'inscription (comme en 2026) : pour tous les voyageurs de la fiche, réglées en espèces sur place */}
              {excursionsProg && hasNuitee && (
                <section className={`bg-white rounded-2xl shadow-md overflow-hidden${st(1)}`} data-testid="section-excursions-pre">
                  <SectionHeader title="EXCURSIONS SUR PLACE — RÉSERVEZ DÈS MAINTENANT (EN OPTION)" />
                  <div className="p-5 space-y-2">
                    <p className="text-xs text-gray-700">
                      {excursionsProg.intro || 'Les excursions sont proposées à tous les participants de la semaine, que vous veniez en car ou par vos propres moyens.'}{' '}
                      <strong>Réglées en espèces sur place</strong> auprès de l'accompagnateur — rien n'est à payer aujourd'hui. Vous pourrez modifier vos choix plus tard depuis votre espace adhérent.
                    </p>
                    {excursionsProg.excursions.map(ex => {
                      const coche = (form.excursionsPre || []).includes(ex.id);
                      const prix = ex.gratuite ? '🎁 incluse dans le prix du voyage' : `${ex.tarif_plein} €/pers.${ex.tarif_reduit != null && ex.tarif_reduit !== ex.tarif_plein ? ` · ${ex.tarif_reduit} € ${ex.condition_reduit || 'tarif réduit'}` : ''}`;
                      return (
                        <label key={ex.id} className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition ${coche ? 'border-[#1E3A5F] bg-sky-50' : 'border-gray-200 bg-white hover:border-[#79c9ea]'}`} data-testid={`excursion-pre-${ex.id}`}>
                          <input type="checkbox" checked={coche} data-testid={`excursion-pre-${ex.id}-checkbox`}
                            onChange={() => setForm(p => ({ ...p, excursionsPre: coche ? (p.excursionsPre || []).filter(x => x !== ex.id) : [...(p.excursionsPre || []), ex.id] }))}
                            className="mt-1 w-5 h-5 accent-[#1E3A5F]" />
                          <span className="text-sm">
                            <strong>{ex.nom}</strong>{ex.jour ? <span className="text-xs text-gray-600"> — {ex.jour}{ex.heure_depart ? ` · départ ${ex.heure_depart}` : ''}{ex.heure_retour ? ` → retour ${ex.heure_retour}` : ''}</span> : null}
                            <span className="block text-xs font-semibold text-[#1E3A5F]">{prix}</span>
                            {ex.description && <span className="block text-[11px] text-gray-500 mt-0.5">{ex.description}</span>}
                          </span>
                        </label>
                      );
                    })}
                    {(form.excursionsPre || []).length > 0 && (
                      <p className="text-xs font-semibold text-emerald-800" data-testid="excursions-pre-resume">✅ {form.excursionsPre.length} excursion{form.excursionsPre.length > 1 ? 's' : ''} pré-réservée{form.excursionsPre.length > 1 ? 's' : ''} pour tous les voyageurs de cette inscription (à régler en espèces sur place).</p>
                    )}
                  </div>
                </section>
              )}

              {/* RAPPEL FORMALITÉS — AVANT VOYAGEURS, uniquement pour les voyages en avion.
                  Iter 113 — Règle stricte Kamel : avion = passeport SEULEMENT, valide 6 mois après retour. */}
              {isAvion && selectedDest?.cniAcceptee && (
                <section className={`bg-white rounded-2xl shadow-md overflow-hidden${st(1)}`} data-testid="section-formalites-top-cni">
                  <SectionHeader title="AVANT DE VOUS INSCRIRE — VÉRIFIEZ VOTRE PIÈCE D'IDENTITÉ" color="bg-sky-700" />
                  <div className="p-5 space-y-3">
                    <div className="bg-sky-50 border-2 border-sky-300 rounded-lg p-4 text-xs text-sky-900 leading-relaxed space-y-2">
                      <p className="font-bold text-sky-900 text-sm">🪪 Voyage en avion en Europe : la carte nationale d'identité OU le passeport, en cours de validité, suffit.</p>
                      <ul className="space-y-1.5 ml-1">
                        <li className="flex items-start gap-2"><span className="font-bold text-sky-700 flex-shrink-0">&#8226;</span><span>La pièce doit être <strong>valide jusqu'à la date de retour</strong> (nom identique à celui de la réservation).</span></li>
                        <li className="flex items-start gap-2"><span className="font-bold text-sky-700 flex-shrink-0">&#8226;</span><span>Carte de séjour ou nationalité hors Union Européenne : <strong>contactez-nous avant de vous inscrire</strong>, un passeport (voire un visa) peut être exigé.</span></li>
                        <li className="flex items-start gap-2"><span className="font-bold text-sky-700 flex-shrink-0">&#8226;</span><span>Bonne nouvelle : <strong>inutile d'avoir la pièce sous la main aujourd'hui</strong> — numéro et dates vous seront demandés <strong>après votre inscription</strong>, via un lien personnel sécurisé.</span></li>
                      </ul>
                    </div>
                  </div>
                </section>
              )}
              {isAvion && !selectedDest?.cniAcceptee && (
                <section className={`bg-white rounded-2xl shadow-md overflow-hidden${st(1)}`} data-testid="section-formalites-top">
                  <SectionHeader title="AVANT DE VOUS INSCRIRE — VÉRIFIEZ VOTRE PASSEPORT" color="bg-red-600" />
                  <div className="p-5 space-y-3">
                    <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4 text-xs text-red-900 leading-relaxed space-y-2">
                      <p className="font-bold text-red-900 text-sm">🛂 Pour tous les voyages en avion, seul le passeport est accepté. Sans passeport conforme, l'inscription ne peut pas être honorée.</p>
                      <ul className="space-y-1.5 ml-1">
                        <li className="flex items-start gap-2"><span className="font-bold text-red-700 flex-shrink-0">&#8226;</span><span><strong>Passeport en cours de validité OBLIGATOIRE</strong> — la carte nationale d'identité (CNI) n'est pas acceptée pour les vols.</span></li>
                        <li className="flex items-start gap-2"><span className="font-bold text-red-700 flex-shrink-0">&#8226;</span><span>Le passeport doit rester valide <strong>au minimum 6 mois après la date de retour</strong>.</span></li>
                        <li className="flex items-start gap-2"><span className="font-bold text-red-700 flex-shrink-0">&#8226;</span><span>Bonne nouvelle : <strong>inutile d'avoir votre passeport sous la main aujourd'hui</strong> — les informations détaillées (numéro, dates) vous seront demandées <strong>après votre inscription</strong>, via un lien personnel sécurisé.</span></li>
                        <li className="flex items-start gap-2"><span className="font-bold text-red-700 flex-shrink-0">&#8226;</span><span>Certaines destinations nécessitent un <strong>visa</strong> ou une <strong>autorisation électronique</strong> (ex. ESTA pour les USA).</span></li>
                      </ul>
                      <p className="text-red-800 font-medium mt-1">Consultez la page du voyage pour connaître les exigences précises. En cas de doute, <strong>contactez-nous par WhatsApp avant de remplir ce formulaire</strong>, nous sommes là pour vous accompagner.</p>
                    </div>
                  </div>
                </section>
              )}

              {/* VOYAGEURS — ancien formulaire, masqué pour Lloret */}
              {!isLloretTrip && (
              <section className={`bg-white rounded-2xl shadow-md overflow-hidden${st(2)}`} data-testid="section-voyageurs">
                <SectionHeader title={T('titre_voyageurs')} />
                <div className="bg-[#1E3A5F] text-white rounded-xl p-3 mb-3" data-testid="regle-un-formulaire">
                  {hasNuitee ? (<>
                  <p className="text-sm font-bold">🧾 Cette année : 1 formulaire = 1 facture = 1 adresse postale</p>
                  <p className="text-xs text-sky-100 mt-1 leading-relaxed">Chaque foyer remplit SON formulaire. Vous voyagez avec des amis d'un autre foyer ? Chacun remplit le sien — vous pourrez quand même <strong>partager la même chambre</strong> : il suffira d'indiquer leur nom dans la chambre. <TutoLienChoix etape={1} label="📖 Voir le tuto en 3 étapes" className="!text-sky-100" /></p>
                  </>) : (<>
                  <p className="text-sm font-bold">🧾 Un formulaire = un foyer = une facture.</p>
                  <p className="text-xs text-sky-100 mt-1 leading-relaxed" data-testid="regle-journee">Adresses différentes ? Chacun remplit son formulaire.</p>
                  </>)}
                </div>
                <div className="p-5 space-y-5">
                  {/* Iter 74quater : warning "aucun majeur" retiré de cette position (jugé prématuré).
                      La validation bloquante au submit (ligne ~1228) affiche un message clair si besoin. */}
                  {form.voyageurs.map((v, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-xl p-4" data-testid={`voyageur-${idx}`}>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-bold text-[#1E3A5F] text-sm">
                          {idx === 0 ? '1ER' : idx === 1 ? '2EME' : idx === 2 ? '3EME' : idx === 3 ? '4E' : idx === 4 ? '5E' : '6E'} VOYAGEUR {idx === 0 && <span className="text-red-500">*</span>}
                        </h3>
                        {idx > 0 && form.voyageurs.length > 1 && (
                          <button type="button" onClick={() => removeVoyageur(idx)} className="text-red-400 hover:text-red-600 text-xs font-semibold">Supprimer</button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-semibold text-gray-500 block mb-1">NOM <span className="text-red-500">*</span></label>
                          <input type="text" value={v.nom} onChange={(e) => setVoyageur(idx, 'nom', e.target.value.toUpperCase())} placeholder="NOM" data-testid={`voyageur-${idx}-nom`}
                            className="w-full min-w-0 border-2 border-gray-400 rounded-lg px-3 py-2.5 text-sm font-bold focus:border-[#79c9ea] focus:ring-1 focus:ring-[#79c9ea] outline-none uppercase" required />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-500 block mb-1">PRÉNOM <span className="text-red-500">*</span></label>
                          <input type="text" value={v.prenom} onChange={(e) => setVoyageur(idx, 'prenom', e.target.value.toUpperCase())} placeholder="PRÉNOM" data-testid={`voyageur-${idx}-prenom`}
                            className="w-full min-w-0 border-2 border-gray-400 rounded-lg px-3 py-2.5 text-sm font-bold focus:border-[#79c9ea] focus:ring-1 focus:ring-[#79c9ea] outline-none uppercase" required />
                        </div>
                        {/* SEXE (placé après nom/prénom) */}
                        <div className="md:col-span-2">
                          <label className="text-xs font-semibold text-gray-500 block mb-1">SEXE <span className="text-red-500">*</span></label>
                          <div className="flex gap-3">
                            {[['M', 'Masculin'], ['F', 'Féminin']].map(([val, label]) => (
                              <button key={val} type="button" onClick={() => setVoyageur(idx, 'civilite', val)}
                                className={`px-4 py-2 rounded-lg border-2 text-sm font-bold transition ${v.civilite === val ? 'bg-[#1E3A5F] text-white border-[#1E3A5F]' : 'border-gray-400 text-gray-500 hover:border-[#79c9ea]'}`}
                                data-testid={`voyageur-${idx}-civilite-${val}`}>
                                {label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-500 block mb-1">DATE DE NAISSANCE (JJ/MM/AAAA) <span className="text-red-500">*</span></label>
                          <input type="text" inputMode="numeric" value={v.dateNaissance} onChange={(e) => handleDateInput(idx, e.target.value)} placeholder="JJ/MM/AAAA" maxLength={10} data-testid={`voyageur-${idx}-ddn`}
                            pattern="\d{2}/\d{2}/\d{4}" title="Format JJ/MM/AAAA, année sur 4 chiffres (ex. 15/03/1985)"
                            className={`w-full border-2 rounded-lg px-3 py-2.5 text-sm focus:border-[#79c9ea] focus:ring-1 focus:ring-[#79c9ea] outline-none ${/^\d{2}\/\d{2}\/\d{2}$/.test(v.dateNaissance || '') ? 'border-red-500 bg-red-50' : 'border-gray-400'}`} required />
                          {/* Iter 918 (Kamel) — année sur 2 chiffres : alerte immédiate (sinon « 85 » devient l'an 85 et tout le monde paraît mineur) */}
                          {/^\d{2}\/\d{2}\/\d{2}$/.test(v.dateNaissance || '') && (
                            <p className="text-xs font-bold text-red-700 mt-1" data-testid={`voyageur-${idx}-ddn-annee4`}>⚠️ Année sur 4 chiffres obligatoire : {v.dateNaissance.slice(0, 6)}<strong>19{v.dateNaissance.slice(6)}</strong> ou {v.dateNaissance.slice(0, 6)}<strong>20{v.dateNaissance.slice(6)}</strong> ?</p>
                          )}
                          {ageLisible(v.dateNaissance) && (
                            <p className="text-[11px] text-[#1E3A5F] font-semibold mt-1" data-testid={`voyageur-${idx}-age`}>
                              Âge au départ du voyage : <span className="font-bold">{ageLisible(v.dateNaissance)}</span>
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-500 block mb-1">TARIF POUR CE VOYAGEUR (€) <span className="text-red-500">*</span></label>
                          {(() => {
                            const tarifNum = parseFloat(v.forfait) || 0;
                            const nomOK = v.nom?.trim();
                            const invalid = nomOK && tarifNum <= 0;
                            // Lloret : tarif auto-calculé selon DDN et composition chambre → input read-only + détail
                            const lloretComputed = isLloretTrip ? computeLloretForfaits()[idx] : null;
                            if (lloretComputed) {
                              return (
                                <>
                                  <input type="number" value={v.forfait || ''} readOnly
                                    data-testid={`voyageur-${idx}-forfait`}
                                    className="w-full border-2 border-sky-300 bg-sky-50 rounded-lg px-3 py-2.5 text-sm font-bold text-[#1E3A5F] outline-none cursor-not-allowed" />
                                  <div className="mt-1 flex items-start gap-1 text-xs text-[#1E3A5F]">
                                    <span className="text-sky-600 font-bold flex-shrink-0" title="Tarif calculé automatiquement">ℹ️</span>
                                    <span className="leading-snug" data-testid={`voyageur-${idx}-lloret-detail`}>
                                      <strong>{lloretComputed.detail}</strong>
                                      {lloretComputed.category === 'child' && <span className="text-green-700 font-semibold"> (réduction enfant appliquée)</span>}
                                      {lloretComputed.category === 'baby' && <span className="text-green-700 font-semibold"> (réduction bébé appliquée)</span>}
                                    </span>
                                  </div>
                                </>
                              );
                            }
                            // Iter 93 — voyages à la journée (Turin, Grau-du-Roi…) : tarif
                            // calculé automatiquement selon la date de naissance
                            // (-12 ans = -5 €). Champ verrouillé en lecture seule pour
                            // garantir l'intégrité du tarif (le voyageur ne peut pas
                            // baisser le montant accidentellement ou volontairement).
                            const baseGlobal = parseInt((selectedDest?.price || '').replace(/[^\d]/g, ''), 10) || 0;
                            const journeeAutoCalc = !hasNuitee && baseGlobal > 0;
                            if (journeeAutoCalc) {
                              const adminCfg = chambreIndOverrides[selectedDest?.id] || {};
                              const childDisc = (adminCfg.reduction_3_11 !== null && adminCfg.reduction_3_11 !== undefined)
                                ? parseFloat(adminCfg.reduction_3_11) : 0;
                              const age = ageAuDepartNum(v.dateNaissance);
                              const isChild = age !== null && age < 12;
                              return (
                                <>
                                  <input type="number" value={v.forfait || ''} readOnly
                                    data-testid={`voyageur-${idx}-forfait`}
                                    className="w-full border-2 border-sky-300 bg-sky-50 rounded-lg px-3 py-2.5 text-sm font-bold text-[#1E3A5F] outline-none cursor-not-allowed" />
                                  <div className="mt-1 flex items-start gap-1 text-xs text-[#1E3A5F]">
                                    <span className="text-sky-600 font-bold flex-shrink-0" title="Tarif calculé automatiquement">ℹ️</span>
                                    <span className="leading-snug" data-testid={`voyageur-${idx}-journee-detail`}>
                                      <strong>{isChild ? `Tarif réduit (moins de 12 ans) : ${baseGlobal - childDisc} €` : `Plein tarif : ${baseGlobal} €`}</strong>
                                      {isChild && childDisc > 0 && <span className="text-green-700 font-semibold"> (−{childDisc} € appliqué automatiquement)</span>}
                                      {age === null && v.dateNaissance && <span className="text-orange-600 italic"> Date de naissance invalide</span>}
                                      {!v.dateNaissance && <span className="text-orange-600 italic"> Saisissez la date de naissance pour calculer le tarif</span>}
                                    </span>
                                  </div>
                                </>
                              );
                            }
                            return (
                              <>
                                <input type="number" value={v.forfait || ''} onChange={(e) => setVoyageur(idx, 'forfait', e.target.value)}
                                  min="1" required
                                  data-testid={`voyageur-${idx}-forfait`}
                                  className={`w-full border-2 rounded-lg px-3 py-2.5 text-sm font-bold text-[#1E3A5F] outline-none ${invalid ? 'border-red-400 bg-red-50 focus:border-red-500 focus:ring-1 focus:ring-red-400' : 'border-gray-400 focus:border-[#79c9ea] focus:ring-1 focus:ring-[#79c9ea]'}`} />
                                {invalid ? (
                                  <p className="text-xs text-red-600 mt-1 font-semibold">Le tarif est obligatoire et doit être supérieur à 0.</p>
                                ) : (
                                  <p className="text-xs text-gray-500 mt-1 font-semibold">Tarif de base : {selectedDest?.price || '—'}</p>
                                )}
                              </>
                            );
                          })()}
                        </div>
                        {/* Supplément chambre individuelle — uniquement pour le 1er voyageur si seul, et uniquement pour voyages avec nuitée */}
                        {idx === 0 && form.voyageurs.filter(vv => vv.nom.trim()).length === 1 && hasNuitee && (() => {
                          const staticInfo = getChambreIndividuelleInfo(selectedDest?.id, selectedDest?.group, DESTINATIONS);
                          const override = chambreIndOverrides[selectedDest?.id] || {};
                          const disabledByAdmin = override.disabled === true;
                          const available = staticInfo.available && !disabledByAdmin;
                          const suggestedAmount = override.montant ?? staticInfo.amount;

                          // Cas 1 : chambre ind impossible (config statique OU override admin)
                          if (!available) {
                            return (
                              <div className="mt-2 bg-amber-50 border-2 border-amber-300 rounded-lg p-3" data-testid="chambre-ind-unavailable">
                                <p className="text-xs text-amber-900 font-bold">
                                  🏨 Chambre individuelle non disponible pour ce voyage
                                </p>
                                <p className="text-[11px] text-amber-800 leading-snug mt-1">
                                  L'hôtel partenaire ne propose pas (ou plus) de chambre individuelle. Vous pouvez voyager avec un(e) compagnon(ne) de votre entourage ou choisir de partager avec un(e) autre voyageur(se) solo à la répartition des chambres.
                                </p>
                              </div>
                            );
                          }

                          // Cas 2 : disponible — on pré-remplit si l'utilisateur n'a rien saisi
                          if (form.supplementChambre && !form.montantSupplementChambre && suggestedAmount > 0) {
                            // eslint-disable-next-line react-hooks/rules-of-hooks
                            setTimeout(() => set('montantSupplementChambre', String(suggestedAmount)), 0);
                          }

                          return (
                            <div className="mt-2 bg-gray-50 border border-gray-400 rounded-lg p-3">
                              <div className="flex items-center gap-3">
                                <input type="checkbox" checked={form.supplementChambre || false}
                                  onChange={e => {
                                    set('supplementChambre', e.target.checked);
                                    // Pré-remplir immédiatement au check si montant suggéré disponible
                                    if (e.target.checked && suggestedAmount > 0 && !form.montantSupplementChambre) {
                                      set('montantSupplementChambre', String(suggestedAmount));
                                    }
                                  }}
                                  data-testid="check-supplement-chambre"
                                  className="w-5 h-5 rounded border-gray-500 text-[#5BC5D4] focus:ring-[#5BC5D4] flex-shrink-0" />
                                <label className="text-xs text-gray-700 cursor-pointer" onClick={() => set('supplementChambre', !form.supplementChambre)}>
                                  Je souhaite une <strong>chambre individuelle</strong>
                                  {suggestedAmount > 0 ? (
                                    <span className="ml-1 text-purple-700 font-semibold">(supplément {suggestedAmount} €)</span>
                                  ) : (
                                    <span className="ml-1 text-gray-500 italic">(sous réserve de disponibilité)</span>
                                  )}
                                </label>
                              </div>
                              {form.supplementChambre && (
                                <div className="mt-3 ml-8 bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-2" data-testid="supplement-chambre-box">
                                  <div className="flex items-center gap-2">
                                    <label className="text-xs text-purple-900 font-bold">Montant du supplément <span className="text-red-500 text-sm">*</span> :</label>
                                    <input type="number" value={form.montantSupplementChambre || ''} onChange={e => set('montantSupplementChambre', e.target.value)}
                                      placeholder={suggestedAmount > 0 ? String(suggestedAmount) : 'ex: 80'}
                                      className="w-28 px-3 py-2 border-2 border-purple-400 rounded-lg text-sm text-center font-bold focus:border-purple-600 focus:ring-1 focus:ring-purple-600 outline-none" data-testid="input-supplement-chambre" required />
                                    <span className="text-xs text-purple-800 font-semibold">€</span>
                                    {suggestedAmount > 0 && (
                                      <span className="text-[10px] text-purple-700 italic" data-testid="supplement-suggested">
                                        Pré-rempli depuis la page voyage
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-[11px] text-purple-800 leading-snug">
                                    ℹ️ Montant confirmé sur la <strong>page du voyage</strong>. Il est <strong>automatiquement ajouté à votre total à payer</strong>.
                                  </p>
                                  <p className="text-[11px] text-purple-700">
                                    Un doute ? <a href="https://wa.me/message/XTYPH2HUY3L6L1" target="_blank" rel="noopener noreferrer" className="text-[#25D366] font-bold underline" data-testid="whatsapp-supplement-help">Demandez-nous par WhatsApp</a>.
                                  </p>
                                  {form.montantSupplementChambre && parseFloat(form.montantSupplementChambre) > 0 && (
                                    <p className="text-[11px] font-bold text-purple-900 bg-white border border-purple-300 rounded px-2 py-1.5 text-center" data-testid="supplement-confirm">
                                      ✓ Supplément de <span className="text-purple-700">{parseFloat(form.montantSupplementChambre).toLocaleString('fr-FR')} €</span> ajouté à votre total
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  ))}
                  {form.voyageurs.length < maxVoyageurs ? (
                    hasNuitee ? (
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-400 rounded-xl p-4 mt-2 shadow-sm" data-testid="add-voyageur-zone">
                      {/* Iter 826 (Kamel) — même vocabulaire « adresse du foyer » sur tous les voyages avec nuitée */}
                      <p className="text-sm font-bold text-amber-900 mb-1 flex items-center gap-2">
                        <span className="text-xl">👥</span>
                        Vous voyagez à plusieurs ?
                      </p>
                      <p className="text-xs text-amber-800 leading-relaxed mb-2">
                        Ajoutez ici, l'une après l'autre, les personnes qui <strong>habitent à la même adresse que vous</strong> : elles seront sur votre formulaire et votre facture. L'adresse du foyer compte, pas le nom de famille.
                        <br />
                        <span className="text-amber-700">⚠️ La <strong>répartition par chambre</strong> (qui dort avec qui) se fera <strong>plus bas</strong>, dans la section « Chambres ».</span>
                      </p>
                      <button type="button" onClick={addVoyageur} data-testid="add-voyageur-btn"
                        className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold rounded-lg px-4 py-2.5 shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6"/></svg>
                        Ajouter une personne de mon foyer — même adresse ({form.voyageurs.length}/{maxVoyageurs})
                      </button>
                      <p className="mt-1.5 text-[11px] text-amber-800 italic px-1 leading-snug max-w-md">
                        👉 Une personne qui habite à une <strong>autre adresse</strong> remplit son propre formulaire. Vous pourrez quand même partager la chambre : plus bas, dans la chambre, choisissez <em>« NON — Autre adresse »</em> (nom et prénom uniquement). <TutoLienChoix etape={2} />
                      </p>
                    </div>
                    ) : (
                    /* Voyage journée (sans nuitée) : simple encart d'ajout, sans notion de chambres */
                    <div className="mt-2" data-testid="add-voyageur-zone-journee">
                      {!questionAdresse && (
                        <button type="button" onClick={() => { setLienSepare(''); setQuestionAdresse(true); }} data-testid="add-voyageur-btn"
                          className="inline-flex items-center gap-2 bg-[#1E3A5F] hover:bg-[#2a4d7a] text-white text-sm font-bold rounded-lg px-4 py-2.5 shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6"/></svg>
                          Ajouter un voyageur ({form.voyageurs.length}/{maxVoyageurs})
                        </button>
                      )}
                      {!questionAdresse && (
                        <p className="mt-1.5 text-[11px] sm:text-xs text-[#1E3A5F] leading-snug max-w-md px-1" data-testid="journee-ajout-foyer-info">
                          Ajoutez uniquement les personnes du <strong>même foyer</strong>, à la <strong>même adresse</strong> et sur la <strong>même facture</strong>. Pour un autre foyer, remplissez un formulaire séparé.
                        </p>
                      )}
                      {questionAdresse && (
                        <div className="border-2 border-[#1E3A5F]/30 rounded-xl p-3 bg-white" data-testid="journee-question-adresse">
                          <p className="text-sm font-bold text-[#1E3A5F] text-center">Cette personne habite-t-elle à la même adresse que vous ?</p>
                          <p className="text-[11px] text-gray-600 text-center mb-2">L'adresse du foyer compte, pas le nom de famille.</p>
                          <div className="grid grid-cols-1 min-[400px]:grid-cols-2 gap-2">
                            <button type="button" onClick={() => { addVoyageur(); setQuestionAdresse(false); }} data-testid="journee-adresse-oui"
                              className="rounded-xl border-2 border-amber-400 bg-amber-50 hover:bg-amber-100 p-3 text-center flex flex-col items-center gap-1 transition-all">
                              <span className="text-2xl">👨‍👩‍👧</span>
                              <span className="text-xs font-bold text-amber-900 leading-tight">OUI — Même adresse que moi</span>
                              <span className="text-[10px] text-amber-800 leading-tight">Elle sera sur <strong>mon formulaire et ma facture</strong>.</span>
                              <span className="text-[10px] text-amber-800 leading-tight">L'association vérifie chaque formulaire avant de le confirmer.</span>
                              <span className="mt-1 text-[11px] font-bold px-3 py-1 rounded-full bg-amber-500 text-white">+ Ajouter</span>
                            </button>
                            <button type="button" onClick={ouvrirFormulaireSepare} data-testid="journee-adresse-non"
                              className="rounded-xl border-2 border-violet-400 bg-violet-50 hover:bg-violet-100 p-3 text-center flex flex-col items-center gap-1 transition-all">
                              <span className="text-2xl">🧾</span>
                              <span className="text-xs font-bold text-violet-900 leading-tight">NON — Autre adresse</span>
                              <span className="text-[10px] text-violet-800 leading-tight">Un nouveau formulaire s'ouvre pour cette personne. Vous pouvez continuer votre inscription ici.</span>
                              <span className="mt-1 text-[11px] font-bold px-3 py-1 rounded-full bg-violet-500 text-white">Ouvrir ↗</span>
                            </button>
                          </div>
                          <button type="button" onClick={() => setQuestionAdresse(false)} className="mt-2 text-[11px] text-gray-500 underline w-full text-center" data-testid="journee-adresse-annuler">Annuler</button>
                        </div>
                      )}
                      {lienSepare && (
                        <p className="mt-2 text-xs text-violet-900 bg-violet-50 border border-violet-300 rounded-lg px-3 py-2" data-testid="journee-lien-separe">
                          Votre navigateur a bloqué l'ouverture. <a href={lienSepare} target="_blank" rel="noopener noreferrer" className="font-bold underline">Ouvrir le formulaire séparé ↗</a>
                        </p>
                      )}
                    </div>
                    )
                  ) : (
                    maxVoyageurs < 6 && form.destination && (
                      <div className="bg-amber-50 border border-amber-300 text-amber-800 text-xs font-semibold rounded-lg px-3 py-2" data-testid="max-voyageurs-reached">
                        Il ne reste que {maxVoyageurs} place{maxVoyageurs > 1 ? 's' : ''} sur ce voyage : impossible d'ajouter un voyageur supplémentaire. Pour une personne de plus, inscrivez-la en liste d'attente après votre inscription.
                      </div>
                    )
                  )}
                </div>
              </section>
              )}

              {/* ============================================================ */}
              {/* LLORET : INSCRIPTION PAR CHAMBRE (iter 68)                   */}
              {/* Remplace VOYAGEURS + RÉPARTITION DES CHAMBRES pour Lloret.   */}
              {/* ============================================================ */}
              {isLloretTrip && selectedDest && (
                <section className={`bg-white rounded-2xl shadow-md overflow-hidden${st(2)}`} data-testid="section-lloret-chambres">
                  <SectionHeader title={T('titre_chambres_lloret')} />
                  <div className="p-5 space-y-4">
                    {!expressMode && (
                    <div className="bg-sky-50 border-2 border-sky-200 rounded-lg p-3 text-xs text-[#1E3A5F] leading-relaxed space-y-1">
                      <p className="font-bold flex items-center justify-between gap-2 flex-wrap">Règles des tarifs pour ce voyage : <TutoLienChoix etape={1} label="📖 Tuto inscription en 3 étapes" /></p>
                      <ul className="list-disc ml-5 space-y-0.5">
                        {(() => {
                          const cfgDest = chambreIndOverrides[selectedDest?.id] || {};
                          const quadOk = cfgDest.quadruple_disponible === true ? true
                            : cfgDest.quadruple_disponible === false ? false : isLloretOnly;
                          const tripleOk = cfgDest.triple_disponible !== false;
                          const maxPers = quadOk ? 4 : tripleOk ? 3 : 2;
                          const reducs = (cfgDest.reduction_3_11 !== null && cfgDest.reduction_3_11 !== undefined) || (cfgDest.reduction_bebe !== null && cfgDest.reduction_bebe !== undefined);
                          const indispo = [!tripleOk && 'triples', !quadOk && 'quadruples'].filter(Boolean).join(' et ');
                          return (
                            <>
                              {maxPers > 2 && reducs
                                ? <li><strong>2 pleins tarifs par chambre à partir de 3 ans.</strong> À partir de la 3e personne : tarif enfant (3-11 ans) ou bébé (&lt; 3 ans) réduit.</li>
                                : <li><strong>Plein tarif pour chaque voyageur</strong>{maxPers === 2 ? ' — 2 personnes par chambre' : ''} : aucune réduction enfant / bébé sur ce voyage.</li>}
                              <li data-testid="regle-max-chambre">Maximum <strong>{maxPers} personnes par chambre</strong>{indispo ? ` (chambres ${indispo} non disponibles sur ce voyage)` : ''}, maximum <strong>3 chambres</strong> sur ce formulaire (1 adresse = 1 formulaire).</li>
                            </>
                          );
                        })()}
                        <li>Le <strong>tarif de chaque personne est calculé automatiquement</strong> selon sa date de naissance (âge à la date de départ) et la composition de la chambre.</li>
                        {isNewYork && (
                          <li className="text-purple-700 font-semibold">⚡ NEW YORK : le prix de base varie selon le type de chambre — 1 grand lit 1590 €/pers, 2 lits séparés 1690 €/pers, 3 lits séparés 1540 €/pers.</li>
                        )}
                        {!isLloretOnly && (selectedDest?.individuelleSousReserve || selectedDest?.tripleSousReserve) && (
                          <li className="text-amber-700"><strong>Sous réserve de disponibilité</strong> : {selectedDest?.individuelleSousReserve && 'chambre individuelle'}{selectedDest?.individuelleSousReserve && selectedDest?.tripleSousReserve && ' et '}{selectedDest?.tripleSousReserve && 'chambre triple'}. Nous vous confirmerons par email sous 48h.</li>
                        )}
                        {isAvion && !selectedDest?.cniAcceptee && (
                          <li className="text-sky-700"><strong>Voyage en avion</strong> : <strong>passeport OBLIGATOIRE</strong> pour chaque voyageur, valide au minimum <strong>6 mois après la date de retour</strong>. La CNI n'est pas acceptée.</li>
                        )}
                        {isAvion && selectedDest?.cniAcceptee && (
                          <li className="text-sky-700"><strong>Voyage en avion en Europe</strong> : <strong>carte d'identité ou passeport en cours de validité</strong> pour chaque voyageur (valide jusqu'au retour). Les cartes de séjour : vérifiez avec nous avant de vous inscrire.</li>
                        )}
                      </ul>
                    </div>
                    )}

                    {/* Iter 74quater : warning "aucun majeur" supprimé (jugé prématuré).
                        La validation bloquante au submit affiche un message clair si nécessaire. */}

                    {/* Liste des chambres */}
                    {(form.chambres || []).map((ch, chIdx) => {
                      const occs = ch.occupants || [];
                      const comps = ch.compagnons || [];
                      // Iter 319 — le compteur inclut TOUTES les lignes ajoutées (même en cours de saisie)
                      const totalOcc = occs.length + comps.length;
                      // Config admin (iter 73) : override dispo triple / quadruple
                      const adminCfg = chambreIndOverrides[selectedDest?.id] || {};
                      const tripleDispo = adminCfg.triple_disponible === false ? false : true;
                      // Iter 312 — la case « Chambre quadruple possible » (Site internet → Voyages)
                      // fait foi pour TOUS les voyages : cochée = autorisée, décochée = interdite,
                      // non renseignée = uniquement Lloret (Espagne) par défaut.
                      const quadrupleDispo = adminCfg.quadruple_disponible === true ? true
                        : adminCfg.quadruple_disponible === false ? false : isLloretOnly;
                      // Max = 4 si quadruple dispo, sinon 3 si triple dispo, sinon 2
                      let maxPersPerRoom;
                      if (quadrupleDispo) maxPersPerRoom = 4;
                      else if (tripleDispo) maxPersPerRoom = 3;
                      else maxPersPerRoom = 2;
                      const canAddPerson = totalOcc < maxPersPerRoom;
                      // Tarifs auto via computeLloretForfaits (déjà existant, recalcul global form)
                      const lloretAll = isLloretTrip ? computeLloretForfaits() : {};
                      // Type de chambre auto suggéré
                      const autoType = totalOcc === 1 ? 'individuelle'
                        : totalOcc === 3 ? '3-lits'
                        : totalOcc === 4 ? '4-lits'
                        : null;
                      // Labels user-friendly (2026-02 iter 69)
                      const typeOptions = totalOcc === 2
                        ? [
                            { val: 'grand-lit', label: '1 grand lit (souhaité, non garanti)' },
                            { val: '2-lits', label: '2 lits séparés' },
                          ]
                        : [];
                      const typeLabel = autoType === 'individuelle' ? 'Chambre individuelle (1 lit simple)'
                        : autoType === '3-lits' ? '3 lits séparés'
                        : autoType === '4-lits' ? '4 lits séparés'
                        : null;
                      // Total de la chambre (forfait par voyageur sommé)
                      const chambreTotal = occs.reduce((s, vi) => s + (parseFloat(lloretAll[vi]?.forfait) || 0), 0);
                      // Iter 73 bis : la carte chambre doit afficher son supplément individuel
                      // si cette chambre est détectée comme "individuelle" (1 seul occupant OU chambre 2 pers optimisée).
                      const isChambreInd = chambresIndividuellesDetectees.some(c => c.chIdx === chIdx);
                      // Iter 74ter/quater : bannière bloquante si admin a désactivé OU si l'hôtel ne propose pas la chambre ind.
                      const chambreIndBloquee = isChambreInd && (chambreIndInfo.disabledByAdmin || chambreIndInfo.nonDisponibleStatic);
                      const suppChambreCard = (isChambreInd && chambreIndInfo.available) ? (chambreIndInfo.montant || 0) : 0;
                      const chambreTotalAvecSupp = chambreTotal + suppChambreCard;
                      return (
                        <div key={chIdx} className="border-2 border-gray-300 rounded-xl p-4 bg-gradient-to-br from-white to-sky-50/30" data-testid={`lloret-chambre-${chIdx}`}>
                          {totalOcc <= 1 && !chambreIndBloquee && (
                            <div className="mb-3 bg-sky-50 border-2 border-sky-300 rounded-lg p-3" data-testid={`chambre-solo-alerte-${chIdx}`}>
                              <p className="text-sm font-bold text-sky-900">🏨 Ici, les inscriptions se font par chambre</p>
                              <p className="text-xs text-sky-800 mt-1 leading-relaxed">
                                Remplissez cette chambre avec <strong>les personnes qui y dormiront</strong> :
                              </p>
                              <ul className="text-xs text-sky-900 mt-1.5 space-y-1 pl-1">
                                <li>👨‍👩‍👧 <strong>Une personne de votre foyer</strong> → bouton « Ajouter » de la chambre</li>
                                <li>🤝 <strong>Une personne qui ne sera pas sur votre facture</strong> (elle remplit son propre formulaire — ami(e) ou binôme) → carte violette : indiquez simplement son NOM et PRÉNOM</li>
                              </ul>
                              <p className="text-[11px] text-sky-700 italic mt-1.5">Vous serez seul(e) dans la chambre ? C'est possible aussi : laissez-la telle quelle (chambre individuelle).</p>
                            </div>
                          )}
                          {totalOcc >= 2 && (
                            <div className="mb-3 bg-emerald-50 border border-emerald-300 rounded-lg px-3 py-2 text-xs font-semibold text-emerald-800" data-testid={`chambre-partagee-ok-${chIdx}`}>
                              ✅ Chambre partagée — {totalOcc} personnes dans cette chambre.
                            </div>
                          )}
                          {chambreIndBloquee && (
                            <div className="mb-3 bg-red-50 border-2 border-red-400 rounded-lg p-3" data-testid={`chambre-ind-bloquee-${chIdx}`}>
                              <p className="text-sm font-bold text-red-800 mb-1">
                                🚫 Chambre individuelle indisponible pour ce voyage
                              </p>
                              <p className="text-[12px] text-red-700 leading-snug">
                                {chambreIndInfo.nonDisponibleStatic
                                  ? "L'hôtel partenaire ne propose pas de chambre individuelle pour ce voyage."
                                  : "L'association a indiqué qu'il n'y a plus de chambre individuelle pour ce voyage."}
                                {" "}Vous êtes seul(e) dans cette chambre — merci de choisir l'une des options ci-dessous avant d'envoyer l'inscription :
                              </p>
                              <ul className="text-[12px] text-red-700 leading-snug mt-1.5 list-disc pl-5 space-y-0.5">
                                <li><strong>Ajouter un(e) voyageur(se)</strong> dans cette chambre (bouton ci-dessous).</li>
                                <li><strong>Ajouter une personne d'un autre formulaire</strong> (section "Personnes d'un autre formulaire").</li>
                                <li><strong>Cocher "Partager ma chambre avec un(e) autre voyageur(se) solo"</strong> si l'option existe.</li>
                              </ul>
                              <p className="text-[11px] text-red-600 italic mt-1.5">
                                Sans l'une de ces options, l'envoi du formulaire sera bloqué.
                              </p>
                            </div>
                          )}
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="font-bold text-[#1E3A5F] text-sm">🏨 Chambre {chIdx + 1} <span className="ml-1 text-xs font-bold text-white bg-[#1E3A5F] rounded-full px-2 py-0.5" data-testid={`lloret-chambre-compteur-${chIdx}`}>{totalOcc}/{maxPersPerRoom}</span></h3>
                            {form.chambres.length > 1 && (
                              <button type="button"
                                onClick={() => { if (window.confirm(`Supprimer la chambre ${chIdx + 1} et ses voyageurs ?`)) lloretRemoveChambre(chIdx); }}
                                className="text-red-500 hover:text-red-700 text-xs font-semibold"
                                data-testid={`lloret-remove-chambre-${chIdx}`}>
                                Supprimer cette chambre
                              </button>
                            )}
                          </div>

                          {/* Personnes dans la chambre */}
                          {occs.length === 0 && (
                            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800 mb-2">
                              Ajoutez au moins une personne dans cette chambre pour commencer.
                            </div>
                          )}
                          <div className="space-y-3">
                            {occs.map((voyIdx, posInChambre) => {
                              const v = form.voyageurs[voyIdx] || {};
                              const tarifAuto = lloretAll[voyIdx];
                              const isFirstVoyageurGlobal = voyIdx === 0;
                              // Téléphone 2 facultatif : sur le 2ème voyageur ajouté (voyIdx === 1)
                              const isSecondVoyageurGlobal = voyIdx === 1;
                              return (
                                <div key={voyIdx} className="bg-white border border-gray-200 rounded-lg p-3" data-testid={`lloret-chambre-${chIdx}-person-${posInChambre}`}>
                                  <div className="flex items-center justify-between mb-2">
                                    <p className="text-xs font-bold text-[#1E3A5F]">Personne {posInChambre + 1}{isFirstVoyageurGlobal && <span className="ml-1 text-[10px] bg-[#1E3A5F] text-white px-1.5 py-0.5 rounded">Responsable</span>}</p>
                                    {form.voyageurs.length > 1 && (
                                      <button type="button" onClick={() => lloretRemovePerson(voyIdx)}
                                        className="text-red-400 hover:text-red-600 text-xs font-semibold"
                                        data-testid={`lloret-remove-person-${chIdx}-${posInChambre}`}>✕ Retirer</button>
                                    )}
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    <input type="text" value={v.nom || ''} onChange={e => setVoyageur(voyIdx, 'nom', e.target.value.toUpperCase())}
                                      placeholder="NOM *" className="border-2 border-gray-400 rounded px-2 py-1.5 text-xs font-bold uppercase focus:border-[#79c9ea] outline-none"
                                      data-testid={`voyageur-${voyIdx}-nom`} required />
                                    <input type="text" value={v.prenom || ''} onChange={e => setVoyageur(voyIdx, 'prenom', e.target.value.toUpperCase())}
                                      placeholder="PRÉNOM *" className="border-2 border-gray-400 rounded px-2 py-1.5 text-xs font-bold uppercase focus:border-[#79c9ea] outline-none"
                                      data-testid={`voyageur-${voyIdx}-prenom`} required />
                                    <input type="text" inputMode="numeric" value={v.dateNaissance || ''}
                                      onChange={e => setVoyageur(voyIdx, 'dateNaissance', formatDateInput(e.target.value))}
                                      placeholder="JJ/MM/AAAA *" className="border-2 border-gray-400 rounded px-2 py-1.5 text-xs focus:border-[#79c9ea] outline-none"
                                      data-testid={`voyageur-${voyIdx}-ddn`} required />
                                    <select value={v.civilite || ''} onChange={e => setVoyageur(voyIdx, 'civilite', e.target.value)}
                                      className="border-2 border-gray-400 rounded px-2 py-1.5 text-xs focus:border-[#79c9ea] outline-none"
                                      data-testid={`voyageur-${voyIdx}-civilite`} required>
                                      <option value="">Sexe *</option>
                                      <option value="M">Homme (M)</option>
                                      <option value="F">Femme (F)</option>
                                    </select>
                                  </div>
                                  {/* Âge affiché en temps réel + tarif auto */}
                                  {v.dateNaissance && tarifAuto && tarifAuto.ageText && (
                                    <div className="mt-2 bg-sky-50 border border-sky-200 rounded px-2 py-1.5 text-xs flex items-center justify-between gap-2 flex-wrap">
                                      <span className="text-gray-700">
                                        <span className="inline-block bg-white border border-sky-300 rounded px-1.5 py-0.5 font-bold text-[#1E3A5F] mr-1.5">{tarifAuto.ageText}</span>
                                        <span className="font-semibold">{tarifAuto.singleRoomReal ? 'Plein tarif' : (tarifAuto.categorieLabel || 'Plein tarif (12 ans et +)')}</span>
                                      </span>
                                      <span className="font-bold text-[#1E3A5F]" data-testid={`lloret-tarif-${voyIdx}`}>{tarifAuto.forfait || 0} €</span>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* Iter 328 — Choix d'ajout : 2 cartes (l'un, l'autre, ou les deux)
                              Iter 823 (Kamel) — question « même adresse ? » ; mêmes traitements : OUI → lloretAddPersonToChambre,
                              NON → chambre.compagnons. Présentation uniquement, payload inchangé. */}
                          <div className="mt-4 pt-3 border-t border-gray-200">
                            <p className="text-xs font-bold text-gray-700 text-center mb-0.5">➕ Ajouter une personne dans cette chambre</p>
                            <p className="text-sm font-bold text-[#1E3A5F] text-center" data-testid={`lloret-question-adresse-${chIdx}`}>Cette personne habite-t-elle à la même adresse que vous ?</p>
                            <p className="text-[11px] text-gray-600 text-center mb-2">L'adresse du foyer compte, pas le nom de famille. <TutoLienChoix etape={2} className="ml-1" /></p>
                            <div className="grid grid-cols-1 min-[400px]:grid-cols-2 gap-2">
                              <button type="button" onClick={() => canAddPerson && lloretAddPersonToChambre(chIdx)}
                                disabled={!canAddPerson}
                                className={`rounded-xl border-2 p-3 text-center transition-all flex flex-col items-center gap-1 ${canAddPerson ? 'border-amber-400 bg-amber-50 hover:bg-amber-100 hover:shadow-md' : 'border-gray-200 bg-gray-100 opacity-60 cursor-not-allowed'}`}
                                data-testid={`lloret-add-person-chambre-${chIdx}`}>
                                <span className="text-2xl">👨‍👩‍👧</span>
                                <span className={`text-xs font-bold leading-tight ${canAddPerson ? 'text-amber-900' : 'text-gray-400'}`}>OUI — Même adresse que moi</span>
                                <span className={`text-[10px] leading-tight ${canAddPerson ? 'text-amber-800' : 'text-gray-400'}`}>Elle sera sur <strong>mon formulaire et ma facture</strong>. L'association vérifie chaque formulaire avant de le confirmer.</span>
                                <span className={`text-[10px] leading-tight italic ${canAddPerson ? 'text-amber-700' : 'text-gray-400'}`}>Ex. : maman et enfant, ou couple, même avec des noms différents.</span>
                                <span className={`mt-1 text-[11px] font-bold px-3 py-1 rounded-full ${canAddPerson ? 'bg-amber-500 text-white' : 'bg-gray-300 text-gray-500'}`}>+ Ajouter</span>
                              </button>
                              <button type="button"
                                onClick={() => { if (totalOcc < maxPersPerRoom) setForm(p => { const chs = [...(p.chambres || [])]; const ch2 = { ...(chs[chIdx] || {}) }; ch2.compagnons = [...(ch2.compagnons || []), { nom: '', prenom: '' }]; chs[chIdx] = ch2; return { ...p, chambres: chs }; }); }}
                                disabled={totalOcc >= maxPersPerRoom}
                                className={`rounded-xl border-2 p-3 text-center transition-all flex flex-col items-center gap-1 ${totalOcc < maxPersPerRoom ? 'border-violet-400 bg-violet-50 hover:bg-violet-100 hover:shadow-md' : 'border-gray-200 bg-gray-100 opacity-60 cursor-not-allowed'}`}
                                data-testid={`lloret-add-compagnon-${chIdx}`}>
                                <span className="text-2xl">🤝</span>
                                <span className={`text-xs font-bold leading-tight ${totalOcc < maxPersPerRoom ? 'text-violet-900' : 'text-gray-400'}`}>NON — Autre adresse</span>
                                <span className={`text-[10px] leading-tight ${totalOcc < maxPersPerRoom ? 'text-violet-800' : 'text-gray-400'}`}><strong>Nom et prénom uniquement</strong> pour la chambre. Pas d'inscription ni de facture sur ce formulaire.</span>
                                <span className={`text-[10px] leading-tight ${totalOcc < maxPersPerRoom ? 'text-violet-800' : 'text-gray-400'}`}>Elle devra remplir son propre formulaire.</span>
                                <span className={`text-[10px] leading-tight italic ${totalOcc < maxPersPerRoom ? 'text-violet-700' : 'text-gray-400'}`}>Ex. : deux sœurs à des adresses différentes.</span>
                                <span className={`mt-1 text-[11px] font-bold px-3 py-1 rounded-full ${totalOcc < maxPersPerRoom ? 'bg-violet-500 text-white' : 'bg-gray-300 text-gray-500'}`}>+ Ajouter</span>
                              </button>
                            </div>
                            {totalOcc < maxPersPerRoom ? (
                              <p className="mt-1.5 text-[10px] text-gray-500 text-center italic">
                                L'un, l'autre… ou les deux (ex. chambre de 3 : vous + votre conjoint + un(e) ami(e) qui remplit son propre formulaire).
                              </p>
                            ) : (
                              <p className="mt-1.5 text-[12px] font-bold text-red-600 text-center px-2" data-testid={`lloret-max-atteint-${chIdx}`}>
                                ⚠️ Maximum atteint : {maxPersPerRoom} personnes par chambre sur ce voyage (tous formulaires confondus).
                              </p>
                            )}
                          </div>

                          {/* Blocage "4 pleins tarifs" en direct (iter 71) */}
                          {(() => {
                            if (totalOcc !== 4) return null;
                            const cats = [];
                            (ch.occupants || []).forEach(vi => {
                              const v = form.voyageurs[vi];
                              if (v && v.dateNaissance?.trim()) cats.push(catFromAge(ageAuDepartNum(v.dateNaissance)));
                              else cats.push('unknown');
                            });
                            (ch.compagnons || []).filter(c => (c.nom || '').trim() && (c.prenom || '').trim()).forEach(c => {
                              if (c.tranche === 'moins12') { cats.push('child'); return; }  // Iter 914
                              const age = ageAuDepartNum(c.dateNaissance || '');
                              cats.push(age === null ? 'adult' : catFromAge(age));
                            });
                            // Saisie en cours (lignes vides ou DDN manquantes) : ne pas alerter prématurément
                            if (cats.length < 4 || cats.includes('unknown')) return null;
                            const hasChildOrBaby = cats.some(c => c === 'child' || c === 'baby');
                            if (hasChildOrBaby) return null;
                            return (
                              <div className="mt-2 bg-red-50 border-2 border-red-400 rounded-lg px-3 py-2 text-[11px] text-red-800" data-testid={`chambre-${chIdx}-4-pleins-tarifs-warning`}>
                                <p className="font-bold">🚫 4 pleins tarifs dans une même chambre n'est pas possible</p>
                                <p className="mt-0.5">Une chambre à 4 personnes doit contenir au moins un <strong>enfant (3-11 ans)</strong> ou un <strong>bébé (&lt; 3 ans)</strong> pour bénéficier du tarif 3e/4e lit. Merci de répartir en 2 chambres ou de <a href="https://wa.me/message/XTYPH2HUY3L6L1" target="_blank" rel="noopener noreferrer" className="underline font-bold">nous contacter</a>.</p>
                              </div>
                            );
                          })()}

                          {/* Personnes d'un autre formulaire — lignes de saisie (le bouton d'ajout est dans les cartes ci-dessus) */}
                          {(ch.compagnons || []).length > 0 && (
                          <div className="mt-3 pt-2 border-t border-gray-100">
                            <p className="text-[11px] font-bold text-violet-700 uppercase mb-1" data-testid={`lloret-compagnons-titre-${chIdx}`}>🤝 Chambre uniquement · Inscription séparée</p>
                            <p className="text-[10px] text-gray-500 italic mb-2">Indiquez NOM, PRÉNOM et la tranche d'âge — ces personnes ne sont ni inscrites ni facturées ici : elles remplissent leur propre formulaire.</p>
                            {(ch.compagnons || []).map((comp, ci) => (
                              <div key={ci} className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2 items-center">
                                <input type="text" value={comp.nom || ''}
                                  onChange={e => setForm(p => { const chs = [...(p.chambres || [])]; const cs = [...(chs[chIdx].compagnons || [])]; cs[ci] = { ...cs[ci], nom: e.target.value.toUpperCase() }; chs[chIdx] = { ...chs[chIdx], compagnons: cs }; return { ...p, chambres: chs }; })}
                                  placeholder="NOM *" className="border-2 border-gray-300 rounded px-2 py-1.5 text-xs uppercase"
                                  data-testid={`lloret-compagnon-${chIdx}-${ci}-nom`} />
                                <input type="text" value={comp.prenom || ''}
                                  onChange={e => setForm(p => { const chs = [...(p.chambres || [])]; const cs = [...(chs[chIdx].compagnons || [])]; cs[ci] = { ...cs[ci], prenom: e.target.value.toUpperCase() }; chs[chIdx] = { ...chs[chIdx], compagnons: cs }; return { ...p, chambres: chs }; })}
                                  placeholder="PRÉNOM *" className="border-2 border-gray-300 rounded px-2 py-1.5 text-xs uppercase"
                                  data-testid={`lloret-compagnon-${chIdx}-${ci}-prenom`} />
                                {/* Iter 914 (Kamel) — tranche d'âge : un enfant d'un autre foyer ne doit pas bloquer la chambre de 4 (règle « 3 pleins tarifs max ») */}
                                <select value={comp.tranche || 'adulte'}
                                  onChange={e => setForm(p => { const chs = [...(p.chambres || [])]; const cs = [...(chs[chIdx].compagnons || [])]; cs[ci] = { ...cs[ci], tranche: e.target.value }; chs[chIdx] = { ...chs[chIdx], compagnons: cs }; return { ...p, chambres: chs }; })}
                                  className={`border-2 rounded px-2 py-1.5 text-xs font-semibold ${comp.tranche === 'moins12' ? 'border-emerald-400 bg-emerald-50 text-emerald-800' : 'border-gray-300'}`}
                                  data-testid={`lloret-compagnon-${chIdx}-${ci}-tranche`}>
                                  <option value="adulte">Adulte · 12 ans et plus</option>
                                  <option value="moins12">Enfant · moins de 12 ans</option>
                                </select>
                                <button type="button"
                                  onClick={() => setForm(p => { const chs = [...(p.chambres || [])]; const cs = (chs[chIdx].compagnons || []).filter((_, i) => i !== ci); chs[chIdx] = { ...chs[chIdx], compagnons: cs }; return { ...p, chambres: chs }; })}
                                  className="text-red-400 hover:text-red-600 text-xs font-semibold"
                                  data-testid={`lloret-remove-compagnon-${chIdx}-${ci}`}>✕ Retirer</button>
                              </div>
                            ))}
                          </div>
                          )}

                          {/* Type de chambre souhaité */}
                          {totalOcc >= 1 && (
                            <div className="mt-4 pt-3 border-t border-gray-200 space-y-2">
                              <div className="flex items-center justify-between flex-wrap gap-2">
                                <p className="text-[11px] font-bold text-gray-600 uppercase">Literie souhaitée <span className="normal-case font-normal text-gray-400">(la décision finale revient à l'hôtel)</span></p>
                                {typeOptions.length === 0 && typeLabel && (
                                  <span className="text-xs font-bold text-purple-700 bg-purple-50 border border-purple-300 rounded px-2 py-1"
                                    data-testid={`lloret-type-chambre-${chIdx}`}>{typeLabel} (auto)</span>
                                )}
                                {typeOptions.length > 0 && (
                                  <div className="flex flex-col gap-1.5 w-full" data-testid={`lloret-type-chambre-${chIdx}`}>
                                    {/* Iter 95 — boutons radio visibles (au lieu de menu déroulant)
                                        car certains voyageurs ne voyaient pas que c'était une liste. */}
                                    {typeOptions.map(o => (
                                      <label key={o.val}
                                        className={`flex items-center gap-2 cursor-pointer border-2 rounded-lg px-3 py-2 transition ${
                                          ch.typeChambre === o.val
                                            ? 'border-[#1E3A5F] bg-sky-100 ring-2 ring-[#79c9ea]'
                                            : 'border-gray-300 bg-white hover:border-[#79c9ea] hover:bg-sky-50'
                                        }`}>
                                        <input type="radio"
                                          name={`lloret-type-${chIdx}`}
                                          value={o.val}
                                          checked={ch.typeChambre === o.val}
                                          onChange={() => setForm(p => { const chs = [...(p.chambres || [])]; chs[chIdx] = { ...chs[chIdx], typeChambre: o.val }; return { ...p, chambres: chs }; })}
                                          className="w-4 h-4"
                                          data-testid={`lloret-type-chambre-${chIdx}-${o.val}`}
                                          required />
                                        <span className="text-xs font-bold text-[#1E3A5F]">{o.label}</span>
                                      </label>
                                    ))}
                                    {!ch.typeChambre && (
                                      <p className="text-[10px] text-orange-600 italic font-semibold">⚠ Choix obligatoire — sélectionnez une option ci-dessus.</p>
                                    )}
                                  </div>
                                )}
                                {piscineDispo && (
                                  <div className="w-full mt-2 border-2 border-[#1E3A5F]/15 rounded-lg p-2" data-testid={`piscine-chambre-${chIdx}`}>
                                    <p className="text-xs font-bold text-[#1E3A5F]">🏊 VUE PISCINE GARANTIE <span className="font-normal text-gray-500">— option, +{piscineMontantChambre} € pour cette chambre et tout le séjour</span></p>
                                    <div className="grid grid-cols-2 gap-2 mt-1.5">
                                      <button type="button" onClick={() => setForm(p => { const chs = [...(p.chambres || [])]; chs[chIdx] = { ...chs[chIdx], vuePiscine: true }; return { ...p, chambres: chs }; })}
                                        className={`py-2 rounded-lg text-xs font-bold border-2 ${ch.vuePiscine ? 'bg-[#D62828] border-[#D62828] text-white' : 'bg-white border-gray-300 text-gray-600'}`} data-testid={`piscine-chambre-${chIdx}-oui`}>OUI (+{piscineMontantChambre} €)</button>
                                      <button type="button" onClick={() => setForm(p => { const chs = [...(p.chambres || [])]; chs[chIdx] = { ...chs[chIdx], vuePiscine: false }; return { ...p, chambres: chs }; })}
                                        className={`py-2 rounded-lg text-xs font-bold border-2 ${!ch.vuePiscine ? 'bg-[#1E3A5F] border-[#1E3A5F] text-white' : 'bg-white border-gray-300 text-gray-600'}`} data-testid={`piscine-chambre-${chIdx}-non`}>NON</button>
                                    </div>
                                    <p className="text-[10px] text-gray-500 mt-1">Vue garantie par l'hôtel. Le grand lit, lui, reste une préférence non garantie.</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Ajouter une chambre — rendu bien visible (demande utilisateur) */}
                    {(form.chambres || []).length < MAX_CHAMBRES_LLORET && (hotelRestCh === null || (form.chambres || []).length < hotelRestCh) && (
                      <div className="border-t border-gray-200 pt-4 mt-4">
                        <p className="text-xs text-gray-600 mb-2">
                          Si vous voulez <strong>séparer</strong> les voyageurs dans une chambre supplémentaire (chambres séparées par couple, etc.), cliquez ici :
                        </p>
                        <button type="button" onClick={lloretAddChambre}
                          className="w-full bg-[#1E3A5F] hover:bg-[#2a4d7a] text-white rounded-lg py-3 text-sm font-bold shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
                          data-testid="lloret-add-chambre">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6"/></svg>
                          Ajouter une chambre supplémentaire ({(form.chambres || []).length}/{MAX_CHAMBRES_LLORET})
                        </button>
                      </div>
                    )}
                    {(form.chambres || []).length >= MAX_CHAMBRES_LLORET && (
                      <p className="text-[11px] text-gray-500 italic text-center">Maximum 3 chambres par formulaire. Pour plus de voyageurs, remplissez un second formulaire.</p>
                    )}
                    {hotelRestCh !== null && (form.chambres || []).length < MAX_CHAMBRES_LLORET && (form.chambres || []).length >= hotelRestCh && (
                      <p className="text-[11px] text-orange-700 font-semibold text-center" data-testid="lloret-chambres-hotel-limite">
                        {chambresManquent ? `L'hôtel n'a plus que ${hotelRestCh} chambre${hotelRestCh > 1 ? 's' : ''} sur la ou les semaines choisies : regroupez les voyageurs ou passez en liste d'attente.`
                          : 'Dernières chambres de l\'hôtel sur cette semaine : impossible d\'en ajouter une autre, regroupez les voyageurs dans les chambres ci-dessus.'}
                      </p>
                    )}
                  </div>
                </section>
              )}

              <section className={`bg-white rounded-2xl shadow-md overflow-hidden${st(2)}`} data-testid="section-adresse">
                <SectionHeader title={T('titre_adresse')} />
                <div className="p-5 space-y-4">
                  {/* Iter 765 (Kamel — smartphone) : rappel « même adresse » retiré ici (déjà dit en haut « 1 formulaire = 1 foyer » + case « Je certifie » en bas) */}
                  {/* Iter 866 (Kamel) — personne contact : à qui appartiennent le téléphone et l'e-mail de cette fiche (pas forcément le 1er inscrit, qui peut être un enfant) */}
                  <div className="rounded-xl border-2 border-[#1E3A5F]/30 bg-[#1E3A5F]/5 p-3" data-testid="bloc-contact-formulaire">
                    <label className="text-xs font-semibold text-[#1E3A5F] block mb-1">PERSONNE CONTACT POUR CE FORMULAIRE <span className="text-red-500">*</span></label>
                    {(() => {
                      // Iter 867 (Kamel) — menu déroulant des inscrits ADULTES (même orthographe que la liste, rien à retaper) + « Autre personne »
                      const estAdulte = (v) => { const m = (v.dateNaissance || '').match(/^(\d{2})\/(\d{2})\/(\d{4})$/); if (!m) return true; const n = new Date(+m[3], +m[2] - 1, +m[1]); const ref = new Date(); let a = ref.getFullYear() - n.getFullYear(); if (ref < new Date(ref.getFullYear(), n.getMonth(), n.getDate())) a--; return a >= 18; };
                      const adultes = (form.voyageurs || []).map((v, i) => ({ v, i })).filter(({ v }) => (v.nom || '').trim() && (v.prenom || '').trim() && estAdulte(v));
                      const valeur = form.contactSource === 'autre' ? 'autre' : (form.contactVoyageurIndex != null && adultes.some(a => a.i === form.contactVoyageurIndex) ? String(form.contactVoyageurIndex) : '');
                      const choisir = (val) => {
                        if (val === 'autre') { setForm(p => ({ ...p, contactSource: 'autre', contactVoyageurIndex: null, contactPrenom: '', contactNom: '' })); return; }
                        const a = adultes.find(x => String(x.i) === val);
                        setForm(p => ({ ...p, contactSource: a ? 'voyageur' : '', contactVoyageurIndex: a ? a.i : null, contactPrenom: a ? (a.v.prenom || '').trim() : '', contactNom: a ? (a.v.nom || '').trim().toUpperCase() : '' }));
                        setError('');
                      };
                      return (
                        <>
                          <select value={valeur} onChange={(e) => choisir(e.target.value)} data-testid="select-contact-formulaire" required
                            className={`w-full border-2 rounded-xl px-4 py-3 text-sm font-semibold focus:border-[#79c9ea] outline-none ${valeur === '' ? 'border-amber-500 bg-amber-50 text-amber-900' : 'border-green-600 bg-green-50 text-green-900'}`}>
                            <option value="">👉 À CHOISIR : cliquez ici et choisissez la personne contact</option>
                            {adultes.map(({ v, i }) => <option key={i} value={String(i)}>{(v.prenom || '').trim()} {(v.nom || '').trim().toUpperCase()}</option>)}
                            <option value="autre">Autre personne (ne voyage pas) — je saisis son nom</option>
                          </select>
                          {valeur === '' && <p className="text-xs font-semibold text-amber-800 mt-1" data-testid="contact-a-choisir">⚠️ Rien n'est pré-rempli : vous devez choisir un nom dans la liste pour continuer.</p>}
                          {valeur !== '' && valeur !== 'autre' && <p className="text-xs text-green-800 mt-1" data-testid="contact-choisi">✅ Personne contact : <strong>{form.contactPrenom} {form.contactNom}</strong></p>}
                          {adultes.length === 0 && form.contactSource !== 'autre' && <p className="text-[11px] text-amber-700 mt-1" data-testid="contact-aucun-adulte">Aucun adulte dans la liste pour l'instant : complétez d'abord les inscrits, ou choisissez « Autre personne ».</p>}
                          {form.contactSource === 'autre' && (
                            <div className="grid grid-cols-2 gap-3 mt-2">
                              <input type="text" autoComplete="off" value={form.contactPrenom || ''} onChange={(e) => set('contactPrenom', e.target.value)} placeholder="Prénom" data-testid="input-contact-prenom"
                                className="w-full border-2 border-gray-400 rounded-xl px-4 py-3 text-sm focus:border-[#79c9ea] outline-none bg-white" required />
                              <input type="text" autoComplete="off" value={form.contactNom || ''} onChange={(e) => set('contactNom', e.target.value.toUpperCase())} placeholder="NOM" data-testid="input-contact-nom"
                                className="w-full border-2 border-gray-400 rounded-xl px-4 py-3 text-sm focus:border-[#79c9ea] outline-none uppercase bg-white" required />
                            </div>
                          )}
                        </>
                      );
                    })()}
                    <p className="text-[11px] text-gray-600 mt-1.5" data-testid="aide-contact-formulaire">Le téléphone et l'e-mail ci-dessous sont ceux de cette personne. <strong>Une seule personne contact par formulaire.</strong> Si plusieurs foyers voyagent ensemble, chaque foyer doit remplir son propre formulaire.</p>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">TÉLÉPHONE DE LA FAMILLE <span className="text-red-500">*</span></label>
                    <input type="tel" inputMode="tel" autoComplete="tel" value={form.telephone} onChange={(e) => set('telephone', e.target.value)} placeholder="06 XX XX XX XX" data-testid="input-telephone" id="input-telephone"
                      className={`w-full border-2 ${telCheck.status === 'ko' || (telCheck.status === 'conflit' && !form.telAttestation) ? 'border-red-500 bg-red-50' : 'border-gray-400'} rounded-xl px-4 py-3 text-sm focus:border-[#79c9ea] focus:ring-1 focus:ring-[#79c9ea] outline-none`} required />
                    <p className="text-[11px] text-gray-500 mt-1">Le numéro du voyageur (ou de son foyer), pas celui de la personne qui remplit pour lui — il sert à toutes nos communications.</p>
                    {telCheck.status === 'ko' && (
                      <p className="text-xs text-red-700 font-bold mt-1 bg-red-50 border border-red-300 rounded-lg p-2" data-testid="telephone-autre-foyer-warning">⛔ {telCheck.message}</p>
                    )}
                    {telCheck.status === 'conflit' && (
                      <div className={`mt-1 border-2 rounded-xl p-3 ${form.telAttestation ? 'bg-green-50 border-green-600' : 'bg-red-50 border-red-400'}`} data-testid="telephone-conflit-contact">
                        <p className="text-xs font-bold text-red-800">⛔ Ce numéro est déjà enregistré chez nous au nom de <span className="uppercase" data-testid="telephone-conflit-autre-nom">{telCheck.autreNom}</span>.</p>
                        <p className="text-[11px] text-gray-700 mt-1">Chez nous, <strong>un numéro = une seule personne contact</strong>. Si ce n'est pas votre numéro, indiquez le vôtre ci-dessus. Si c'est bien le vôtre, cochez :</p>
                        <label className="flex items-start gap-2 mt-2 cursor-pointer">
                          <input type="checkbox" checked={!!form.telAttestation} onChange={(e) => set('telAttestation', e.target.checked)} className="w-5 h-5 mt-0.5 accent-green-700" data-testid="checkbox-tel-attestation" />
                          <span className="text-xs font-semibold text-gray-900">J'atteste que ce numéro de téléphone est bien le mien ({(form.contactPrenom || '').trim()} {(form.contactNom || '').trim().toUpperCase()}). L'association l'associera à mon nom et le retirera de l'ancien.</span>
                        </label>
                      </div>
                    )}
                    {telCheck.status === 'ok' && (
                      <p className="text-[11px] text-green-700 font-semibold mt-1" data-testid="telephone-foyer-ok">✓ Numéro vérifié.</p>
                    )}
                    {/* Iter 846 (Kamel) — canal de contact préféré : certains réagissent au SMS (individuel) plus qu'au WhatsApp (groupe) */}
                    <div className="mt-2 bg-sky-50 border border-sky-200 rounded-xl px-3 py-2" data-testid="canaux-contact">
                      <p className="text-xs font-semibold text-[#1E3A5F] mb-1">Pour les infos importantes, vous préférez être contacté par :</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1">
                        {[['whatsapp', '💬 WhatsApp'], ['sms', '📱 SMS'], ['email', '✉️ E-mail']].map(([k, l]) => (
                          <label key={k} className="flex items-center gap-1.5 text-sm text-gray-800 cursor-pointer">
                            <input type="checkbox" checked={(form.canauxContact || []).includes(k)} data-testid={`canal-${k}`}
                              onChange={(e) => set('canauxContact', e.target.checked ? [...(form.canauxContact || []), k] : (form.canauxContact || []).filter(x => x !== k))} />
                            {l}
                          </label>
                        ))}
                      </div>
                      <p className="text-[10px] text-gray-500 mt-1">Le SMS est un message individuel, le WhatsApp passe par nos groupes. L'e-mail reste envoyé dans tous les cas (récapitulatif, factures).</p>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">RUE *</label>
                    <input type="text" autoComplete="street-address" value={form.rue} onChange={(e) => set('rue', e.target.value.toUpperCase())} placeholder="NUMÉRO ET NOM DE RUE" data-testid="input-rue"
                      className="w-full border-2 border-gray-400 rounded-xl px-4 py-3 text-sm focus:border-[#79c9ea] focus:ring-1 focus:ring-[#79c9ea] outline-none uppercase" required />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-500 block mb-1">CODE POSTAL *</label>
                      <input type="text" inputMode="numeric" autoComplete="postal-code" maxLength={5} value={form.codePostal} onChange={(e) => set('codePostal', e.target.value)} placeholder="69000" data-testid="input-code-postal"
                        className="w-full border-2 border-gray-400 rounded-xl px-4 py-3 text-sm focus:border-[#79c9ea] focus:ring-1 focus:ring-[#79c9ea] outline-none" required />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 block mb-1">VILLE *</label>
                      <input type="text" autoComplete="address-level2" value={form.ville} onChange={(e) => set('ville', e.target.value.toUpperCase())} placeholder="LYON" data-testid="input-ville"
                        className="w-full border-2 border-gray-400 rounded-xl px-4 py-3 text-sm focus:border-[#79c9ea] focus:ring-1 focus:ring-[#79c9ea] outline-none uppercase" required />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">ADRESSE E-MAIL *</label>
                    <input type="email" inputMode="email" autoComplete="email" autoCapitalize="none" value={form.email} onChange={(e) => set('email', e.target.value)} onBlur={(e) => checkPrefill(e.target.value)} placeholder="votre@email.com" data-testid="input-email" required
                      className="w-full border-2 border-gray-400 rounded-xl px-4 py-3 text-sm focus:border-[#79c9ea] focus:ring-1 focus:ring-[#79c9ea] outline-none" />
                    {/* Iter 823 (Kamel) — pédagogique uniquement : un e-mail = un espace adhérent */}
                    <div className="mt-1.5 text-[11px] text-[#1E3A5F] leading-snug space-y-0.5" data-testid="email-aide-espace">
                      <p>👋 <strong>Déjà venu ?</strong> Utilisez le même e-mail pour retrouver vos voyages.</p>
                      <p><strong>Un e-mail = un espace adhérent.</strong> Changement d'e-mail ? Contactez-nous avant de valider.</p>
                    </div>
                    <p className="mt-2 text-xs font-semibold text-amber-900 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2" data-testid="coords-avertissement">
                      ✋ Vérifiez bien votre adresse e-mail et votre numéro de téléphone, même si vous les avez copiés-collés. Ils nous permettent de vous envoyer votre récapitulatif et de vous contacter.
                    </p>
                    {/* Iter 848 (Kamel) — provenance déclarée (optionnel) : sert à savoir ce qui remplit les cars */}
                    <div className="mt-3">
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Comment avez-vous connu ce voyage ? <span className="font-normal text-gray-400">(facultatif, ça nous aide)</span></label>
                      <select value={form.connuPar || ''} onChange={(e) => set('connuPar', e.target.value)} data-testid="select-connu-par"
                        className="w-full border-2 border-gray-300 rounded-xl px-3 py-2 text-sm bg-white focus:border-[#79c9ea] outline-none">
                        <option value="">— Choisir —</option>
                        <option value="bouche_a_oreille">Bouche-à-oreille (famille, ami, voisin)</option>
                        <option value="whatsapp">Groupe WhatsApp</option>
                        <option value="facebook">Facebook</option>
                        <option value="instagram">Instagram</option>
                        <option value="tiktok">TikTok</option>
                        <option value="snapchat">Snapchat</option>
                        <option value="permanence">Permanence / affiche / marché</option>
                        <option value="deja_adherent">Je suis déjà adhérent(e)</option>
                        <option value="google">Recherche Google</option>
                        <option value="autre">Autre</option>
                      </select>
                    </div>
                    {prefill && (
                      <div className="mt-2 bg-emerald-50 border-2 border-emerald-300 rounded-xl p-3 flex flex-wrap items-center gap-2" data-testid="prefill-banner">
                        <p className="text-sm text-emerald-900 flex-1 min-w-[200px]">
                          👋 <strong>{prefill.prenom ? `Bonjour ${prefill.prenom} !` : 'Content de vous revoir !'}</strong> On vous connaît déjà — voulez-vous pré-remplir vos coordonnées (adresse, téléphone{(prefill.voyageurs || []).length ? ', voyageurs' : ''}) ?
                        </p>
                        <button type="button" onClick={appliquerPrefill}
                          className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold" data-testid="prefill-oui">
                          ✨ Oui, pré-remplir
                        </button>
                        <button type="button" onClick={() => { setPrefillData(null); setPrefillDone(true); }}
                          className="px-3 py-1.5 rounded-lg bg-gray-200 text-gray-600 text-xs font-bold" data-testid="prefill-non">
                          Non merci
                        </button>
                      </div>
                    )}
                    {/* Iter 825 — ancienne note « même adresse email » retirée : doublon de l'aide e-mail validée (Iter 823) */}
                  </div>
                  <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
                    <input type="checkbox" checked={form.certifieAdresse || false} onChange={e => set('certifieAdresse', e.target.checked)} data-testid="check-certifie-adresse"
                      className="w-5 h-5 mt-0.5 rounded border-gray-400 text-[#5BC5D4] focus:ring-[#5BC5D4] flex-shrink-0" />
                    <label className="text-xs text-gray-700 leading-relaxed cursor-pointer" onClick={() => set('certifieAdresse', !form.certifieAdresse)}>
                      <strong>Je certifie</strong> que toutes les personnes inscrites sur ce formulaire habitent bien à la même adresse ou sont mineures sous ma responsabilité. <span className="text-red-500">*</span>
                    </label>
                  </div>
                </div>
              </section>

              {/* PLACEMENT AUTOCAR (car) + RÉPARTITION CHAMBRE (nuitée) */}
              {selectedDest && (showBusDepart || hasNuitee) && (
              <section className={`bg-white rounded-2xl shadow-md overflow-hidden${st(3)}`} data-testid="section-placement">
                {/* PLACEMENT AUTOCAR */}
                {showBusDepart && (
                <>
                <SectionHeader title={T('titre_placement')} />
                <div className="p-5 space-y-3">
                  <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 text-sm text-sky-900 leading-relaxed" data-testid="placement-nouvelle-politique">
                    <p className="font-bold mb-1">{T('placement_titre')}</p>
                    <p>Le placement n'est plus effectué par les organisateurs. <strong>Dès votre premier paiement</strong>, vous recevrez un <strong>lien personnel « Mon Bus »</strong> (par email et WhatsApp, aussi dans votre Espace Adhérent) pour choisir vous-même vos places sur le plan de l'autocar, <strong>parmi les places disponibles au moment de votre choix</strong> — premiers arrivés, premiers servis.</p>
                    <p className="mt-1">👨‍👩‍👧‍👦 <strong>Vous voyagez à plusieurs familles ou entre amis ?</strong> Inscrivez-vous et payez <strong>en même temps</strong>, puis coordonnez-vous pour choisir vos places dès réception du lien : c'est la meilleure garantie d'être côte à côte.</p>
                    <p className="mt-1"><strong>Sans choix de votre part avant le départ</strong>, une place vous sera attribuée automatiquement par les organisateurs — attribution définitive et <strong>non négociable</strong>.</p>
                    <p className="mt-1 text-xs text-sky-700 italic">Vos places restent modifiables jusqu'au départ, selon les disponibilités. Voir la <a href="/charte-placement-bus" target="_blank" rel="noopener noreferrer" className="underline font-semibold">charte de placement</a>.</p>
                  </div>
                </div>
                </>
                )}

                {/* RÉPARTITION CHAMBRE — ancien système, masqué pour Lloret */}
                {hasNuitee && !isLloretTrip && (
                <>
                <SectionHeader title="RÉPARTITION DES CHAMBRES *" color={showBusDepart ? 'bg-[#2a4f7a]' : undefined} />
                <div className="p-5 space-y-4">
                  <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-sky-800 leading-relaxed">
                    <p>Indiquez comment vous souhaitez répartir les voyageurs dans les chambres. Vous pouvez ajouter des personnes inscrites sur <strong>un autre formulaire</strong> si vous partagez une chambre avec elles.</p>
                    <p className="mt-1.5"><strong>📌 Important :</strong> chaque voyageur doit d'abord être ajouté dans la section « VOYAGEURS » ci-dessus (carte orange « Une personne de ma famille »). C'est ici, ensuite, que vous décidez la répartition par chambre comme vous le souhaitez (qui dort avec qui).</p>
                    <p className="text-sky-600 italic mt-1">La disponibilité des types de chambres varie selon la destination et l'hôtel. Cette répartition n'est pas contractuelle et pourra être ajustée.</p>
                  </div>

                  {(() => {
                    const voyageursActifs = form.voyageurs.filter(v => v.nom.trim());
                    const totalAssignes = (form.chambres || []).reduce((s, c) => s + (c.occupants || []).length, 0);
                    const tousRepartis = voyageursActifs.length > 0 && totalAssignes >= voyageursActifs.length;
                    const isEspagne = selectedDest?.espagne || false;

                    // Helper: compte d'adultes (12+ à la date de départ) dans une chambre
                    const parseDDN = (ddn) => {
                      if (!ddn) return null;
                      const m = ddn.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                      if (!m) return null;
                      return new Date(+m[3], +m[2] - 1, +m[1]);
                    };
                    const dateDepart = selectedDest?.dateDepart ? new Date(selectedDest.dateDepart) : null;
                    const ageAuDepart = (ddn) => {
                      const d = parseDDN(ddn);
                      if (!d || !dateDepart) return null;
                      let age = dateDepart.getFullYear() - d.getFullYear();
                      const m = dateDepart.getMonth() - d.getMonth();
                      if (m < 0 || (m === 0 && dateDepart.getDate() < d.getDate())) age--;
                      return age;
                    };

                    return (
                      <>
                        {(form.chambres || [{ occupants: [], compagnons: [], autreOccupants: '', typeChambre: '', partageSolo: false }]).map((chambre, chIdx) => {
                          const maxOccChambre = getMaxOccupantsChambre(selectedDest?.id, selectedDest?.group, DESTINATIONS);
                          const compagnons = Array.isArray(chambre.compagnons) ? chambre.compagnons : [];
                          const autresNames = chambre.autreOccupants?.trim() ? chambre.autreOccupants.split(',').filter(n => n.trim()) : [];
                          const compagnonsCount = compagnons.length + autresNames.length;
                          const partageSolo = !!chambre.partageSolo;
                          const occupantsInscrits = (chambre.occupants || []).length;
                          const occupantCount = occupantsInscrits + compagnonsCount + (partageSolo ? 1 : 0);
                          const typeOptions = occupantCount <= 1
                            ? [{ val: 'individuelle', label: 'Chambre individuelle' }]
                            : occupantCount === 2
                              ? [{ val: 'grand-lit', label: '1 grand lit (couple, non garanti)' }, { val: '2-lits', label: '2 lits simples' }]
                              : occupantCount === 3
                                ? [{ val: '3-lits', label: '3 lits simples' }]
                                : [{ val: '4-lits', label: '4 lits (quadruple)' }];
                          const autoType = typeOptions.length === 1 ? typeOptions[0].val : '';
                          const displayType = typeOptions.length === 1 ? autoType : (chambre.typeChambre || '');
                          const dejaAttribues = (form.chambres || []).flatMap((c, i) => i === chIdx ? [] : (c.occupants || []));

                          // Contrainte Espagne : 4 adultes (12+) interdits en quadruple
                          let espagneError = null;
                          if (isEspagne && occupantCount === 4) {
                            const adultesInscrits = (chambre.occupants || []).map(vi => voyageursActifs[vi]).filter(Boolean).filter(v => {
                              const age = ageAuDepart(v.dateNaissance);
                              return age === null || age >= 12;
                            }).length;
                            // Les "autres" d'un autre formulaire sont adultes sauf tranche « moins de 12 ans » déclarée (Iter 914)
                            const compagnonsMoins12 = (chambre.compagnons || []).filter(c => (c.nom || c.prenom) && c.tranche === 'moins12').length;
                            const adultesTotal = adultesInscrits + compagnonsCount - compagnonsMoins12 + (partageSolo ? 1 : 0);
                            if (adultesTotal >= 4) {
                              espagneError = "Chambre quadruple réservée aux familles : l'hôtel impose au moins 1 personne de moins de 12 ans.";
                            }
                          }

                          return (
                            <div key={chIdx} className={`rounded-xl p-4 border ${espagneError ? 'bg-red-50 border-red-400' : 'bg-gray-50 border-gray-200'}`} data-testid={`chambre-${chIdx}`}>
                              <div className="flex items-center justify-between mb-3">
                                <p className="text-sm font-bold text-[#1E3A5F]">Chambre {chIdx + 1}</p>
                                {chIdx > 0 && (
                                  <button type="button" onClick={() => setForm(p => ({ ...p, chambres: p.chambres.filter((_, i) => i !== chIdx) }))}
                                    className="text-[10px] text-red-500 hover:text-red-700 font-semibold">Supprimer</button>
                                )}
                              </div>
                              <div className="space-y-3">
                                <div>
                                  <label className="text-xs text-[#1E3A5F] font-bold block mb-1">
                                    Qui dort dans cette chambre ? <span className="text-red-500">*</span>
                                  </label>
                                  <p className="text-[11px] text-gray-500 italic leading-snug mb-2">
                                    <strong>Cliquez sur le(s) prénom(s)</strong> des voyageurs de ce formulaire qui dormiront dans <strong>cette chambre</strong>. Les boutons passent en bleu foncé pour indiquer qu'ils sont bien sélectionnés.{voyageursActifs.length > 1 ? " Si plusieurs voyageurs partagent plusieurs chambres, faites la répartition chambre par chambre." : ""}
                                  </p>
                                  <div className="flex flex-wrap gap-2">
                                    {voyageursActifs.map((v, vIdx) => {
                                      const nom = `${v.nom} ${v.prenom}`.trim();
                                      const isInThisChambre = (chambre.occupants || []).includes(vIdx);
                                      const isInOther = dejaAttribues.includes(vIdx);
                                      // Blocage Espagne : empêche d'ajouter un 4e adulte 12+ dans la chambre
                                      let blockedEspagne = false;
                                      if (isEspagne && !isInThisChambre && !isInOther) {
                                        const ageV = ageAuDepart(v.dateNaissance);
                                        const vIsAdulte = ageV === null || ageV >= 12;
                                        if (vIsAdulte) {
                                          const adultesInscritsActuels = (chambre.occupants || []).map(oi => voyageursActifs[oi]).filter(Boolean).filter(vv => { const a = ageAuDepart(vv.dateNaissance); return a === null || a >= 12; }).length;
                                          const adultesTotalSiAjoute = adultesInscritsActuels + compagnonsCount + (partageSolo ? 1 : 0) + 1;
                                          const occupantTotalSiAjoute = occupantCount + 1;
                                          if (occupantTotalSiAjoute === 4 && adultesTotalSiAjoute >= 4) blockedEspagne = true;
                                        }
                                      }
                                      return (
                                        <button key={vIdx} type="button" disabled={isInOther || blockedEspagne}
                                          title={blockedEspagne ? "Espagne : une chambre quadruple nécessite au moins 1 personne de moins de 12 ans. Merci de créer une deuxième chambre." : undefined}
                                          onClick={() => {
                                            setForm(p => {
                                              const ch = [...(p.chambres || [])];
                                              const occ = [...(ch[chIdx].occupants || [])];
                                              ch[chIdx] = { ...ch[chIdx], occupants: isInThisChambre ? occ.filter(i => i !== vIdx) : [...occ, vIdx] };
                                              return { ...p, chambres: ch };
                                            });
                                          }}
                                          className={`text-xs px-3 py-1.5 rounded-lg border transition ${
                                            isInThisChambre ? 'bg-[#1E3A5F] text-white border-[#1E3A5F] font-bold' :
                                            isInOther ? 'bg-gray-100 text-gray-300 border-gray-100 cursor-not-allowed line-through' :
                                            blockedEspagne ? 'bg-red-50 text-red-400 border-red-200 cursor-not-allowed' :
                                            'border-gray-400 text-gray-600 hover:border-[#79c9ea]'
                                          }`}
                                          data-testid={`chambre-${chIdx}-voy-${vIdx}`}>
                                          {nom}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>

                                {/* Option "autre voyageur solo" : retirée sur demande utilisateur (trop lourde, <10% des cas) */}

                                <div>
                                  <label className="text-[10px] text-violet-700 font-bold block mb-1" data-testid={`chambre-${chIdx}-compagnons-titre`}>
                                    🤝 CHAMBRE UNIQUEMENT · INSCRIPTION SÉPARÉE (autre adresse) {maxOccChambre > 0 && <span className="text-gray-400 normal-case font-normal">(max {maxOccChambre} par chambre)</span>}
                                  </label>
                                  {compagnons.map((comp, cIdx) => (
                                    <div key={cIdx} className="mb-2 space-y-1">
                                      <div className="flex items-center gap-2">
                                        <input type="text" value={comp.nom || ''} placeholder="NOM"
                                          onChange={e => setForm(p => {
                                            const ch = [...(p.chambres || [])];
                                            const list = [...(ch[chIdx].compagnons || [])];
                                            list[cIdx] = { ...list[cIdx], nom: e.target.value.toUpperCase() };
                                            ch[chIdx] = { ...ch[chIdx], compagnons: list };
                                            return { ...p, chambres: ch };
                                          })}
                                          data-testid={`chambre-${chIdx}-compagnon-${cIdx}-nom`}
                                          className="flex-1 border-2 border-gray-400 rounded-lg px-3 py-2 text-xs uppercase focus:border-[#79c9ea] focus:ring-1 focus:ring-[#79c9ea] outline-none" />
                                        <input type="text" value={comp.prenom || ''} placeholder="PRÉNOM"
                                          onChange={e => setForm(p => {
                                            const ch = [...(p.chambres || [])];
                                            const list = [...(ch[chIdx].compagnons || [])];
                                            list[cIdx] = { ...list[cIdx], prenom: e.target.value.toUpperCase() };
                                            ch[chIdx] = { ...ch[chIdx], compagnons: list };
                                            return { ...p, chambres: ch };
                                          })}
                                          data-testid={`chambre-${chIdx}-compagnon-${cIdx}-prenom`}
                                          className="flex-1 border-2 border-gray-400 rounded-lg px-3 py-2 text-xs uppercase focus:border-[#79c9ea] focus:ring-1 focus:ring-[#79c9ea] outline-none" />
                                        <button type="button"
                                          onClick={() => setForm(p => {
                                            const ch = [...(p.chambres || [])];
                                            const list = [...(ch[chIdx].compagnons || [])];
                                            list.splice(cIdx, 1);
                                            ch[chIdx] = { ...ch[chIdx], compagnons: list };
                                            return { ...p, chambres: ch };
                                          })}
                                          className="text-red-500 hover:text-red-700 text-xs font-bold w-6 h-8 flex items-center justify-center"
                                          data-testid={`chambre-${chIdx}-compagnon-${cIdx}-remove`}
                                          title="Retirer cette personne">
                                          ✕
                                        </button>
                                      </div>
                                      {/* Date de naissance — uniquement pour Lloret (Espagne), pour calculer la réduction */}
                                      {isLloretTrip && (
                                        <div className="flex items-center gap-2 pl-1">
                                          <label className="text-[10px] text-gray-500 font-semibold whitespace-nowrap">DATE NAISSANCE :</label>
                                          <input type="text" value={comp.dateNaissance || ''}
                                            onChange={e => {
                                              const clean = e.target.value.replace(/[^\d]/g, '');
                                              let formatted = '';
                                              if (clean.length <= 2) formatted = clean;
                                              else if (clean.length <= 4) formatted = clean.slice(0, 2) + '/' + clean.slice(2);
                                              else formatted = clean.slice(0, 2) + '/' + clean.slice(2, 4) + '/' + clean.slice(4, 8);
                                              setForm(p => {
                                                const ch = [...(p.chambres || [])];
                                                const list = [...(ch[chIdx].compagnons || [])];
                                                list[cIdx] = { ...list[cIdx], dateNaissance: formatted };
                                                ch[chIdx] = { ...ch[chIdx], compagnons: list };
                                                return { ...p, chambres: ch };
                                              });
                                            }}
                                            placeholder="JJ/MM/AAAA" maxLength={10}
                                            data-testid={`chambre-${chIdx}-compagnon-${cIdx}-ddn`}
                                            className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-xs focus:border-[#79c9ea] focus:ring-1 focus:ring-[#79c9ea] outline-none" />
                                          {(() => {
                                            const age = ageAuDepartNum(comp.dateNaissance);
                                            if (age === null) {
                                              return <span className="text-[10px] text-gray-400 italic whitespace-nowrap">(optionnel — sinon compté adulte)</span>;
                                            }
                                            const cat = catFromAge(age);
                                            const label = cat === 'baby' ? 'Bébé' : cat === 'child' ? 'Enfant' : 'Adulte';
                                            const color = cat === 'baby' ? 'bg-purple-100 text-purple-700' : cat === 'child' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700';
                                            return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${color}`}>{label} ({age} ans)</span>;
                                          })()}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                  {occupantCount < maxOccChambre && maxOccChambre > 0 && (
                                    <>
                                      <button type="button"
                                        onClick={() => setForm(p => {
                                          const ch = [...(p.chambres || [])];
                                          const list = [...(ch[chIdx].compagnons || []), { nom: '', prenom: '', dateNaissance: '' }];
                                          ch[chIdx] = { ...ch[chIdx], compagnons: list };
                                          return { ...p, chambres: ch };
                                        })}
                                        className="mt-2 w-full bg-violet-500 hover:bg-violet-600 text-white rounded-lg py-2.5 text-sm font-bold shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
                                        data-testid={`chambre-${chIdx}-add-compagnon`}>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6"/></svg>
                                        NON — Autre adresse : ajouter pour la chambre uniquement
                                      </button>
                                      <p className="mt-1.5 text-[11px] text-violet-800 italic text-center px-2 leading-snug">
                                        👉 Nom et prénom uniquement pour la chambre. Pas d'inscription ni de facture sur ce formulaire : elle devra remplir son propre formulaire. <TutoLienChoix etape={2} />
                                      </p>
                                    </>
                                  )}
                                  {occupantCount >= maxOccChambre && maxOccChambre > 0 && (
                                    <p className="text-[10px] text-gray-500 italic">Capacité maximale atteinte ({maxOccChambre} personnes pour ce voyage).</p>
                                  )}
                                  <p className="text-[10px] text-gray-400 italic mt-1 leading-snug">
                                    ℹ️ Si la personne qui partage cette chambre a aussi rempli son propre formulaire, nous détecterons automatiquement le doublon et consoliderons les fiches.
                                  </p>
                                </div>
                                <div>
                                  <label className="text-[10px] text-gray-500 block mb-1.5">TYPE DE CHAMBRE SOUHAITÉ {!displayType && typeOptions.length > 1 && <span className="text-orange-600 font-bold">*</span>}</label>
                                  {/* Iter 95 — boutons radio visibles (chambre à 2 personnes : grand lit OU 2 lits séparés).
                                      Pour les chambres à 1 occupant, choix unique → on garde le select disabled. */}
                                  {typeOptions.length > 1 ? (
                                    <div className="flex flex-col gap-1.5" data-testid={`chambre-${chIdx}-type`}>
                                      {typeOptions.map(o => (
                                        <label key={o.val}
                                          className={`flex items-center gap-2 cursor-pointer border-2 rounded-lg px-3 py-2 transition ${
                                            chambre.typeChambre === o.val
                                              ? 'border-[#1E3A5F] bg-sky-100 ring-2 ring-[#79c9ea]'
                                              : 'border-gray-300 bg-white hover:border-[#79c9ea] hover:bg-sky-50'
                                          }`}>
                                          <input type="radio"
                                            name={`chambre-${chIdx}-type`}
                                            value={o.val}
                                            checked={chambre.typeChambre === o.val}
                                            onChange={() => setForm(p => {
                                              const ch = [...(p.chambres || [])];
                                              ch[chIdx] = { ...ch[chIdx], typeChambre: o.val };
                                              return { ...p, chambres: ch };
                                            })}
                                            className="w-4 h-4"
                                            data-testid={`chambre-${chIdx}-type-${o.val}`}
                                            required />
                                          <span className="text-xs font-bold text-[#1E3A5F]">{o.label}</span>
                                        </label>
                                      ))}
                                      {!chambre.typeChambre && (
                                        <p className="text-[10px] text-orange-600 italic font-semibold">⚠ Choix obligatoire pour les chambres à 2 personnes (un grand lit ne convient pas à tous les binômes).</p>
                                      )}
                                    </div>
                                  ) : (
                                    <select value={displayType} disabled={typeOptions.length === 1}
                                      onChange={e => setForm(p => {
                                        const ch = [...(p.chambres || [])];
                                        ch[chIdx] = { ...ch[chIdx], typeChambre: e.target.value };
                                        return { ...p, chambres: ch };
                                      })}
                                      data-testid={`chambre-${chIdx}-type`}
                                      className="px-3 py-2 border border-gray-400 rounded-lg text-xs focus:border-[#79c9ea] outline-none">
                                      <option value="">-- Choisir --</option>
                                      {typeOptions.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
                                    </select>
                                  )}
                                  {isEspagne && occupantCount === 4 && (
                                    <p className="text-[10px] text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1.5 mt-1 leading-relaxed flex items-start gap-1">
                                      <span className="font-bold flex-shrink-0">ℹ️</span>
                                      <span>Chambre quadruple en Espagne : l'hôtel impose <strong>au moins 1 personne de moins de 12 ans</strong> (âge calculé à la date de départ). Sans cela, merci de prévoir 2 chambres doubles/triple.</span>
                                    </p>
                                  )}
                                </div>

                                {espagneError && (
                                  <div className="bg-red-100 border border-red-400 text-red-800 text-xs rounded-lg p-2 font-semibold">
                                    ⚠️ {espagneError}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}

                        {/* Bouton "Ajouter une chambre" OU message "toutes réparties" */}
                        {tousRepartis ? (
                          <div className="bg-green-50 border-2 border-green-300 rounded-lg p-3 text-xs text-green-800 font-semibold text-center" data-testid="chambres-all-assigned">
                            ✓ Toutes les personnes de ce formulaire ont été sélectionnées.
                          </div>
                        ) : (
                          <button type="button" onClick={() => setForm(p => ({ ...p, chambres: [...(p.chambres || []), { occupants: [], compagnons: [], autreOccupants: '', typeChambre: '', partageSolo: false }] }))}
                            className="text-sm text-[#79c9ea] hover:text-[#5ba8c8] font-semibold flex items-center gap-1"
                            data-testid="add-chambre-btn">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6"/></svg>
                            Ajouter une chambre
                          </button>
                        )}

                        {/* ===== WARNING SOLO — retiré sur demande utilisateur (cas rare, géré manuellement par l'association) ===== */}

                        {/* ===== CHAMBRE INDIVIDUELLE AUTO-DÉTECTÉE (iter 67) ===== */}
                        {chambresIndividuellesDetectees.length > 0 && (() => {
                          // Construire les infos par chambre (seul OU optimisé adulte+bébé)
                          const noms = chambresIndividuellesDetectees.map(({ chIdx, voyageurIdx, reason }) => {
                            const v = form.voyageurs[voyageurIdx] || {};
                            const label = `${v.prenom || ''} ${v.nom || ''}`.trim() || `Voyageur ${voyageurIdx + 1}`;
                            const suffix = reason === 'optimized' ? ' [tarif optimisé : plein + suppl. individuel au lieu de 2 pleins tarifs]' : '';
                            return `${label} (Chambre ${chIdx + 1})${suffix}`;
                          });
                          // Cas 1 : chambre ind désactivée (admin) → BLOQUE
                          if (chambreIndInfo.disabledByAdmin) {
                            return (
                              <div className="bg-red-50 border-2 border-red-500 rounded-xl p-4 space-y-2" data-testid="chambre-ind-indispo-banner">
                                <p className="font-bold text-red-800 text-sm">🚫 Chambre individuelle non disponible sur ce voyage</p>
                                <p className="text-xs text-red-800 leading-relaxed">
                                  {noms.length === 1 ? noms[0] + ' est seul(e)' : noms.join(' et ') + ' sont seul(e)s'} dans une chambre, mais l'hôtel ne propose pas (ou plus) de chambre individuelle sur ce voyage.
                                </p>
                                <p className="text-xs text-red-800">
                                  Merci de <strong>nous contacter avant de valider</strong> pour trouver une solution (partage avec un(e) autre voyageur(se), report sur un autre voyage, ou confirmation qu'une chambre individuelle peut être débloquée).
                                </p>
                                <a href="https://wa.me/message/XTYPH2HUY3L6L1" target="_blank" rel="noopener noreferrer"
                                  className="inline-block bg-[#25D366] hover:bg-[#1ebe57] text-white font-bold px-4 py-2 rounded-lg text-xs transition">
                                  Nous contacter par WhatsApp
                                </a>
                              </div>
                            );
                          }
                          // Cas 2 : montant NON configuré → WARNING avec WhatsApp
                          if (!chambreIndInfo.montant || chambreIndInfo.montant <= 0) {
                            return (
                              <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-4 space-y-2" data-testid="chambre-ind-montant-manquant-banner">
                                <p className="font-bold text-amber-900 text-sm">⚠️ Chambre individuelle détectée — montant à confirmer</p>
                                <p className="text-xs text-amber-900 leading-relaxed">
                                  {noms.length === 1 ? noms[0] + ' est seul(e)' : noms.join(' et ') + ' sont seul(e)s'} dans une chambre. Un <strong>supplément chambre individuelle</strong> s'applique, mais son montant n'est pas encore publié pour ce voyage.
                                </p>
                                <p className="text-xs text-amber-900">
                                  Nous vous communiquerons le montant exact par retour. Vous pouvez finaliser votre inscription : le supplément sera ajouté à votre facture.
                                </p>
                                <a href="https://wa.me/message/XTYPH2HUY3L6L1" target="_blank" rel="noopener noreferrer"
                                  className="inline-block bg-[#25D366] hover:bg-[#1ebe57] text-white font-bold px-3 py-1.5 rounded-lg text-xs transition">
                                  Demander le montant par WhatsApp
                                </a>
                              </div>
                            );
                          }
                          // Cas 3 : OK — supplément gardé en arrière-plan (dépliable, discret)
                          return (
                            <details className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-500" data-testid="chambre-ind-auto-banner">
                              <summary className="cursor-pointer select-none italic">ℹ️ Chambre individuelle — un supplément peut s'appliquer si vous restez seul(e) <span className="underline">(voir le détail)</span></summary>
                              <div className="mt-2 space-y-1.5">
                                <ul className="text-xs text-gray-600 space-y-0.5">
                                  {noms.map((n, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                      <span className="text-gray-400 font-bold">•</span>
                                      <span><strong>{n}</strong> — supplément {chambreIndInfo.montant} €</span>
                                    </li>
                                  ))}
                                </ul>
                                <p className="text-xs text-gray-600">
                                  Total estimé : <span className="font-bold">{totalSupplementAuto.toLocaleString('fr-FR')} €</span> — il sera visible dans le récapitulatif avant l'envoi.
                                </p>
                                <p className="text-[11px] text-gray-500 italic leading-snug">
                                  💡 Ce supplément ne s'applique que si vous restez seul(e) dans la chambre : il disparaît automatiquement dès qu'une autre personne partage votre chambre.
                                </p>
                              </div>
                            </details>
                          );
                        })()}

                        {/* Lien WhatsApp pour aide */}
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 text-center">
                          Vous avez du mal avec cette partie ? <a href="https://wa.me/message/XTYPH2HUY3L6L1" target="_blank" rel="noopener noreferrer" className="text-[#25D366] font-bold underline">Contactez-nous par WhatsApp</a>, on vous aide.
                        </div>
                      </>
                    );
                  })()}
                </div>
                </>
                )}
              </section>
              )}

              {/* ADHÉSION ANNUELLE – OBLIGATOIRE (année dynamique selon le voyage)
                  Iter 99 — Masquée pour les voyages à la journée (non concernés) */}
              {hasNuitee && (
              <section className={`bg-white rounded-2xl shadow-md overflow-hidden${st(3)}`} data-testid="section-adhesion">
                <SectionHeader title={`ADHÉSION ANNUELLE ${voyageYear} – OBLIGATOIRE`} color="bg-[#1E3A5F]" />
                <div className="p-5 space-y-3">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800 leading-relaxed">
                    <p className="font-semibold mb-1">L'adhésion annuelle de 10 € par famille est obligatoire pour participer à nos voyages (séjours).</p>
                    <p className="whitespace-pre-line">{T('adhesion_texte').replaceAll('{annee}', String(voyageYear)).replaceAll('{annee_prec}', String(voyageYear - 1))}</p>
                  </div>
                  {adhesionDeja && (
                    <div className="bg-emerald-50 border border-emerald-300 rounded-lg p-3 text-xs text-emerald-900 font-semibold" data-testid="adhesion-deja-detectee">
                      ✅ Bonne nouvelle : votre adhésion {voyageYear} est déjà réglée ({adhesionDeja.voyage}). Nous l'avons pré-cochée, rien à payer de plus.
                    </div>
                  )}
                  {/* Iter 763 (Kamel) — « Déjà inscrit » disponible pour TOUTE année : une inscription 2027 faite en 2026 compte pour l'adhésion 2027 */}
                  <Radio name="adhesion" value="deja-inscrit" label={`Déjà adhérent ${voyageYear} : adhésion ${voyageYear} déjà réglée sur un autre voyage ${voyageYear}`} current={form.adhesion} onClick={() => { set('adhesion', 'deja-inscrit'); set('montantAdhesion', '0'); }} />
                  {form.adhesion === 'deja-inscrit' && (
                    <div className="ml-8">
                      <label className="text-xs text-gray-500 block mb-1">Sur quel voyage {voyageYear} avez-vous réglé votre adhésion {voyageYear} ? (vérifié par nos soins — une adhésion {voyageYear - 1} ne vaut pas pour {voyageYear})</label>
                      <input type="text" value={form.adhesionVoyagePrecedent || ''} onChange={e => set('adhesionVoyagePrecedent', e.target.value)}
                        placeholder={voyageYear >= 2027 ? 'Ex : Lloret 17 → 24 juillet, Thaïlande janvier...' : 'Ex : Istanbul, Turin 23 mai...'}
                        className="w-full max-w-sm px-3 py-2 border border-gray-400 rounded-lg text-sm focus:border-[#79c9ea] focus:ring-1 focus:ring-[#79c9ea] outline-none" data-testid="input-adhesion-voyage-precedent" />
                    </div>
                  )}
                  <Radio name="adhesion" value="premier-voyage" label={`1er voyage ${voyageYear} – je règle 10 €`} current={form.adhesion} onClick={() => { set('adhesion', 'premier-voyage'); set('montantAdhesion', '10'); }} />
                </div>
              </section>
              )}

              {/* ASSURANCE MULTIRISQUE
                  Iter 99 — Masquée pour les voyages à la journée (assurance non proposée) */}
              {hasNuitee && (
              <section className={`bg-white rounded-2xl shadow-md overflow-hidden${st(3)}`} data-testid="section-assurance">
                <SectionHeader title={T('titre_assurance')} color="bg-[#1E3A5F]" />
                <div className="p-5 space-y-3">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs text-gray-700 leading-relaxed">
                    <p className="font-bold text-[#1E3A5F] mb-2">{T('assurance_couvre')}</p>
                    <ul className="space-y-1.5 ml-1">
                      <li className="flex items-start gap-2"><span className="text-[#79c9ea] font-bold flex-shrink-0">&#10003;</span><span><strong>Annulation avant départ</strong> — maladie, accident, décès, licenciement, refus de visa, etc.</span></li>
                      <li className="flex items-start gap-2"><span className="text-[#79c9ea] font-bold flex-shrink-0">&#10003;</span><span><strong>Assistance rapatriement</strong> — transport médical, retour anticipé, recherche en mer/montagne</span></li>
                      <li className="flex items-start gap-2"><span className="text-[#79c9ea] font-bold flex-shrink-0">&#10003;</span><span><strong>Frais médicaux à l'étranger</strong> — hospitalisation, soins dentaires d'urgence</span></li>
                      <li className="flex items-start gap-2"><span className="text-[#79c9ea] font-bold flex-shrink-0">&#10003;</span><span><strong>Pertes et vol de bagages</strong> — remboursement sur justificatifs</span></li>
                      <li className="flex items-start gap-2"><span className="text-[#79c9ea] font-bold flex-shrink-0">&#10003;</span><span><strong>Interruption de voyage</strong> — remboursement des prestations non utilisées</span></li>
                      <li className="flex items-start gap-2"><span className="text-[#79c9ea] font-bold flex-shrink-0">&#10003;</span><span><strong>Protection accident corporel</strong> — capital décès, incapacité permanente</span></li>
                    </ul>
                    <p className="mt-3 text-gray-500 italic">{T('assurance_details')}</p>
                  </div>

                  {/* Iter 765 (Kamel — smartphone) : 3 encadrés fusionnés en un seul, compact */}
                  <div className="bg-sky-50 border-2 border-sky-300 rounded-lg p-3 text-xs text-[#1E3A5F] leading-relaxed space-y-1.5" data-testid="assurance-regles">
                    {selectedDest?.assurance > 0 && (
                      <p data-testid="assurance-tarif-voyage">💶 <span className="font-semibold">Tarif pour ce voyage :</span> <strong>{selectedDest.assurance} € par personne</strong>, appliqué automatiquement.</p>
                    )}
                    <p>⏱️ <strong>À souscrire en même temps que le voyage</strong> — après l'inscription, seulement à titre exceptionnel (nous contacter).</p>
                    <p className="text-red-700">⚠️ <strong>Sans assurance, en cas d'annulation, les frais restent intégralement à votre charge.</strong></p>
                    <p data-testid="interlocuteur-assurance">🤝 <strong>En cas de sinistre, l'association fait les démarches pour vous</strong> : prévenez immédiatement les accompagnateurs sur place.</p>
                  </div>

                  <div className="border-t border-gray-200 pt-3 mt-3 space-y-2">
                    <Radio name="assurance" value="oui" label="JE SOUSCRIS À L'ASSURANCE MULTIRISQUE POUR TOUS LES VOYAGEURS DE CE FORMULAIRE" current={form.assurance} onClick={() => {
                      set('assurance', 'oui');
                      // Tarif pris directement du catalogue (champ 'assurance' de chaque destination)
                      const tarif = selectedDest?.assurance || 0;
                      set('montantAssurance', String(tarif));
                    }} />
                    {form.assurance === 'oui' && (
                      <div className="ml-8 bg-green-50 border border-green-200 rounded-lg p-3">
                        <p className="text-sm text-green-800 font-bold">
                          {form.montantAssurance || 0} € / personne x {form.voyageurs.filter(v => v.nom.trim()).length} voyageur(s) = <span className="text-lg">{(parseFloat(form.montantAssurance) || 0) * form.voyageurs.filter(v => v.nom.trim()).length} €</span>
                        </p>
                        <p className="text-[10px] text-green-600 mt-1 italic">Tarif appliqué automatiquement selon la destination.</p>
                      </div>
                    )}
                    <Radio name="assurance" value="non" label="JE REFUSE L'ASSURANCE. En cas d'annulation qui relève de ma décision, les frais d'annulation sont ceux indiqués sur la facture." current={form.assurance} onClick={() => { set('assurance', 'non'); set('montantAssurance', '0'); }} />
                  </div>
                </div>
              </section>
              )}

              {/* RÉCAPITULATIF + ACOMPTE — tout automatique */}
              <section className={`bg-white rounded-2xl shadow-md overflow-hidden${st(3)}`} data-testid="section-calcul">
                <SectionHeader title="RÉCAPITULATIF FINANCIER" color="bg-[#79c9ea]" />
                <div className="p-5 space-y-4">
                  {/* Info Espagne (Lloret) : tarifs calculés automatiquement selon la répartition des chambres.
                      Iter 577 (Kamel) — affiché UNIQUEMENT pour les voyages en Espagne (plus sur Algérie, Thaïlande…). */}
                  {isLloretTrip && (isEspagne || isLloretOnly) && (() => {
                    const missingDDN = lloretCompagnonsMissingDDN();
                    return (
                      <div className="bg-sky-50 border-2 border-sky-300 rounded-xl p-3 space-y-2" data-testid="lloret-auto-info">
                        {/* Iter 765 (Kamel — smartphone) : résumé en 1 phrase, le détail des règles est déjà dans « Organisez vos chambres » */}
                        <p className="text-xs text-[#1E3A5F] leading-relaxed">ℹ️ <strong>Tarifs calculés automatiquement</strong> selon la date de naissance et la composition finale de chaque chambre (règle hôtel : 2 pleins tarifs par chambre pour ouvrir la réduction enfant 3–11 ans ; bébés &lt; 3 ans toujours réduits).</p>
                        {missingDDN.length > 0 && (
                          <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 text-xs text-amber-900" data-testid="lloret-missing-compagnon-ddn">
                            <p className="font-bold mb-1">⚠️ Dates de naissance manquantes pour {missingDDN.length} personne{missingDDN.length > 1 ? 's' : ''} d'un autre formulaire :</p>
                            <ul className="list-disc ml-5 space-y-0.5">
                              {missingDDN.map((m, i) => (
                                <li key={i}>
                                  Chambre {m.chIdx + 1} — <strong>{m.nom} {m.prenom}</strong> : renseignez sa DDN pour éventuellement bénéficier de la réduction enfant sur votre (vos) enfant(s). Sans DDN, la personne est considérée <strong>adulte plein tarif</strong>.
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {selectedDest && (() => {
                    const voyageursActifs = form.voyageurs.filter(v => v.nom.trim());
                    const sommeForfaits = voyageursActifs.reduce((s, v) => s + (parseFloat(v.forfait) || 0), 0);
                    const adhesion = parseFloat(form.montantAdhesion) || 0;
                    const assUnit = parseFloat(form.montantAssurance) || 0;
                    const assTotal = assUnit * voyageursActifs.length;
                    const suppChambre = form.supplementChambre ? (parseFloat(form.montantSupplementChambre) || 0) : 0;
                    // Iter 778 — semaine 1 seule (= la fiche « mère ») vs séjour complet (toutes les semaines)
                    const s1HorsAss = sommeForfaits + suppChambre + supplementPiscine;
                    const sommeForfaitsAvecSupp = s1HorsAss + totalSemainesSupp;
                    const horsAssToutesSemaines = s1HorsAss + semainesSupp.reduce((a, w) => a + w.horsAss, 0);
                    if ((parseFloat(form.supplementPiscine) || 0) !== supplementPiscine) setTimeout(() => set('supplementPiscine', supplementPiscine), 0);
                    const totalVoyageS1 = s1HorsAss + adhesion + assTotal;
                    const totalVoyage = sommeForfaitsAvecSupp + adhesion + assTotal;

                    // Calcul automatique du pourcentage selon la date de départ — règle RAAV 20/50/100
                    // Cohérent avec backend invoice_generator.compute_acompte_minimum()
                    // - J > 180 jours    → 20%
                    // - 180 ≥ J ≥ 45 j   → 50%
                    // - J < 45 jours     → 100%
                    // On compare en dates calendaires (sans heure) pour eviter les decalages
                    const dateDepart = selectedDest.dateDepart ? new Date(selectedDest.dateDepart) : null;
                    const aujourdHui = new Date();
                    aujourdHui.setHours(0, 0, 0, 0);
                    if (dateDepart) dateDepart.setHours(0, 0, 0, 0);
                    const joursAvantDepart = dateDepart ? Math.round((dateDepart - aujourdHui) / (1000 * 60 * 60 * 24)) : 999;
                    let pctAuto, pctLabel;
                    // Voyage à la journée (pas de nuitée) : toujours 100% à l'inscription
                    if (!hasNuitee) { pctAuto = 1; pctLabel = 'Totalité 100% (voyage à la journée)'; }
                    else if (joursAvantDepart > 180) { pctAuto = 0.2; pctLabel = 'Acompte 20% (départ dans plus de 6 mois)'; }
                    else if (joursAvantDepart >= 45) { pctAuto = 0.5; pctLabel = 'Acompte 50% (départ dans moins de 6 mois)'; }
                    else { pctAuto = 1; pctLabel = 'Règlement intégral 100% (départ dans moins de 45 jours)'; }

                    const pctKey = pctAuto === 0.2 ? '20' : pctAuto === 0.5 ? '50' : '100';
                    if (form.paiement !== pctKey) {
                      setTimeout(() => set('paiement', pctKey), 0);
                    }

                    const acompteS1 = Math.ceil(s1HorsAss * pctAuto) + adhesion + assTotal;
                    const acompteForfaits = Math.ceil(s1HorsAss * pctAuto) + semainesSupp.reduce((a, w) => a + Math.ceil(w.horsAss * pctAuto), 0);
                    const assToutesSemaines = assTotal + semainesSupp.reduce((a, w) => a + w.assurance, 0);
                    const montantARegler = acompteForfaits + adhesion + assToutesSemaines;

                    if (montantARegler > 0) {
                      const stored = parseFloat(form.montantCalcule) || 0;
                      if (stored !== montantARegler || String(form._acompteS1 || '') !== String(acompteS1)) {
                        setTimeout(() => { set('montantCalcule', montantARegler.toString()); set('_totalVoyage', totalVoyage.toString()); set('_totalVoyageS1', totalVoyageS1.toString()); set('_acompteS1', acompteS1.toString()); }, 0);
                      }
                    }

                    return (
                      <>
                        <div className="bg-[#F0F9FF] border-2 border-[#79c9ea] rounded-xl p-4">
                          <p className="text-xs font-bold text-[#1E3A5F] mb-2">Détail du voyage :</p>
                          {isLloretEteCar && (
                            <p className="text-[11px] font-semibold text-[#1E3A5F] mb-2 bg-white border border-[#79c9ea] rounded px-2 py-1" data-testid="recap-trajets-car">
                              🚌 Car : {trajetsLegs.length === 2 ? `Aller + Retour (${prixTrajet} € × 2 par personne, inclus dans les forfaits)` : trajetsLegs.length === 1 ? `${trajetsLegs[0] === 'aller' ? 'Aller seulement' : 'Retour seulement'} (${prixTrajet} € par personne, −${prixTrajet} € déjà déduit des forfaits)` : 'aucun trajet choisi'}
                            </p>
                          )}
                          {isFusion && (
                            <p className="text-[11px] font-semibold text-[#1E3A5F] mb-2 bg-white border border-[#79c9ea] rounded px-2 py-1" data-testid="recap-transport-fusion">
                              {trajetsLegs.length === 0 ? '🏨 Sans transport — vous rejoignez l\'hôtel par vos propres moyens (aucun supplément)'
                                : nbSemainesEff > 1 ? `🚌 Séjour de ${nbSemainesEff} semaines : ${trajetsLegs.includes('aller') ? `car aller en début de semaine 1 (+${prixTrajet} €/pers.)` : 'aller par vos propres moyens'}${trajetsLegs.includes('retour') ? (semainesSupp.some(w => w.retourIndispo) ? `, ⚠️ retour par vos propres moyens (${semainesSupp.find(w => w.retourIndispo).retourMotif} — non facturé)` : `, car retour en fin de semaine ${nbSemainesEff} (+${prixTrajet} €/pers.)`) : ', retour par vos propres moyens'} — aucun car sur les semaines intermédiaires`
                                : trajetsLegs.length === 2 ? `🚌 Car aller + retour : +${prixTrajet} € × 2 par personne (ligne « Transport » ci-dessous, à part du forfait hôtel)`
                                : `🚌 Car ${trajetsLegs[0] === 'aller' ? 'aller' : 'retour'} seulement : +${prixTrajet} € par personne (ligne « Transport » ci-dessous, à part du forfait hôtel)`}
                            </p>
                          )}
                          <table className="w-full text-sm">
                            <tbody>
                              {/* Pour Lloret : affichage groupé par chambre. Iter 779 (Kamel) — Espagne fusionnée : forfait HÔTEL et TRANSPORT sur deux lignes séparées */}
                              {isLloretTrip ? (
                                (form.chambres || []).map((ch, chIdx) => {
                                  const occs = ch.occupants || [];
                                  const chambreVoys = occs.map(vi => form.voyageurs[vi]).filter(v => v && v.nom?.trim());
                                  if (chambreVoys.length === 0) return null;
                                  const trajetPP = isFusion ? supplementTrajets : 0;
                                  const chambreTotal = occs.reduce((s, vi) => s + (form.voyageurs[vi]?.nom?.trim() ? Math.max(0, (parseFloat(form.voyageurs[vi]?.forfait) || 0) - trajetPP) : 0), 0);
                                  return (
                                    <React.Fragment key={`recap-ch-${chIdx}`}>
                                      <tr className="bg-sky-100/50">
                                        <td colSpan={2} className="py-1.5 px-2 text-[11px] font-bold text-[#1E3A5F]">🏨 Chambre {chIdx + 1} ({chambreVoys.length} personne{chambreVoys.length > 1 ? 's' : ''}){nbSemainesEff > 1 ? ' — semaine 1' : ''}</td>
                                      </tr>
                                      {occs.map(vi => {
                                        const vv = form.voyageurs[vi];
                                        if (!vv || !vv.nom?.trim()) return null;
                                        return (
                                          <tr key={`recap-v-${vi}`} className="border-b border-sky-200">
                                            <td className="py-1.5 pl-5 text-gray-700 text-xs">{vv.prenom} {vv.nom} — {isFusion ? 'forfait hôtel' : 'forfait'}{nbSemainesEff > 1 ? ' semaine 1' : ''}</td>
                                            <td className="py-1.5 text-right font-semibold text-[#1E3A5F]">{Math.max(0, (parseFloat(vv.forfait) || 0) - trajetPP)} €</td>
                                          </tr>
                                        );
                                      })}
                                      <tr className="border-b-2 border-sky-300">
                                        <td className="py-1.5 pl-5 text-[#1E3A5F] italic text-xs">Sous-total chambre {chIdx + 1}{isFusion ? ' (hôtel)' : ''}</td>
                                        <td className="py-1.5 text-right font-bold text-[#1E3A5F]" data-testid={`recap-chambre-${chIdx}-total`}>{chambreTotal.toFixed(2)} €</td>
                                      </tr>
                                    </React.Fragment>
                                  );
                                })
                              ) : (
                                voyageursActifs.map((v, i) => (
                                  <tr key={i} className="border-b border-sky-200">
                                    <td className="py-1.5 text-gray-700">{v.nom} {v.prenom} — forfait</td>
                                    <td className="py-1.5 text-right font-bold text-[#1E3A5F]">{parseFloat(v.forfait) || 0} €</td>
                                  </tr>
                                ))
                              )}
                              {isFusion && transportActif && (
                                <tr className="border-b-2 border-sky-300 bg-sky-50/60" data-testid="recap-transport-semaine-1">
                                  <td className="py-1.5 px-2 text-xs font-semibold text-[#1E3A5F]">🚌 Transport{nbSemainesEff > 1 ? ' semaine 1' : ''} : {legsSemaine1.length === 0 ? 'par vos propres moyens (aucun car facturé)'
                                    : legsSemaine1.length === 2 ? `car aller + retour — ${prixTrajet} € × 2 × ${voyageursActifs.length} pers.`
                                    : `car ${legsSemaine1[0]} — ${prixTrajet} € × ${voyageursActifs.length} pers.`}</td>
                                  <td className="py-1.5 text-right font-bold text-[#1E3A5F]">{legsSemaine1.length ? `${supplementTrajets * voyageursActifs.length} €` : '—'}</td>
                                </tr>
                              )}
                              {nbSemainesEff > 1 && (<>
                                {suppChambre > 0 && (
                                  <tr className="border-b border-sky-200"><td className="py-1 pl-5 text-gray-700 text-xs">Supplément chambre individuelle semaine 1</td><td className="py-1 text-right font-semibold text-[#1E3A5F]">{suppChambre} €</td></tr>
                                )}
                                {supplementPiscine > 0 && (
                                  <tr className="border-b border-sky-200" data-testid="recap-piscine"><td className="py-1 pl-5 text-gray-700 text-xs">Vue piscine garantie semaine 1 (par chambre)</td><td className="py-1 text-right font-semibold text-[#1E3A5F]">{supplementPiscine} €</td></tr>
                                )}
                                {assTotal > 0 && (
                                  <tr className="border-b border-sky-200" data-testid="recap-semaine-1-assurance"><td className="py-1 pl-5 text-gray-700 text-xs">Assurance semaine 1 ({assUnit} € × {voyageursActifs.length} pers.)</td><td className="py-1 text-right font-semibold text-[#1E3A5F]">{assTotal} €</td></tr>
                                )}
                                <tr className="border-b-2 border-emerald-300 bg-emerald-50/60" data-testid="recap-semaine-1-total">
                                  <td className="py-1.5 px-2 text-[11px] font-bold text-emerald-900">📅 Sous-total semaine 1 — {selectedDest.label}</td>
                                  <td className="py-1.5 text-right font-bold text-[#1E3A5F]">{(s1HorsAss + assTotal).toFixed(2)} €</td>
                                </tr>
                              </>)}
                              {semainesSupp.map((w, wi) => (
                                <React.Fragment key={`recap-sem-${wi}`}>
                                  <tr className="bg-emerald-50">
                                    <td colSpan={2} className="py-1.5 px-2 text-[11px] font-bold text-emerald-900" data-testid={`recap-semaine-${wi + 2}`}>📅 Semaine {wi + 2} — {w.dest.label}{w.legs.length ? ` · car retour +${w.prixTrajet} €/pers.` : w.retourIndispo ? ` · ⚠️ ${w.retourMotif} : retour par vos propres moyens${w.sansCar ? '' : ' (ou liste d\'attente retour)'}` : ' · sans car'}{w.piscine ? ` · vue piscine +${w.piscine} €` : ''}</td>
                                  </tr>
                                  {Object.keys(w.forfaits).filter(k => form.voyageurs[k]?.nom?.trim()).map(k => (
                                    <tr key={`recap-sem-${wi}-${k}`} className="border-b border-emerald-100">
                                      <td className="py-1 pl-5 text-gray-700 text-xs">{form.voyageurs[k].prenom} {form.voyageurs[k].nom} — forfait hôtel semaine {wi + 2}</td>
                                      <td className="py-1 text-right font-semibold text-[#1E3A5F]">{Math.max(0, w.forfaits[k] - w.legs.length * w.prixTrajet)} €</td>
                                    </tr>
                                  ))}
                                  <tr className="border-b border-emerald-100 bg-emerald-50/40" data-testid={`recap-transport-semaine-${wi + 2}`}>
                                    <td className="py-1 px-2 text-xs font-semibold text-emerald-900">🚌 Transport semaine {wi + 2} : {w.legs.length
                                      ? `car retour — ${w.prixTrajet} € × ${Object.keys(w.forfaits).filter(k => form.voyageurs[k]?.nom?.trim()).length} pers.`
                                      : w.retourIndispo ? `retour par vos propres moyens (${w.retourMotif}) — non facturé`
                                      : wi === semainesSupp.length - 1 ? 'retour par vos propres moyens — aucun car facturé' : 'aucun (semaine intermédiaire, vous restez sur place)'}</td>
                                    <td className="py-1 text-right font-bold text-[#1E3A5F]">{w.legs.length ? `${w.legs.length * w.prixTrajet * Object.keys(w.forfaits).filter(k => form.voyageurs[k]?.nom?.trim()).length} €` : '—'}</td>
                                  </tr>
                                  {w.suppCh > 0 && (
                                    <tr className="border-b border-emerald-100">
                                      <td className="py-1 pl-5 text-gray-700 text-xs">Supplément chambre individuelle semaine {wi + 2}</td>
                                      <td className="py-1 text-right font-semibold text-[#1E3A5F]">{w.suppCh} €</td>
                                    </tr>
                                  )}
                                  {w.piscine > 0 && (
                                    <tr className="border-b border-emerald-100">
                                      <td className="py-1 pl-5 text-gray-700 text-xs">Vue piscine garantie semaine {wi + 2} (par chambre)</td>
                                      <td className="py-1 text-right font-semibold text-[#1E3A5F]">{w.piscine} €</td>
                                    </tr>
                                  )}
                                  {w.assurance > 0 && (
                                    <tr className="border-b border-emerald-100" data-testid={`recap-semaine-${wi + 2}-assurance`}>
                                      <td className="py-1 pl-5 text-gray-700 text-xs">Assurance semaine {wi + 2} ({w.assUnit} € × {Object.keys(w.forfaits).filter(k => form.voyageurs[k]?.nom?.trim()).length} pers.)</td>
                                      <td className="py-1 text-right font-semibold text-[#1E3A5F]">{w.assurance} €</td>
                                    </tr>
                                  )}
                                  <tr className="border-b-2 border-emerald-200">
                                    <td className="py-1.5 pl-5 text-emerald-900 italic text-xs">Sous-total semaine {wi + 2}</td>
                                    <td className="py-1.5 text-right font-bold text-[#1E3A5F]" data-testid={`recap-semaine-${wi + 2}-total`}>{w.total.toFixed(2)} €</td>
                                  </tr>
                                </React.Fragment>
                              ))}
                              {nbSemainesEff > 1 && (
                                <tr className="border-b-2 border-[#1E3A5F]/30 bg-sky-50" data-testid="recap-sejour-total">
                                  <td className="py-2 px-2 font-bold text-[#1E3A5F]">🧾 Total séjour {nbSemainesEff} semaines ({new Date(selectedDest.dateDepart).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} → {new Date(semainesSupp[semainesSupp.length - 1].dest.dateFin).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}), hors adhésion</td>
                                  <td className="py-2 text-right font-bold text-[#1E3A5F]">{(sommeForfaitsAvecSupp + assTotal).toFixed(2)} €</td>
                                </tr>
                              )}
                              {adhesion > 0 && (
                                <tr className="border-b border-sky-200">
                                  <td className="py-1.5 text-gray-700">Adhésion{nbSemainesEff > 1 ? ' (une seule fois pour tout le séjour)' : ''}</td>
                                  <td className="py-1.5 text-right font-bold text-[#1E3A5F]">{adhesion} €</td>
                                </tr>
                              )}
                              {assTotal > 0 && nbSemainesEff === 1 && (
                                <tr className="border-b border-sky-200">
                                  <td className="py-1.5 text-gray-700">Assurance ({assUnit} € × {voyageursActifs.length} pers.)</td>
                                  <td className="py-1.5 text-right font-bold text-[#1E3A5F]">{assTotal} €</td>
                                </tr>
                              )}
                              {suppChambre > 0 && nbSemainesEff === 1 && (
                                <tr className="border-b border-sky-200">
                                  <td className="py-1.5 text-gray-700">Supplément chambre individuelle</td>
                                  <td className="py-1.5 text-right font-bold text-[#1E3A5F]">{suppChambre} €</td>
                                </tr>
                              )}
                              {supplementPiscine > 0 && nbSemainesEff === 1 && (
                                <tr className="border-b border-sky-200" data-testid="recap-piscine">
                                  <td className="py-1.5 text-gray-700">Vue piscine garantie ({(form.chambres || []).filter(c => c.vuePiscine && (c.occupants || []).length > 0).length} chambre{supplementPiscine > piscineMontantChambre ? 's' : ''} × {piscineMontantChambre} €)</td>
                                  <td className="py-1.5 text-right font-bold text-[#1E3A5F]">{supplementPiscine} €</td>
                                </tr>
                              )}
                              <tr>
                                <td className="py-2 font-bold text-[#1E3A5F]">COÛT TOTAL DU VOYAGE</td>
                                <td className="py-2 text-right font-bold text-[#1E3A5F] text-lg">{totalVoyage} €</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        {!hasNuitee && (
                          <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-3 text-sm text-amber-900" data-testid="journee-notice">
                            <p className="text-xs leading-relaxed">
                              ⚠️ <strong>Voyage à la journée : 100 % du montant est dû à l'inscription</strong> (pas d'échéancier). En cas de difficulté, contactez-nous — on trouve toujours une solution ensemble.
                            </p>
                          </div>
                        )}

                        {montantARegler > 0 && (
                          <div className="bg-green-50 border-2 border-green-400 rounded-xl p-4" data-testid="montant-a-regler">
                            <p className="text-xs font-semibold text-green-800 mb-2">{pctLabel}</p>
                            <table className="w-full text-sm">
                              <tbody>
                                <tr className="border-b border-green-200">
                                  <td className="py-1.5 text-gray-700">{pctAuto < 1 ? `Acompte ${Math.round(pctAuto*100)}% sur forfaits${suppChambre > 0 ? ' + suppl. chambre' : ''} (${horsAssToutesSemaines} €)` : `Forfaits${suppChambre > 0 ? ' + suppl. chambre' : ''} (100%)`}</td>
                                  <td className="py-1.5 text-right font-bold">{acompteForfaits} €</td>
                                </tr>
                                {adhesion > 0 && (
                                  <tr className="border-b border-green-200">
                                    <td className="py-1.5 text-gray-700">+ Adhésion (due à l'inscription)</td>
                                    <td className="py-1.5 text-right font-bold">{adhesion} €</td>
                                  </tr>
                                )}
                                {assToutesSemaines > 0 && (
                                  <tr className="border-b border-green-200">
                                    <td className="py-1.5 text-gray-700">+ Assurance (due à l'inscription{nbSemainesEff > 1 ? `, ${nbSemainesEff} semaines` : ''})</td>
                                    <td className="py-1.5 text-right font-bold">{assToutesSemaines} €</td>
                                  </tr>
                                )}
                                <tr>
                                  <td className="py-2 font-bold text-green-800 text-base">{pctAuto >= 1 ? "SOMME EXACTE À RÉGLER (à régler dans les 24 heures, en ligne ou lors de votre rendez-vous)" : "MINIMUM À RÉGLER AUJOURD'HUI OU LORS DE VOTRE RENDEZ-VOUS (à prendre dans les 24 heures)"}</td>
                                  <td className="py-2 text-right font-bold text-green-700 text-2xl">{montantARegler} €</td>
                                </tr>
                              </tbody>
                            </table>
                            <p className="text-xs text-green-900 mt-2 bg-white/70 border border-green-300 rounded-lg px-3 py-2" data-testid="rien-a-payer-maintenant">
                              <strong>Rien à payer et aucune carte bancaire à l'envoi</strong> : vous envoyez simplement le formulaire. Le règlement se fait <strong>ensuite</strong>, depuis votre espace adhérent, par virement, carte ou rendez-vous. Ce montant est un <strong>minimum indicatif</strong>.
                            </p>
                            {nbSemainesEff > 1 && (
                              <div className="mt-2 text-xs text-green-900 bg-white/70 border border-green-300 rounded-lg px-3 py-2" data-testid="acompte-par-semaine">
                                <p className="font-bold">🧾 Une semaine = une facture (comme chaque année) :</p>
                                <p>Semaine 1 ({selectedDest.label}) : <strong>{acompteS1} €</strong> à régler maintenant sur {totalVoyageS1} €{adhesion > 0 ? ' (adhésion incluse)' : ''}</p>
                                {semainesSupp.map((w, wi) => <p key={wi}>Semaine {wi + 2} ({w.dest.label}) : <strong>{acompteSemaine(w, pctAuto)} €</strong> à régler maintenant sur {w.total} €</p>)}
                                <p className="text-[11px] text-green-800 mt-1">Chaque semaine a sa propre fiche, sa propre facture et son propre échéancier dans votre espace adhérent : les rappels de solde partent semaine par semaine, selon la date de départ de chacune.</p>
                              </div>
                            )}
                            {pctAuto >= 1 && (
                              <p className="text-xs text-green-700 mt-2 italic">En cas de difficulté de paiement, n'hésitez pas à <strong>nous contacter</strong> — nous trouverons toujours une solution ensemble.</p>
                            )}
                            {pctAuto === 0.2 && (() => {
                              const dix = Math.ceil(horsAssToutesSemaines * 0.1);
                              const fixes = adhesion + assToutesSemaines;
                              return (
                                <div className="mt-3 bg-sky-50 border-2 border-sky-300 rounded-xl px-3 py-2.5 text-xs text-sky-900 leading-relaxed" data-testid="acompte-10-pourcent">
                                  <p className="font-bold text-sm">💡 {montantARegler} € aujourd'hui, c'est compliqué ? Ne renoncez pas.</p>
                                  <p className="mt-1">Exceptionnellement, vous pouvez ne verser que <strong>10 % du forfait</strong> pour réserver votre place, soit <strong>{dix} €</strong>{fixes > 0 ? <> (+ {adhesion > 0 && assToutesSemaines > 0 ? 'adhésion et assurance' : adhesion > 0 ? 'adhésion' : 'assurance'} {fixes} € = <strong>{dix + fixes} €</strong> au total aujourd'hui)</> : ''}.</p>
                                  <p className="mt-1 text-sky-800">Rien d'autre ne change : le reste de l'acompte se rattrape tranquillement, les paliers suivants et le solde 45 jours avant le départ restent les mêmes. Indiquez-le nous simplement au moment du règlement.</p>
                                </div>
                              );
                            })()}
                            {pctAuto === 0.2 && (
                              <p className="text-xs text-green-700 mt-2">Le complément de <strong>{Math.ceil(horsAssToutesSemaines * 0.3)} €</strong> (pour atteindre 50%) est dû 6 mois avant le départ. Le solde de <strong>{Math.ceil(horsAssToutesSemaines * 0.5)} €</strong> devra être réglé au plus tard 45 jours avant le départ.</p>
                            )}
                            {pctAuto === 0.5 && (
                              <p className="text-xs text-green-700 mt-2">Le solde restant de <strong>{horsAssToutesSemaines - acompteForfaits} €</strong> devra être réglé au plus tard 45 jours avant le départ.</p>
                            )}
                          </div>
                        )}

                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 leading-relaxed">
                          <p><strong>Ce calcul est fourni à titre indicatif.</strong> Seules les informations figurant sur la page du voyage concerné font foi.</p>
                          {hasNuitee && isEspagne && (
                            <p className="mt-1">Rappel : les hôtels appliquent la règle de <strong>2 pleins tarifs minimum par chambre</strong>, quel que soit l'âge. Les réductions enfants s'appliquent à partir de la 3e personne dans la chambre. N'hésitez pas à consulter la page du voyage pour le bon calcul et à nous contacter si besoin.</p>
                          )}
                          <p className="mt-1">L'association se réserve le droit de rectifier tout forfait en cas d'erreur de calcul.</p>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </section>

              {/* Iter 103e - Section "Avoir" SUPPRIMÉE du formulaire public.
                  Les rares cas d'avoir sont désormais traités directement par l'admin
                  (contact direct → vérification dossier ancien/nouveau → application manuelle). */}

              {/* Iter 360 (Kamel — allègement handicap numérique) : section
                  « COMMENT SOUHAITEZ-VOUS RÉGLER ? » (canalReglement) SUPPRIMÉE —
                  doublon avec la page Payer où le choix se fait réellement.
                  Le champ canalReglement reste optionnel côté validation/PDF. */}

              {/* Iter 207 (Kamel) — DEMANDES PARTICULIÈRES : chambres côte à côte, handicap...
                  Ce sont des DEMANDES transmises, pas des garanties. */}
              <section className={`bg-white rounded-2xl shadow-md overflow-hidden${st(3)}`} data-testid="section-demandes-speciales">
                <SectionHeader title="DEMANDES PARTICULIÈRES (facultatif)" />
                <div className="p-5 space-y-3">
                  <p className="text-xs text-gray-600 leading-relaxed">
                    Chambres côte à côte, proximité avec des voyageurs d'un autre formulaire, ou toute autre
                    précision utile pour votre séjour : écrivez-le ici, nous transmettrons votre demande
                    à l'hôtel et aux organisateurs.
                  </p>
                  <textarea
                    value={form.demandesSpeciales}
                    onChange={e => set('demandesSpeciales', e.target.value)}
                    placeholder={"Ex : « Merci de prévoir nos 2 chambres côte à côte » · « Mme X est en fauteuil roulant, chambre proche de l'ascenseur si possible » · « Nous souhaitons être proches de la famille Y (autre formulaire) »"}
                    rows={3}
                    maxLength={600}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md text-sm focus:border-[#1E3A5F] outline-none"
                    data-testid="demandes-speciales-input"
                  />
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 leading-relaxed">
                    ⚠️ Il s'agit de <strong>demandes</strong> que nous relayons de notre mieux — elles ne constituent
                    <strong> pas un engagement ni une obligation</strong> : l'attribution finale (chambres, emplacements)
                    reste gérée par les hôtels et prestataires.
                  </div>
                </div>
              </section>

              {/* Iter 769 (Kamel — smartphone) : RÉCAP lisible à l'écran 4 avant le bouton Envoyer */}
              {stepperOn && etape === 4 && selectedDest && (() => {
                const vs = form.voyageurs.filter(v => (v.nom || '').trim() || (v.prenom || '').trim());
                const totalVoyage = parseFloat(form._totalVoyage) || 0;
                const aRegler = parseFloat(form.montantCalcule) || 0;
                const legsTxt = trajetsLegs.length === 2 ? 'car aller + retour' : trajetsLegs.length === 1 ? `car ${trajetsLegs[0]} uniquement` : (isFusion ? 'sans transport' : '');
                const ligne = (lab, val, tid) => <div className="flex justify-between gap-3 text-xs" data-testid={tid}><span className="text-gray-500">{lab}</span><span className="font-semibold text-[#1E3A5F] text-right">{val}</span></div>;
                return (
                  <section className="bg-white rounded-2xl shadow-md overflow-hidden border-2 border-[#79c9ea]" data-testid="recap-avant-envoi">
                    <SectionHeader title="VÉRIFIEZ AVANT D'ENVOYER" color="bg-[#79c9ea]" />
                    <div className="p-4 space-y-3">
                      <div className="space-y-1">
                        <p className="text-[10px] font-bold text-gray-400 uppercase">Voyage</p>
                        <p className="text-sm font-bold text-[#1E3A5F]" data-testid="recap-voyage">{selectedDest.group} — {selectedDest.label}</p>
                        {(legsTxt || selectedDest.transport) && ligne('Transport', `${legsTxt || selectedDest.transport}${form.lieuDepart && trajetsLegs.length ? ` · départ ${form.lieuDepart}` : ''}`, 'recap-transport')}
                        {form.nbSemaines > 1 && ligne('Durée', `${form.nbSemaines} semaines`, 'recap-semaines')}
                        <button type="button" onClick={() => allerEtape(1)} className="text-[11px] underline text-[#1E3A5F]" data-testid="recap-modifier-voyage">✏️ modifier</button>
                      </div>
                      <div className="space-y-1 border-t pt-2">
                        <p className="text-[10px] font-bold text-gray-400 uppercase">Voyageurs ({vs.length})</p>
                        {vs.map((v, i) => ligne(`${(v.prenom || '').trim()} ${(v.nom || '').trim().toUpperCase()}${isFusion && transportActif ? ' · hôtel' : ''}`, v.forfait ? `${Math.max(0, (parseFloat(v.forfait) || 0) - (isFusion ? supplementTrajets : 0))} €` : '—', `recap-voyageur-${i}`))}
                        {isFusion && transportActif && supplementTrajets > 0 && ligne(`🚌 Transport${nbSemainesEff > 1 ? ' semaine 1' : ''} (${legsSemaine1.length === 2 ? 'aller + retour' : `car ${legsSemaine1[0]}`}) × ${vs.length} pers.`, `${supplementTrajets * vs.length} €`, 'recap-transport-montant')}
                        {form.email && ligne('E-mail', form.email, 'recap-email')}
                        {form.telephone && ligne('Téléphone', form.telephone, 'recap-tel')}
                        <button type="button" onClick={() => allerEtape(2)} className="text-[11px] underline text-[#1E3A5F]" data-testid="recap-modifier-voyageurs">✏️ modifier</button>
                      </div>
                      <div className="space-y-1 border-t pt-2">
                        <p className="text-[10px] font-bold text-gray-400 uppercase">Montants</p>
                        {parseFloat(form.montantAdhesion) > 0 && ligne('Adhésion', `${form.montantAdhesion} €`, 'recap-adhesion')}
                        {form.assurance === 'oui' && ligne('Assurance', `${form.montantAssurance || 0} € × ${vs.length}`, 'recap-assurance')}
                        {nbSemainesEff > 1 && (() => {
                          const assS1 = form.assurance === 'oui' ? (parseFloat(form.montantAssurance) || 0) * vs.length : 0;
                          const s1 = vs.reduce((s, v) => s + (parseFloat(v.forfait) || 0), 0) + (form.supplementChambre ? (parseFloat(form.montantSupplementChambre) || 0) : 0) + supplementPiscine + assS1;
                          return (
                            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1.5 space-y-0.5" data-testid="recap-par-semaine">
                              {ligne(`Semaine 1 · ${selectedDest.label}`, `${s1.toFixed(0)} €`, 'recap-sem-1')}
                              {semainesSupp.map((w, wi) => ligne(`Semaine ${wi + 2} · ${w.dest.label}${w.legs.length ? ' · car retour' : ''}`, `${w.total.toFixed(0)} €`, `recap-sem-${wi + 2}`))}
                              <div className="flex justify-between gap-3 text-xs border-t border-emerald-300 pt-0.5" data-testid="recap-sejour-total-mobile"><span className="font-bold text-emerald-900">Séjour {nbSemainesEff} semaines (hors adhésion)</span><span className="font-bold text-emerald-900">{(s1 + totalSemainesSupp).toFixed(0)} €</span></div>
                              <p className="text-[10px] text-emerald-800">Une semaine = une facture : chaque semaine aura sa fiche, son échéancier et ses rappels dans votre espace adhérent.</p>
                            </div>
                          );
                        })()}
                        {totalVoyage > 0 && ligne('Total du voyage', `${totalVoyage} €`, 'recap-total')}
                        {aRegler > 0 && <div className="flex justify-between gap-3 text-sm bg-sky-50 rounded-lg px-2 py-1.5 mt-1" data-testid="recap-a-regler"><span className="font-bold text-[#1E3A5F]">À régler sous 24 h</span><span className="font-bold text-[#1E3A5F]">{aRegler} €{form.paiement && form.paiement !== '100' ? ` (acompte ${form.paiement} %)` : ''}</span></div>}
                        <button type="button" onClick={() => allerEtape(3)} className="text-[11px] underline text-[#1E3A5F]" data-testid="recap-modifier-options">✏️ modifier</button>
                      </div>
                    </div>
                  </section>
                );
              })()}


              {/* DÉCLARATION */}
              <section className={`bg-white rounded-2xl shadow-md overflow-hidden${st(4)}`} data-testid="section-declaration">
                <SectionHeader title="DÉCLARATION" />
                <div className="p-5 space-y-4">
                  <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 text-xs text-[#1E3A5F] leading-relaxed">
                    <p className="font-semibold text-sm mb-2">Veuillez cocher chaque case pour valider votre inscription :</p>
                    <p>Les déclarations ci-dessous sont signées par la personne qui effectue cette inscription et <strong>valent pour l'ensemble des voyageurs mentionnés sur le présent formulaire</strong> (conjoint, enfants, concubin·e, membres du foyer ou proches partageant la même adresse). L'inscrivant atteste avoir préalablement informé chacun d'eux de ces conditions et s'en porte garant auprès de l'association.</p>
                  </div>

                  {/* Iter 765 (Kamel — smartphone) : encadré « Vous devez avoir pris connaissance du détail du voyage » retiré
                      (doublon de la 1re case à cocher et du rappel « avant de poursuivre » dans le bloc Destination). */}

                  <div className="space-y-3">
                    <Check checked={form.cond1 || false} onChange={() => set('cond1', !form.cond1)}
                      label="J'ai pris connaissance du détail du voyage, des conditions générales de vente et des garanties d'assurance proposées."
                      testId="cond-1" />
                    <Check checked={form.cond2 || false} onChange={() => set('cond2', !form.cond2)}
                      label="Je certifie l'exactitude des informations saisies (noms, prénoms et dates de naissance conformes aux pièces d'identité)."
                      testId="cond-2" />
                    <Check checked={form.cond4 || false} onChange={() => set('cond4', !form.cond4)}
                      label={hasNuitee
                        ? "J'ai pris connaissance de l'échéancier de paiement : 20 % du total à l'inscription, 50 % CUMULÉ à 6 mois du départ, 100 % (solde complet) à 45 jours du départ. Il s'agit de cumuls : à chaque échéance, le total déjà versé doit avoir atteint le pourcentage indiqué. Frais d'annulation jusqu'à 100 % selon la date d'annulation. EN CAS DE DIFFICULTÉ POUR RESPECTER CET ÉCHÉANCIER, JE M'ENGAGE À CONTACTER L'ASSOCIATION."
                        : "J'ai pris connaissance qu'il s'agit d'un voyage à la journée : 100 % du montant est dû à l'inscription, sans échéancier. En cas de désistement, les frais d'annulation sont de 100 %. Je sais qu'un remplacement par un proche est possible, sous ma propre responsabilité, à condition d'en informer l'association en amont (nom du remplaçant pour assurance / autocar / repas). EN CAS DE DIFFICULTÉ, JE M'ENGAGE À CONTACTER L'ASSOCIATION."}
                      testId="cond-4" />
                    <Check checked={form.cond3 || false} onChange={() => set('cond3', !form.cond3)}
                      label='J&apos;ai pris connaissance que, dans les 24 heures qui suivent l&apos;envoi de ce formulaire, je dois, depuis mon Espace Adhérent (ouvert automatiquement après l&apos;envoi), régler en ligne ou prendre un rendez-vous pour régler sur place (mercredi ou samedi, dans les 10 prochains jours). Sans règlement, l&apos;inscription ne sera pas validée.'
                      testId="cond-3" />
                    <Check checked={form.cond5 || false} onChange={() => set('cond5', !form.cond5)}
                      label="J'ai pris connaissance que l'association Rhône Alpes Amitié Voyages décline toute responsabilité en cas d'informations erronées transmises par le voyageur."
                      testId="cond-5" />

                    {/* Iter 100 — Consentement RGPD obligatoire (case dédiée, distincte des CGV) */}
                    <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-3 mt-2">
                      <Check checked={form.condRgpd || false} onChange={() => set('condRgpd', !form.condRgpd)}
                        label={<span>J'autorise l'association Rhône Alpes Amitié Voyages à collecter et traiter mes données personnelles (et celles des voyageurs inscrits sur ce formulaire) pour les besoins de l'organisation du voyage, conformément à la <a href="/politique-de-confidentialite" target="_blank" rel="noopener noreferrer" className="text-[#5BC5D4] underline font-bold">Politique de confidentialité</a> (RGPD). Je peux exercer mes droits (accès, rectification, suppression, portabilité) à tout moment par email à <strong>rhoneamitie@gmail.com</strong>.</span>}
                        testId="cond-rgpd" />
                    </div>
                    {/* Consentement newsletter — facultatif, distinct du consentement RGPD obligatoire */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <Check checked={form.condNewsletter || false} onChange={() => set('condNewsletter', !form.condNewsletter)}
                        label={<span><span className="text-xs font-bold text-gray-500 uppercase">Facultatif</span> — J'accepte de recevoir occasionnellement par email les actualités de l'association (nouveaux voyages, lives TikTok, événements). Je peux me désinscrire à tout moment.</span>}
                        testId="cond-newsletter" />
                    </div>
                  </div>

                  {/* ===== QUI REMPLIT CE FORMULAIRE ? ===== */}
                  <div className={`mt-6 p-4 border-2 rounded-lg ${isMinorOnly ? 'bg-amber-50 border-amber-400' : 'bg-blue-50 border-blue-300'}`} data-testid="rempli-par-section">
                    {isMinorOnly ? (
                      <>
                        <div className="flex items-start gap-2 mb-2" data-testid="autorisation-parentale-block">
                          <span className="text-xl">👨‍👩‍👧</span>
                          <div>
                            <p className="text-sm font-bold text-amber-900">Inscription d'un/des mineur(s) seul(s) — Autorisation parentale requise <span className="text-red-500">*</span></p>
                            <p className="text-[11px] text-amber-800 mt-1 leading-snug">
                              Aucun voyageur majeur (≥ 18 ans à la date de départ) n'est inscrit sur ce formulaire. En tant que <strong>représentant légal</strong> (parent ou tuteur légal), vos coordonnées et votre autorisation expresse sont obligatoires ci-dessous.
                            </p>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-bold text-[#1E3A5F] mb-2">Qui remplit ce formulaire ? <span className="text-red-500">*</span></p>
                        <p className="text-[11px] text-blue-800 mb-3 leading-snug">
                          Merci de nous dire si vous remplissez ce formulaire pour vous-même, ou pour quelqu'un d'autre.
                        </p>
                        <div className="space-y-2">
                          <label className="flex items-start gap-2 cursor-pointer">
                            <input type="radio" name="rempliPar" value="moi"
                              checked={form.rempliPar !== 'tiers'}
                              onChange={() => set('rempliPar', 'moi')}
                              className="mt-1" data-testid="rempli-par-moi" />
                            <span className="text-xs text-gray-800">Je remplis ce formulaire <strong>pour moi-même et pour toutes les personnes inscrites</strong> sur ce formulaire dont j'ai la responsabilité (enfants, conjoint(e), etc.).</span>
                          </label>
                          <label className="flex items-start gap-2 cursor-pointer">
                            <input type="radio" name="rempliPar" value="tiers"
                              checked={form.rempliPar === 'tiers'}
                              onChange={() => set('rempliPar', 'tiers')}
                              className="mt-1" data-testid="rempli-par-tiers" />
                            <span className="text-xs text-gray-800">Je remplis ce formulaire <strong>pour une autre personne (ou plusieurs autres personnes)</strong>, avec son accord (ou leur accord).</span>
                          </label>
                        </div>
                      </>
                    )}
                    {(form.rempliPar === 'tiers' || isMinorOnly) && (
                      <div className={`mt-3 ${isMinorOnly ? '' : 'pl-5'} grid grid-cols-1 sm:grid-cols-3 gap-2`} data-testid="rempli-tiers-fields">
                        <div>
                          <label className="block text-[10px] text-gray-600 font-bold uppercase mb-1">{isMinorOnly ? 'Votre nom (représentant légal)' : 'Votre nom'} <span className="text-red-500">*</span></label>
                          <input type="text" value={form.rempliParTiersNom || ''}
                            onChange={e => set('rempliParTiersNom', e.target.value.toUpperCase())}
                            placeholder="NOM" className="w-full border-2 border-gray-400 rounded-lg px-3 py-2 text-xs uppercase focus:border-[#79c9ea] outline-none"
                            data-testid="rempli-tiers-nom" required={form.rempliPar === 'tiers'} />
                        </div>
                        <div>
                          <label className="block text-[10px] text-gray-600 font-bold uppercase mb-1">{isMinorOnly ? 'Votre prénom (représentant légal)' : 'Votre prénom'} <span className="text-red-500">*</span></label>
                          <input type="text" value={form.rempliParTiersPrenom || ''}
                            onChange={e => set('rempliParTiersPrenom', e.target.value.toUpperCase())}
                            placeholder="PRÉNOM" className="w-full border-2 border-gray-400 rounded-lg px-3 py-2 text-xs uppercase focus:border-[#79c9ea] outline-none"
                            data-testid="rempli-tiers-prenom" required={form.rempliPar === 'tiers'} />
                        </div>
                        <div>
                          <label className="block text-[10px] text-gray-600 font-bold uppercase mb-1">{isMinorOnly ? 'Votre qualité' : 'Votre lien avec la (les) personne(s) inscrite(s)'} <span className="text-red-500">*</span></label>
                          <select value={form.rempliParTiersLien || ''}
                            onChange={e => set('rempliParTiersLien', e.target.value)}
                            className="w-full border-2 border-gray-400 rounded-lg px-2 py-2 text-xs focus:border-[#79c9ea] outline-none"
                            data-testid="rempli-tiers-lien" required={form.rempliPar === 'tiers'}>
                            <option value="">— Choisir —</option>
                            {isMinorOnly ? (
                              <>
                                <option value="parent">Parent</option>
                                <option value="tuteur-legal">Tuteur légal</option>
                              </>
                            ) : (
                              <>
                                <option value="tuteur-legal">Tuteur légal</option>
                                <option value="tutelle">Sous tutelle</option>
                                <option value="epoux">Époux / Épouse</option>
                                <option value="parent">Parent</option>
                                <option value="enfant">Enfant</option>
                                <option value="ami">Ami(e)</option>
                                <option value="autre">Autre</option>
                              </>
                            )}
                          </select>
                        </div>
                        {isMinorOnly ? (
                          <div className="sm:col-span-3 mt-2 p-3 bg-white border-2 border-amber-400 rounded-lg">
                            <Check checked={form.autorisationParentale || false}
                              onChange={() => set('autorisationParentale', !form.autorisationParentale)}
                              label={<span className="text-xs"><strong>J'atteste sur l'honneur</strong> être le représentant légal (parent ou tuteur légal) du/des mineur(s) inscrit(s) ci-dessus et <strong>j'autorise expressément</strong> sa/leur participation au voyage organisé par l'association Rhône Alpes Amitié Voyages, dans le cadre des conditions générales de vente acceptées ci-dessus. <span className="text-red-500">*</span></span>}
                              testId="autorisation-parentale-check" />
                            <p className="mt-2 text-[10px] text-amber-800 italic leading-snug">
                              ℹ️ Cette autorisation sera consignée dans le dossier d'inscription, le PDF récapitulatif et l'email de confirmation. L'association se réserve le droit de demander un justificatif (livret de famille, jugement de tutelle) avant le départ.
                            </p>
                          </div>
                        ) : (
                          <p className="sm:col-span-3 text-[10px] text-blue-700 italic leading-snug">
                            ℹ️ Cette mention atteste que l'inscription a été initiée avec l'accord explicite de la personne inscrite. Elle sera rappelée dans l'email de confirmation.
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <p className="text-xs text-[#1E3A5F] font-semibold mt-2">Un e-mail de confirmation vous sera envoyé après validation ; votre dossier complet est dans votre Espace Adhérent.</p>

                  <div className="grid grid-cols-2 gap-3 mt-3 text-center">
                    <div>
                      <label className="text-[10px] font-semibold text-gray-500 block mb-1">DATE D'INSCRIPTION</label>
                      <div className="border-2 border-gray-200 rounded-lg px-2 py-2 text-xs bg-gray-50 font-semibold">{form.dateInscription}</div>
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-400 block mb-1">SIGNATURE</label>
                      <div className="border border-gray-200 rounded-lg px-2 py-2 text-xs bg-gray-50 text-gray-400 italic">Validée électroniquement à l'envoi</div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Iter 78sex — Confirmation email + téléphone responsable avant envoi.
                  Bloque la soumission si différent des champs Adresse / Voyageur 1. */}
              <section className={`bg-amber-50 border-2 border-amber-300 rounded-2xl p-5 shadow-md${st(4)}`} data-testid="section-confirmation-coordonnees">
                <h2 className="text-lg font-bold text-[#1E3A5F] mb-1 flex items-center gap-2">
                  <span aria-hidden>🔒</span> Confirmation des coordonnées
                </h2>
                <p className="text-xs text-amber-800 mb-3">
                  Re-saisissez votre <strong>email</strong> et le <strong>téléphone du voyageur 1</strong> pour vérifier qu'ils sont corrects.
                  C'est sur ces coordonnées que nous vous enverrons votre facture, le descriptif voyage et toutes les informations pratiques.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-[#1E3A5F] block mb-1">EMAIL (confirmation) *</label>
                    <input
                      type="email"
                      inputMode="email"
                      value={form.emailConfirmation || ''}
                      onChange={e => set('emailConfirmation', e.target.value.trim())}
                      placeholder="re-saisissez votre email"
                      className="w-full border-2 border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#79c9ea]"
                      data-testid="email-confirmation-input"
                      autoComplete="email"
                      name="email-confirmation"
                      spellCheck={false}
                      autoCorrect="off"
                      autoCapitalize="off"
                    />
                    <p className="text-[10px] text-amber-700 italic mt-0.5">Les deux emails doivent être identiques.</p>
                    {form.emailConfirmation && form.email && form.emailConfirmation.toLowerCase() === (form.email || '').toLowerCase() && (
                      <p className="text-[11px] text-green-700 font-semibold mt-1 flex items-center gap-1 email-match-check"
                         data-testid="email-match-check">
                        <span className="inline-block w-4 h-4 rounded-full bg-green-100 text-green-700 text-center font-bold leading-4 text-[11px]">✓</span>
                        Email confirmé — identique à celui saisi plus haut.
                      </p>
                    )}
                    {form.emailConfirmation && form.email && form.emailConfirmation.toLowerCase() !== (form.email || '').toLowerCase() && (
                      <p className="text-[11px] text-red-600 font-semibold mt-1" data-testid="email-mismatch-warning">⚠ Différent de l'email saisi en haut du formulaire — vérifiez l'orthographe.</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#1E3A5F] block mb-1">TÉLÉPHONE VOYAGEUR 1 (confirmation) *</label>
                    <input
                      type="tel"
                      inputMode="tel"
                      value={form.telephoneConfirmation || ''}
                      onChange={e => set('telephoneConfirmation', e.target.value.trim())}
                      placeholder="re-saisissez votre numéro de téléphone"
                      className="w-full border-2 border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#79c9ea]"
                      data-testid="telephone-confirmation-input"
                      autoComplete="tel"
                      name="telephone-confirmation"
                      spellCheck={false}
                      autoCorrect="off"
                    />
                    <p className="text-[10px] text-amber-700 italic mt-0.5">Les deux numéros doivent être identiques.</p>
                    {form.telephoneConfirmation && form.telephone && form.telephoneConfirmation.replace(/\D/g, '') === (form.telephone || '').replace(/\D/g, '') && (
                      <p className="text-[11px] text-green-700 font-semibold mt-1 flex items-center gap-1 email-match-check"
                         data-testid="telephone-match-check">
                        <span className="inline-block w-4 h-4 rounded-full bg-green-100 text-green-700 text-center font-bold leading-4 text-[11px]">✓</span>
                        Téléphone confirmé — identique à celui de vos coordonnées.
                      </p>
                    )}
                    {form.telephoneConfirmation && form.telephone && form.telephoneConfirmation.replace(/\D/g, '') !== (form.telephone || '').replace(/\D/g, '') && (
                      <p className="text-[11px] text-red-600 font-semibold mt-1" data-testid="telephone-mismatch-warning">⚠ Différent du téléphone saisi dans vos coordonnées — vérifiez le numéro.</p>
                    )}
                  </div>
                </div>
                {/* Iter 900 — relecture explicite + case obligatoire (deux champs identiques ≠ preuve d'exactitude) */}
                <div className="mt-4 bg-white border-2 border-amber-400 rounded-xl p-4" data-testid="coords-recap">
                  <p className="text-sm font-bold text-[#1E3A5F]">Ces coordonnées sont-elles correctes ? Votre récapitulatif sera envoyé à l'adresse e-mail indiquée.</p>
                  <div className="mt-2 grid sm:grid-cols-2 gap-2 text-sm">
                    <div className="bg-amber-50 rounded-lg px-3 py-2">
                      <p className="text-[10px] uppercase font-bold text-amber-700">E-mail</p>
                      <p className="font-bold break-all text-base" data-testid="coords-recap-email">{(form.email || '').trim() || <span className="text-red-600 font-semibold">— non renseigné —</span>}</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg px-3 py-2">
                      <p className="text-[10px] uppercase font-bold text-amber-700">Téléphone</p>
                      <p className="font-bold text-base" data-testid="coords-recap-telephone">{(form.telephone || '').trim() || <span className="text-red-600 font-semibold">— non renseigné —</span>}</p>
                    </div>
                  </div>
                  <button type="button" onClick={() => { if (stepperOn) setEtape(2); setTimeout(() => { const el = document.getElementById('input-email'); if (el) { el.focus({ preventScroll: true }); el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } }, stepperOn ? 150 : 0); }}
                    className="mt-2 text-xs font-bold text-[#1E3A5F] underline" data-testid="coords-corriger-btn">✏️ Corriger mon e-mail ou mon téléphone</button>
                  <label className={`mt-3 flex items-start gap-2 rounded-lg border-2 px-3 py-2 cursor-pointer ${coordsVerifiees ? 'border-green-400 bg-green-50' : 'border-amber-400 bg-amber-50'}`}>
                    <input type="checkbox" checked={coordsVerifiees}
                      onChange={(e) => { setCoordsVerifiees(e.target.checked); coordsVerifieesPour.current = e.target.checked ? `${(form.email || '').trim().toLowerCase()}|${(form.telephone || '').replace(/\D/g, '')}` : ''; }}
                      className="mt-0.5 w-5 h-5" id="coords-verifiees-checkbox" data-testid="coords-verifiees-checkbox" />
                    <span className="text-sm font-semibold text-[#1E3A5F]">J'ai vérifié mon adresse e-mail et mon numéro de téléphone. <span className="text-red-600">*</span></span>
                  </label>
                  {!coordsVerifiees && coordsVerifieesPour.current === '' && (form.email || form.telephone) && (
                    <p className="text-[11px] text-amber-800 mt-1" data-testid="coords-verifiees-hint">Case obligatoire pour valider l'inscription.</p>
                  )}
                </div>
              </section>

              {/* Error */}
              {error && (
                <div className={`border rounded-xl p-4 text-sm font-semibold ${errorType === 'semaine_ajout' ? 'bg-amber-50 border-amber-400 text-amber-900' : 'bg-red-50 border-red-300 text-red-700'}`} data-testid="form-error">
                  {errorType === 'semaine_ajout' && <p className="text-base font-bold mb-1">➕ Vous souhaitez ajouter une semaine ?</p>}
                  {error}
                  {errorType === 'semaine_ajout' && (
                    <a href={`https://wa.me/33660416218?text=${encodeURIComponent("Bonjour, je souhaite ajouter la semaine " + (selectedDest?.label || '') + " à la suite de mon séjour à Lloret. Merci !")}`} target="_blank" rel="noreferrer"
                      className="mt-3 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-700" data-testid="form-error-whatsapp-ajout-semaine">
                      📲 Écrire aux organisateurs sur WhatsApp
                    </a>
                  )}
                </div>
              )}

              {/* Iter 263 — Refus « COMPLET » : offre de liste d'attente pré-remplie */}
              {error && waitlistOffer && (
                <WaitlistMini dest={waitlistOffer}
                  prefill={{
                    nom: form.voyageurs?.[0]?.nom || '',
                    prenom: form.voyageurs?.[0]?.prenom || '',
                    email: form.email || '',
                    telephone: form.voyageurs?.[0]?.telephone || '',
                    nbPersonnes: form.voyageurs.filter(v => (v.nom || '').trim()).length || 1,
                  }}
                  onDone={() => setWaitlistOffer(null)} />
              )}

              {/* Rappel juste au-dessus du bouton Envoyer (plus visible qu'en dessous) */}
              <div className={`bg-sky-50 border-2 border-sky-300 rounded-xl p-4 text-sm text-[#1E3A5F] leading-relaxed${st(4)}`}>
                <p className="font-bold mb-1">Que se passe-t-il après l'envoi ?</p>
                <p><strong>Dès l'envoi</strong>, votre <strong>Espace Adhérent</strong> s'ouvre automatiquement, dans tous les cas (votre lien d'accès personnel vous est aussi envoyé par e-mail). Depuis cet espace, vous pourrez soit <strong>régler en ligne</strong> (carte, virement), soit <strong>prendre un rendez-vous</strong> pour régler à la permanence (<strong>espèces ou chèques-vacances uniquement</strong>). <strong>Dans les deux cas, cette démarche doit se faire dans les 24 heures.</strong> Votre inscription sera définitivement validée à la réception du formulaire <strong>et</strong> de votre règlement à distance <strong>ou</strong> de votre prise de rendez-vous.</p>
              </div>

              {/* Iter 95 — Bouton « Voir l'aperçu » supprimé : la plupart des
                  voyageurs étant sur mobile, cliquer sur le bouton ouvrait un PDF
                  qui les sortait du formulaire et beaucoup ne revenaient pas.
                  Retiré pour simplifier le parcours. */}

              {/* Iter 154L (Kamel) — Charte de placement bus : simple case à cocher
                  obligatoire pour les voyages "Avec transport". Mini-lien pour
                  ceux qui veulent lire. Pas de bandeau d'alerte, pas de trace BDD —
                  l'objectif est un formulaire fluide et rapide. */}
              {isAvecTransportBus && (
                <div className={`flex flex-col gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3${st(4)}`} data-testid="charte-bus-block">
                  <p className="text-xs text-amber-900 leading-relaxed" data-testid="charte-bus-nouveaute">
                    🚌 <strong>Nouveau :</strong> les places dans le bus sont désormais <strong>choisies par les voyageurs
                    eux-mêmes</strong> sur un plan interactif, après l'inscription (lien « Mon Bus » envoyé par email) —
                    <strong> premières arrivées, premières servies</strong>. Sans choix avant le départ, une place est
                    attribuée automatiquement.
                    <br />💡 <strong>Astuce :</strong> pour être assis à côté d'amis inscrits sur un autre formulaire,
                    synchronisez-vous avec eux et choisissez vos places <strong>ensemble, au même moment</strong>,
                    avant que le car ne se remplisse.
                  </p>
                  <div className="flex items-start gap-3">
                  <input type="checkbox"
                    checked={!!form.accepteChartePlacementBus}
                    onChange={e => set('accepteChartePlacementBus', e.target.checked)}
                    data-testid="charte-bus-checkbox"
                    className="w-5 h-5 mt-0.5 rounded border-gray-400 text-[#5BC5D4] focus:ring-[#5BC5D4] flex-shrink-0" />
                  <label className="text-xs text-gray-700 leading-relaxed cursor-pointer" onClick={() => set('accepteChartePlacementBus', !form.accepteChartePlacementBus)}>
                    <strong>J&apos;accepte</strong> la{' '}
                    <a
                      href="/charte-placement-bus"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-[#1E3A5F] underline hover:text-[#5BC5D4] font-semibold"
                      data-testid="charte-bus-link"
                    >Charte de placement en autocar</a>. <span className="text-red-500">*</span>
                  </label>
                  </div>
                </div>
              )}

              {/* Submit — Iter 595 (mobile) : bouton collant en bas d'écran avec l'avancement du dossier */}
              <div className="sticky bottom-0 z-30 -mx-4 px-4 pt-2 pb-3 bg-white/95 backdrop-blur border-t border-gray-200 sm:static sm:mx-0 sm:p-0 sm:bg-transparent sm:border-0" data-testid="submit-sticky">
                {stepperOn && etapeIssues.length > 0 && (
                  <div className="mb-2 bg-red-50 border-2 border-red-300 rounded-xl p-3 text-xs text-red-800" data-testid="etape-issues">
                    <p className="font-bold mb-1">Avant de continuer :</p>
                    <ul className="list-disc ml-4 space-y-0.5">{etapeIssues.map(p => <li key={p.id}>{p.msg}</li>)}</ul>
                  </div>
                )}
                {avancement.pct < 100 && (!stepperOn || etape === 4) && (
                  <div className="sm:hidden mb-2" data-testid="form-avancement">
                    <div className="flex justify-between text-[11px] font-bold text-gray-600"><span>Dossier complété à {avancement.pct} %</span><span>{avancement.manquants.length ? `Reste : ${avancement.manquants.slice(0, 2).join(', ')}${avancement.manquants.length > 2 ? '…' : ''}` : ''}</span></div>
                    <div className="h-1.5 rounded-full bg-gray-200 mt-1"><div className="h-1.5 rounded-full bg-[#5ba8c8] transition-all" style={{ width: `${avancement.pct}%` }} /></div>
                  </div>
                )}
                {stepperOn && etape < 4 ? (
                  <div className="flex gap-2" key="nav-etapes">
                    {etape > 1 && (
                      <button type="button" onClick={() => allerEtape(etape - 1)} data-testid="etape-retour"
                        className="px-4 py-4 min-h-[52px] rounded-xl font-bold text-base border-2 border-[#1E3A5F] text-[#1E3A5F] bg-white">← Retour</button>
                    )}
                    <button type="button" onClick={etapeSuivante} data-testid="etape-suivant"
                      className="flex-1 py-4 min-h-[52px] rounded-xl font-bold text-lg bg-gradient-to-r from-[#79c9ea] to-[#5ba8c8] text-white hover:shadow-xl">
                      Suivant : {ETAPES[etape][1]} →
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2" key="nav-envoi">
                    {stepperOn && (
                      <button type="button" onClick={() => allerEtape(3)} data-testid="etape-retour"
                        className="px-4 py-4 min-h-[52px] rounded-xl font-bold text-base border-2 border-[#1E3A5F] text-[#1E3A5F] bg-white">←</button>
                    )}
                    <button type="submit" disabled={submitting} data-testid="submit-registration"
                      className={`flex-1 py-4 min-h-[52px] rounded-xl font-bold text-lg transition ${
                        submitting ? 'bg-gray-300 text-gray-500 cursor-wait' : 'bg-gradient-to-r from-[#79c9ea] to-[#5ba8c8] text-white hover:shadow-xl cursor-pointer'
                      }`}>
                      {submitting ? 'Envoi en cours...' : 'Envoyer mon inscription'}
                      {/* Iter 864 (Kamel) — compteur en direct des points manquants, avant même de cliquer */}
                      {!submitting && selectedDest && nbPointsManquants > 0 && (
                        <span className="block text-xs font-semibold text-white/95 mt-0.5" data-testid="submit-points-manquants">
                          ⚠️ {nbPointsManquants} point{nbPointsManquants > 1 ? 's' : ''} à compléter — cliquez pour voir la liste
                        </span>
                      )}
                      {!submitting && selectedDest && nbPointsManquants === 0 && (
                        <span className="block text-xs font-semibold text-white/95 mt-0.5" data-testid="submit-points-ok">✅ Tout est complet</span>
                      )}
                    </button>
                  </div>
                )}
                {!(stepperOn && etape < 4) && (
                  <p className="text-xs text-center text-[#1E3A5F] bg-sky-50 border border-sky-200 rounded-lg px-3 py-1.5 mt-2" data-testid="rien-a-payer-envoi">
                    🔒 <strong>Rien à payer et aucune carte bancaire maintenant</strong> : vous envoyez juste le formulaire. Le règlement se fait ensuite, depuis votre espace adhérent.
                  </p>
                )}
              </div>

              <p className="text-xs text-gray-400 text-center pb-4">
                En cas de question : <a href="mailto:rhoneamitie@gmail.com" className="text-[#79c9ea] font-semibold">rhoneamitie@gmail.com</a> ou <a href="https://wa.me/message/XTYPH2HUY3L6L1" target="_blank" rel="noopener noreferrer" className="text-[#25D366] font-semibold">WhatsApp</a>
              </p>
            </form>
      </div>

      {/* ============================================ */}
      {/* MODALE VALIDATION AU CLIC ENVOYER            */}
      {/* ============================================ */}
      {validationModal.open && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setValidationModal({ open: false, issues: [] })}>
          <div className="bg-white rounded-2xl max-w-xl w-full my-6 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="validation-modal">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-5 rounded-t-2xl flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">Quelques informations manquent</h2>
                <p className="text-sm text-white/90 mt-1">{validationModal.issues.length} point{validationModal.issues.length > 1 ? 's' : ''} à compléter avant de valider</p>
                <p className="text-xs text-white/90 mt-1" data-testid="validation-modal-hint">🟥 Les champs et cases concernés sont encadrés en rouge dans le formulaire. Touchez une ligne pour y aller.</p>
              </div>
              <button onClick={() => setValidationModal({ open: false, issues: [] })}
                className="text-white/80 hover:text-white text-2xl leading-none" data-testid="validation-modal-close">✕</button>
            </div>
            <div className="p-5 space-y-3 max-h-[60vh] overflow-y-auto">
              {Object.entries(validationModal.issues.reduce((acc, it) => {
                (acc[it.section] = acc[it.section] || []).push(it);
                return acc;
              }, {})).map(([section, items]) => (
                <div key={section}>
                  <p className="text-[11px] font-bold text-amber-800 uppercase tracking-wider mb-1">{section}</p>
                  <ul className="space-y-1">
                    {items.map((it) => (
                      <li key={it.id} className="flex items-start gap-2 text-sm text-gray-800" data-testid={`validation-modal-issue-${it.id}`}>
                        <span className="text-amber-600 font-bold flex-shrink-0 mt-0.5">•</span>
                        <button type="button"
                          onClick={() => {
                            setValidationModal({ open: false, issues: [] });
                            if (stepperOn) { setEtapeIssues([]); setEtape(etapeDeAnchor(it)); }
                            setTimeout(() => {
                              const el = document.querySelector(`[data-testid="${it.anchor}"]`);
                              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }, 150);
                          }}
                          className="text-left hover:underline hover:text-amber-700 transition cursor-pointer flex-1">
                          {it.msg}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="bg-gray-50 px-5 py-3 rounded-b-2xl flex justify-end gap-2 border-t">
              <button onClick={() => setValidationModal({ open: false, issues: [] })}
                className="px-5 py-2 bg-[#1E3A5F] text-white font-bold rounded-lg hover:bg-[#2a4f7a] transition text-sm"
                data-testid="validation-modal-ok">
                J'ai compris, je complète
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* MODALE APERÇU PDF (iter 89)                  */}
      {/* ============================================ */}
      {previewOpen && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-2 sm:p-4"
          onClick={() => setPreviewOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-4xl h-[90vh] flex flex-col shadow-2xl overflow-hidden"
            onClick={e => e.stopPropagation()}
            data-testid="preview-modal">
            <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2a4f7a] text-white p-4 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-lg sm:text-xl font-bold">Aperçu de votre récapitulatif</h2>
                <p className="text-xs sm:text-sm text-white/90 mt-0.5">
                  Vérifiez vos informations. Vos données ne sont pas encore envoyées.
                </p>
              </div>
              <button
                onClick={() => setPreviewOpen(false)}
                className="text-white/80 hover:text-white text-2xl leading-none px-2"
                data-testid="preview-modal-close">
                ✕
              </button>
            </div>

            <div className="flex-1 bg-gray-100 overflow-hidden">
              {previewPdfUrl && (
                <iframe
                  src={previewPdfUrl}
                  title="Aperçu inscription"
                  className="w-full h-full border-0"
                  data-testid="preview-modal-iframe"
                />
              )}
            </div>

            <div className="bg-gray-50 px-4 py-3 border-t flex flex-col sm:flex-row gap-2 justify-between items-stretch sm:items-center flex-shrink-0">
              <a
                href={previewPdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs sm:text-sm text-[#1E3A5F] font-semibold underline hover:text-[#79c9ea] text-center sm:text-left"
                data-testid="preview-open-newtab">
                Ouvrir dans un nouvel onglet (mobile)
              </a>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setPreviewOpen(false)}
                  className="px-4 py-2 bg-white border-2 border-[#1E3A5F] text-[#1E3A5F] font-bold rounded-lg hover:bg-[#1E3A5F] hover:text-white transition text-sm"
                  data-testid="preview-modal-back">
                  Modifier mes informations
                </button>
                <button
                  onClick={() => setPreviewOpen(false)}
                  className="px-4 py-2 bg-gradient-to-r from-[#79c9ea] to-[#5ba8c8] text-white font-bold rounded-lg hover:shadow-md transition text-sm"
                  data-testid="preview-modal-confirm">
                  Tout est correct
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrationForm;
